import i18next from 'i18next';
import FSM from 'javascript-state-machine';
import { equals, isNotNil } from 'ramda';
import watch from 'redux-watch';
import { trackFirmwareTransfer } from '../analytics/bpsEvents.ts';
import { getAcceptedStreamSampleTime } from '../Components/Graphs/sensor-utils.ts';
import { applicationSlice } from '../redux/ApplicationSlice';
import { controllerDataSlice } from '../redux/ControllerDataSlice';
import { projectSlice, selectProgramState } from '../redux/ProjectSlice';
import { robotDataSlice, selectRobotData } from '../redux/RobotDataSlice';
import { robotSlice, selectDiscoveryState } from '../redux/RobotSlice';
import { selectConnectionState, selectFirmwareProgress, selectFirmwareState } from '../redux/Selectors';
import { BprLogOperationCancelledError, BprToyLogs } from '../robots/BprToyLogs';
import { normalizeProgramDeployProgressPercent } from '../utils/programDeployProgressUtils';
import { resolveDeployProgramCode } from '../Components/CodeEditor/utils/mainProgramEditor.ts';
import {
  ApplicationActions,
  BATTERY_STATES,
  BATTERY_TYPE,
  BPR_FIRMWARE_BASE_URL,
  BPS_SKU,
  FUEL_GAUGE_STATES,
  MAXIMUM_RSSI,
  MINIMUM_RSSI,
  POWER_STATES,
  REMOTE_DEVICE_SYSTEM_INFO_NON_PORT_INDEX_MAX,
  REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START,
  RobotActions,
  RobotErrorCode,
  ROBOTS,
  SLOT_NAMES,
} from '../utils/constants';
import { formatBoardRevision, formatDeviceUidHex } from '../utils/deviceFormatters';
import { formatFirmwareVersionOrUndefined } from '../utils/formatFirmwareVersion';
import { getControllID, rgbaToRgb } from '../utils/helpers';
import {
  micropythonTypeFromByte,
  parseMicropythonStreamValue,
  STREAM_KNOWN_MICROPYTHON_TYPES,
  isOneByteMicropythonStreamType,
} from '../utils/micropythonStreamTypes.ts';
import {
  buildMotorCheckRestoreSettings,
  buildMotorCheckRunSettings,
  buildMotorCheckStopSettings,
  extractMotorPlusSettingsUpdate,
  signedRpmForDirection,
} from '../utils/motor-plus-settings.js';
import { CURRENT_PROJECT_VERSION, projectVersionToString } from '../utils/projectVersions';
import {
  appendBpsProjectMetadataToDeployedPythonWithReport,
  BpsDashboardDeployMetadataError,
  getValidSaveSlotNumber,
  parseBpsProjectMetadataFromPythonSource,
  parseProjectLastModifiedToUtcMs,
  saveSlotMatchesProject,
} from '../utils/saveSlotProjectId';
import { setPathMetadataOmittedWarning } from '../Components/Snackbars/pathMetadataOmittedNotification';
import { showDrivingDisabledWarning } from '../Components/Snackbars/drivingDisabledWarningNotification.ts';
import { resolveExecutingPythonDisplayName, slotNumberFromExecutingPythonPath } from '../utils/executingPythonNotificationUtils.ts';
import { addDataToSensorRun, createSensorRun, saveSensorRun, setActiveRunDashboardLayout } from '../utils/sensor-data/database.ts';
import { getUserAgentInfo } from '../utils/setup-detection.ts';
import {
  enrichDashboardLayoutVariableNamesForRunSnapshot,
  parseDataStreamRequestList,
  reorderPythonStreamDefinitionsToRequestList,
  resolveExpectedStreamWireOrder,
  resolveRepeatableStreamWireUnit,
  tileStreamWireUnitToSlotCount,
} from '../utils/sensor-stream-tracker.ts';
import { shouldQueueReconnect, shouldReportConnectFailure } from './connection-state-helpers.ts';
import { getAssetConfig } from './robot-asset-manager.ts';
import RobotInterface from './robot-interface';
import SpheroSDK from './robot-sdk-helper-impl';
import {
  _getError,
  _getErrorForCode,
  _getErrorForResponse,
  _logMessage,
  _normalizeErrorForDispatch,
  _onErrorHandler,
  isFirmwareTransferRetryState,
} from './robot-service-helpers';
import RobotFirmwareManager, {
  evaluateFirmwareStatus,
  evaluatePostTransferLibraryUpdaterState,
  hasConcreteLibraryUpdaterPhase,
  normalizeLibraryUpdaterProgressPayload,
} from './RobotFirmwareManager';
import {
  DRIVEBASE_SYSTEM_STATE,
  isDrivebaseDriving,
  isDrivebasePythonProgramBusy,
  isDrivebaseRuntimeBusyForFirmwareActions,
  isDrivebaseRuntimeBusyForSlotStorageDeferral,
  isDrivebaseSystemStateBusy,
  isDrivebaseSystemStateExecutingPython,
  isDrivebaseSystemStateIdle,
  normalizeDrivebaseSystemState,
} from './drivebase-system-state.ts';
import { resolveDrivingDisabledWarningReason } from './driving-disabled-warning.ts';
import {
  assertDeployableSlotDeviceFilename,
  MAX_SLOT_DEVICE_FILENAME_UTF8_BYTES,
  projectNameToSlotDeviceFilename,
  SlotDeviceFilenameValidationError,
  normalizeSlotFilename,
  sanitizeDeviceReportedSlotFilename,
} from './slotFilenameUtils.ts';
import { allStates, FirmwareUpdateConnectedStates, FirmwareUpdateStates, InitializedStates, NotInitializedStates } from './state-machine-constants';

const READY_TOY_TIMEOUT = 10000;
const DISCONNECT_COMPLETION_TIMEOUT = 5000;
/** BLE-only delay before starting BPR asset load after entering updatingFirmware. */
const BPR_FIRMWARE_LOAD_DELAY_MS = 5000;
const MAX_RECONNECTS = 3;
const SPHERO_VID = '0x2DC1'; // This is the VID for Sphero toys, it is used to identify Sphero toys in serial connections
const SPHERO_PID = '0x0002'; // This is the PID for Sphero BPR
const BPR_BLE_SERVICE_UUID = '00010001-574f-4f20-5370-6865726f2121';
const SDK_TOY_STATE_CONNECTED = 2;

/** Stream sends drive `Position X` / `Position Y` in meters; BPS assumes cm. Set to 1 when firmware reports cm. */
const PYTHON_STREAM_POSITION_METERS_TO_CM = 100;
// Micropython stream item types the parser knows how to size off the wire. Anything else means the
// cached definitions are stale/incomplete, so a frame referencing an unknown type is rejected.
// Debounce window for re-requesting stream definitions after a misaligned frame, so a burst of
// corrupt frames triggers at most one resync.
const PYTHON_STREAM_DEFS_RESYNC_COOLDOWN_MS = 2000;

const FIRMWARE_PROGRAM_STATES = {
  STARTING: 'updatingFirmwareProgramStarting',
  EXECUTING: 'updatingFirmwareProgramExecuting',
  STOPPING: 'updatingFirmwareProgramStopping',
};
const LOCAL_PROGRAM_STARTING_STATE = 'programStartingLocal';
const LOCAL_PROGRAM_EXECUTING_STATE = 'programExecutingLocal';
const OBSERVED_PROGRAM_EXECUTING_STATE = 'programExecutingObserved';
const PROGRAM_STOPPING_STATE = 'programStopping';
const PROGRAM_EXECUTING_STATES = [LOCAL_PROGRAM_EXECUTING_STATE, OBSERVED_PROGRAM_EXECUTING_STATE];
const PROGRAM_RUNNING_STATES = [LOCAL_PROGRAM_STARTING_STATE, ...PROGRAM_EXECUTING_STATES];
const PROGRAM_BUSY_STATES = [...PROGRAM_RUNNING_STATES, PROGRAM_STOPPING_STATE];

let numberReconnectAttempts = 0;
let connectionType = RobotActions.STATE.CONNECTION_TYPE.BLE;
let lastControllerConnected = false;
let lastControllerConnectionType = null;

const ASSET_LOAD_COMMAND_TIMEOUT_MS_DEFAULT = 5000;
const ASSET_LOAD_COMMAND_TIMEOUT_MS_BLE = 15000;
const REMOTE_DEVICE_SYSTEM_INFO_INDEX_SCAN_TIMEOUT_IDLE_MS = 250;
const REMOTE_DEVICE_SYSTEM_INFO_INDEX_SCAN_TIMEOUT_RUNNING_MS = 1000;
const PORT_REMOTE_DEVICE_SYSTEM_INFO_TIMEOUT_IDLE_MS = 1000;
const PORT_REMOTE_DEVICE_SYSTEM_INFO_STALE_REPLY_GRACE_MS = 1000;
const PORT_REMOTE_DEVICE_SYSTEM_INFO_RESPONSE_COALESCE_MS = 150;
const REMOTE_DEVICE_TYPES_RETRY_DELAY_MS = 250;
const REMOTE_DEVICE_TYPES_COMMAND_TIMEOUT_MS = 1000;
const REMOTE_DEVICE_TYPES_SEQUENCE_MIN = 1;
const REMOTE_DEVICE_TYPES_SEQUENCE_MAX = 255;
const REMOTE_DEVICE_TYPES_PRE_HANDLE_RESPONSE_BUFFER_LIMIT = 4;
const REMOTE_DEVICE_TYPES_REQUEST_ERROR = 'Unable to read connected electronics.';
const REMOTE_DEVICE_TYPES_TIMEOUT_BLOCKED_ERROR = 'Reconnect the drive unit before checking connected electronics again.';
const REMOTE_DEVICE_TYPES_TIMEOUT_BLOCKED_ERROR_CODE = 'REMOTE_DEVICE_TYPES_RECONNECT_REQUIRED';
/** Inclusive upper bound for background non-port `getRemoteDeviceSystemInfoByIndex` sweep after connect. */
const SLOT_ASSET_NAME_REGEX = /^slot([1-8])$/;

function isValidSlotName(slot) {
  return SLOT_ASSET_NAME_REGEX.test(String(slot).trim());
}

function getAssetLoadCommandTimeoutMs() {
  return connectionType === RobotActions.STATE.CONNECTION_TYPE.BLE ? ASSET_LOAD_COMMAND_TIMEOUT_MS_BLE : ASSET_LOAD_COMMAND_TIMEOUT_MS_DEFAULT;
}

function isValidRemoteDeviceTypesSequenceNumber(sequenceNumber) {
  return Number.isInteger(sequenceNumber) && sequenceNumber >= REMOTE_DEVICE_TYPES_SEQUENCE_MIN && sequenceNumber <= REMOTE_DEVICE_TYPES_SEQUENCE_MAX;
}

function getPerformanceTimestamp() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}

function getPythonProgramByteLength(pythonScript) {
  if (typeof TextEncoder === 'undefined') {
    return pythonScript.length;
  }

  return new TextEncoder().encode(pythonScript).length;
}

function roundMetricMs(milliseconds) {
  return Math.round(milliseconds);
}

function hasConcreteFirmwareTransferProgress(progress) {
  if (progress == null || typeof progress !== 'object') {
    return false;
  }

  const hasAssetId = typeof progress.assetId === 'string' && progress.assetId.trim() !== '';
  const hasLibraryId = typeof progress.libraryId === 'string' && progress.libraryId.trim() !== '';
  const hasPositivePercent = typeof progress.percent === 'number' && Number.isFinite(progress.percent) && progress.percent > 0;

  return hasAssetId || hasLibraryId || hasPositivePercent;
}

function isFirmwareFailureState(firmwareState) {
  return isFirmwareTransferRetryState(firmwareState) || firmwareState === RobotActions.FIRMWARE_UPDATE.FINALIZATION_FAILED;
}

function isEvaluatedFirmwareStatus(status) {
  return (
    status === RobotActions.FIRMWARE_STATUS.UPDATE_AVAILABLE ||
    status === RobotActions.FIRMWARE_STATUS.READY_TO_INSTALL ||
    status === RobotActions.FIRMWARE_STATUS.UP_TO_DATE
  );
}

function isReconnectSafeLibraryUpdaterFallbackPhase(phase) {
  return ['ready_to_finalize', 'jumping_to_finalize', 'finalizing', 'jumping_after_finalize'].includes(phase);
}

function canQueryRemoteDeviceSystemInfoInState(state) {
  return ['ready', ...PROGRAM_BUSY_STATES].includes(state);
}

function getRemoteDeviceSystemInfoIndexScanTimeoutMs(state) {
  if (PROGRAM_BUSY_STATES.includes(state)) {
    return REMOTE_DEVICE_SYSTEM_INFO_INDEX_SCAN_TIMEOUT_RUNNING_MS;
  }

  return REMOTE_DEVICE_SYSTEM_INFO_INDEX_SCAN_TIMEOUT_IDLE_MS;
}

function getPortRemoteDeviceSystemInfoTimeoutMs(state) {
  return Math.max(getRemoteDeviceSystemInfoIndexScanTimeoutMs(state), PORT_REMOTE_DEVICE_SYSTEM_INFO_TIMEOUT_IDLE_MS);
}

// Battery polling can stay relatively coarse; overseer/system-state polling runs independently.
const BATTERY_MONITORING_TIMEOUT_MS = 3000;
const SYSTEM_STATE_MONITORING_TIMEOUT_MS = 1000;
// Give the initial connect-time system-state probe a full idle polling window before
// falling back to the normal validation path. This avoids making defer/no-defer depend
// on a very short response race when the DU is already busy.
const VALIDATION_SYSTEM_STATE_RESPONSE_TIMEOUT_MS = SYSTEM_STATE_MONITORING_TIMEOUT_MS;
// Poll faster while reconciling a BPS-started run so brief overseer idle between DU cancel/rerun is less likely to be missed.
const ACTIVE_PROGRAM_OVERSEER_RECONCILE_POLL_MS = 500;
// Allow executePythonFile to take effect before reconciling idle while a local run is starting.
const LOCAL_PROGRAM_OVERSEER_STARTUP_GRACE_MS = 2 * SYSTEM_STATE_MONITORING_TIMEOUT_MS;
let isMonitoringBatteryLevel = false;
let isMonitoringSystemState = false;
// Batch plain Python print() lines into short bursts instead of one Redux action per line.
const PYTHON_PRINT_CONSOLE_FLUSH_MS = 120;
const SLOT_STORAGE_QUERY_RETRY_DELAY_MS = 500;
const SLOT_STORAGE_QUERY_MAX_ATTEMPTS = 3;
const SLOT_STORAGE_PROGRAM_NAME_QUERY_TIMEOUT_MS = 1000;
const SLOT_STORAGE_SWEEP_STALE = 'stale';
const STREAMED_ITEM_DEFINITION_QUERY_TIMEOUT_MS = 1000;

let storedHandshakeId = null;

/**
 * Utility object for setting timeouts during bluetooth connection flow.
 *
 */
const btConnectionTimeout = {
  timeout: false,
  /**
   * Starts a timeout. The callback will be called after the timeout expires.
   *
   * @param {function} callback - called after the timeout expires
   * @param {?number} time - time out time. Defaults to 10 seconds.
   */
  start(callback, time = 10000) {
    this.stop();
    this.timeout = setTimeout(() => this.onError(callback), time);
  },
  /**
   * Clears the current timeout.
   */
  stop() {
    clearTimeout(this.timeout);
  },
  /**
   * Handles the timeout expiring
   */
  onError(callback) {
    callback();
  },
};
const batteryMonitorTimeout = {
  timeout: false,
  /**
   * Starts a timeout. The callback will be called after the timeout expires.
   *
   * @param {function} callback - called after the timeout expires
   * @param {?number} time - time out time. Defaults to 10 seconds.
   */
  start(callback, time = 10000) {
    this.stop();
    this.timeout = setTimeout(() => this.onError(callback), time);
  },
  /**
   * Clears the current timeout.
   */
  stop() {
    clearTimeout(this.timeout);
  },
  /**
   * Handles the timeout expiring
   */
  onError(callback) {
    callback();
  },
};
const systemStateMonitorTimeout = {
  timeout: false,
  /**
   * Starts a timeout. The callback will be called after the timeout expires.
   *
   * @param {function} callback - called after the timeout expires
   * @param {?number} time - time out time. Defaults to 10 seconds.
   */
  start(callback, time = 10000) {
    this.stop();
    this.timeout = setTimeout(() => this.onError(callback), time);
  },
  /**
   * Clears the current timeout.
   */
  stop() {
    clearTimeout(this.timeout);
  },
  /**
   * Handles the timeout expiring
   */
  onError(callback) {
    callback();
  },
};

/**
 * Default instance state for RobotService.  Shared with tests so they can
 * build partial service instances without duplicating (and drifting from)
 * the constructor's defaults.
 */
export const ROBOT_SERVICE_DEFAULTS = {
  robotState: null,
  robotPrefix: null,
  batteryCharging: false,
  batteryPercentage: null,
  powerState: null,
  powerLevel: null,
  filter: null,
  robot: null,
  robotFirmwareManager: null,
  sdk: null,
  robotCalibrating: false,
  currentHeading: 0,
  disconnectError: null,
  connectionFailedInCharger: false,
  userCancelledConnection: false,
  userRobotColor: null,
  robotType: null,
  sensorData: null,
  joystick: null,
  currentVersions: null,
  firmwareCheckInitialized: false,
  programDrive: false,
  startTime: null,
  programId: null,
  userAgentInfo: null,
  firmwareProgress: null,
  _criticalFirmwareUpdateFlow: false,
  _backgroundFirmwarePauseHoldCount: 0,
  _pendingBackgroundFirmwareDownload: false,
  _pendingCriticalFirmwareDownload: false,
  _pendingFirmwareFinalization: false,
  runState: null,
  pythonPrintBuffer: '',
  lastSendApiResponseTime: null,
  sdkInitPromise: null,
  _deployInProgress: false,
  _runStartInProgress: false,
  _programActionSequence: 0,
  _drivingDisabledWarningRequestToken: 0,
  _drivingDisabledWarningRunToken: 0,
  _drivingDisabledWarningShownForRun: false,
  _drivingDisabledWarningEvidenceInvalidRobot: null,
  _systemStateRequestInFlight: false,
  _systemStateRequestQueue: [],
  _systemStateDrainWaiters: [],
  _systemStateResponseOwnershipAmbiguousRobot: null,
  _systemStateResponseOwnershipRecoveryAt: 0,
  _disconnectCompletionTimeout: null,
  _pendingConnectionRequest: null,
  _pendingConnectionTimer: null,
  _disconnectToReconnect: false,
  _robotDisconnectReleased: false,
  _lastElectronicsPortTypes: null,
  _lastElectronicsPortConnectedMask: null,
  _lastAcceptedLiveStreamTime: null,
  _pendingPythonPrintLines: [],
  _pythonPrintFlushTimer: null,
  numberOfStreamedItems: 0,
  streamedItemDefinitions: [],
  /** True once stream item definitions are committed for the current program run. */
  _pythonStreamDefinitionsCommittedForRun: false,
  /** True when committed defs were aligned using a resolved program request-list source. */
  _pythonStreamDefinitionsAlignmentTrusted: false,
  /** True once this run's registration order proved underivable, which blocks streaming for the run. */
  _pythonStreamDefinitionAlignmentUnprovableForRun: false,
  _lastKnownStreamedItemDefinitions: [],
  _lastKnownStreamedItemDefinitionsProjectId: null,
  _lastKnownStreamedItemDefinitionsSlot: null,
  _lastKnownStreamedItemDefinitionsFilename: null,
  /** Session deploy source keyed by slot name for stream-definition alignment when editor metadata diverges. */
  _sessionDeployedProgramCodeBySlot: {},
  _pendingBprLogOperation: null,
  _pendingBprLogAbortController: null,
  _bprLogOperationGeneration: 0,
  _queryRobotSlotStorageNeedsRefresh: false,
  _queryRobotSlotStoragePromise: null,
  _queryRobotSlotStorageRequestRobot: null,
  _queryRobotSlotStorageRequestToken: 0,
  _deferSlotStorageQueryUntilObservedProgramEnds: false,
  _observedProgramEndedSlotStorageQueryAttempted: false,
  _slotStorageMetadataLoadedForConnection: false,
  _slotStorageProgramNameQueryDisabledForConnection: false,
  _lastSlotStorageRecoveryFresh: false,
  _pendingConnectionMetadataBootstrap: false,
  _deferConnectionMetadataBootstrapUntilObservedProgramEnds: false,
  _connectionMetadataBootstrapped: false,
  _deferredConnectionValidationPending: false,
  _deferredConnectionMetadataResumePromise: null,
  _deferredObservedProgramCriticalDialogShown: false,
  _pendingDeferredCriticalFirmwareDialog: false,
  _validationObservedRuntimeProgramState: null,
  _locallyStartedProgramThisConnection: false,
  _awaitingLocalRunOverseerExecution: false,
  _localRunStartedAt: null,
  _localRunStoppedAt: null,
  _localRunOverseerIdleSeen: false,
  _localRunOverseerExecutionEstablished: false,
  _localRunPythonStreamNotificationSeen: false,
  _localRunPythonStreamBaselineFingerprint: null,
  _localRunPythonStreamClearedSeen: false,
  _localRunOverseerExecutionReentrySeen: false,
  _localRunMicropythonStatusTransitionSeen: false,
  _localRunMatchingExecutingIdentityEstablished: false,
  _localRunExecutionBoundarySeen: false,
  _localRunLastMicropythonStatus: null,
  _observedExternalRunningProgramSynced: false,
  _runningProgramNotificationContextRequestToken: 0,
  _awaitObservedRuntimeReconfirmation: false,
  _handoffLocalRunToObservedExternalPromise: null,
  _executingPythonIdentityRequestPromise: null,
  isPowerStateAlreadyLow: false,
  currentSlot: null,
  currentFilename: null,
  _remoteDeviceSystemInfoIndexScanActive: false,
  _remoteDeviceSystemInfoIndexScanAwaitingIndex: null,
  _remoteDeviceSystemInfoIndexScanGeneration: 0,
  _remoteDeviceSystemInfoIndexScanRobot: null,
  _remoteDeviceSystemInfoIndexScanTimeoutId: null,
  _portRemoteDeviceSystemInfoRequestsByIndex: {},
  _portRemoteDeviceSystemInfoGenerationByIndex: {},
  _portRemoteDeviceSystemInfoAttemptCompleteByIndex: {},
  _portRemoteDeviceSystemInfoLastRequestedIndex: null,
  _portRemoteDeviceSystemInfoBufferedResponsesByIndex: {},
  _portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex: {},
  _portRemoteDeviceSystemInfoInvalidationGraceByIndex: {},
  _portRemoteDeviceSystemInfoSupersededInfoByIndex: {},
  _portRemoteDeviceSystemInfoSupersededRejectWaveByIndex: {},
  _portRemoteDeviceSystemInfoAllowIdenticalReplacementByIndex: {},
  _portUntypedFirmwareHydrationPorts: null,
  _remoteDeviceTypesTimer: null,
  _remoteDeviceTypesTimeoutBlockedRobot: null,
  _remoteDeviceTypesConnectionGeneration: 0,
  _remoteDeviceTypesUnresolvedSequences: [],
  _remoteDeviceTypesBackgroundRecoveryPending: false,
  _remoteDeviceTypesActiveRequest: null,
  _remoteDeviceTypesOneShotQueue: [],
  _bprFirmwareLoadTimer: null,
  _getRobotFirmwareVersionsPromise: null,
  _getRobotFirmwareVersionsPromiseToken: null,
  _lastGetRobotFirmwareVersionsTime: null,
  _firmwareMetadataRequestToken: 0,
  _deployReadinessWatchKey: '',
};

class RobotService {
  constructor(store) {
    this.store = store;
    this.dispatch = this.store.dispatch;
    this.robotCommandActions = robotSlice.actions;
    this.mainAppActions = applicationSlice.actions;
    this.robotDataActions = robotDataSlice.actions;
    this.controllerDataActions = controllerDataSlice.actions;
    this.programActions = projectSlice.actions;

    Object.assign(this, structuredClone(ROBOT_SERVICE_DEFAULTS));
    this._motorCheckRestoreByPort = new Map();

    this.bindEventListeners();
    this.init();
  }

  _getEffectiveRobotType() {
    return this.robotType ?? this.store.getState().robotData?.robotType ?? ROBOTS.NONE;
  }

  _getBatteryMonitoringIntervalMs() {
    return BATTERY_MONITORING_TIMEOUT_MS;
  }

  _getSystemStateMonitoringIntervalMs() {
    const projectProgramState = this.store.getState().project?.programState;
    const isProgramActive =
      isDrivebasePythonProgramBusy(this._getRuntimeProgramState()) ||
      projectProgramState === RobotActions.PROGRAM.RUN ||
      projectProgramState === RobotActions.PROGRAM.STARTING ||
      projectProgramState === RobotActions.PROGRAM.STARTED;

    if (isProgramActive) {
      return ACTIVE_PROGRAM_OVERSEER_RECONCILE_POLL_MS;
    }

    return SYSTEM_STATE_MONITORING_TIMEOUT_MS;
  }

  _invalidateSlotStorageQuery() {
    this._queryRobotSlotStorageNeedsRefresh = false;
    this._queryRobotSlotStoragePromise = null;
    this._queryRobotSlotStorageRequestRobot = null;
    this._queryRobotSlotStorageRequestToken += 1;
  }

  _reset() {
    this._cancelBprLogOperations(new BprLogOperationCancelledError('Drive Unit disconnected.'));
    this._clearDisconnectCompletionTimeout();
    this._clearPendingConnectionTimer();
    this._clearBprFirmwareLoadTimer();
    this._cancelRemoteDeviceTypesRequests();
    this._invalidateFirmwareMetadataRequests();
    this.robot = null;
    this.dispatch(this.robotDataActions.onUpdateRobotType(ROBOTS.NONE));
    this.robotFirmwareManager = null;
    this._criticalFirmwareUpdateFlow = false;
    this._backgroundFirmwarePauseHoldCount = 0;
    this._pendingBackgroundFirmwareDownload = false;
    this._pendingCriticalFirmwareDownload = false;
    this._pendingFirmwareFinalization = false;
    this._robotDisconnectReleased = false;
    this.robotCalibrating = false;
    this.currentHeading = 0;
    this.disconnectError = null;
    this.batteryCharging = false;
    this.batteryPercentage = null;
    this.powerState = null;
    this.powerLevel = null;
    this.startTime = null;
    this.programId = null;
    this.currentSlot = null;
    this.currentFilename = null;
    this._sessionDeployedProgramCodeBySlot = {};
    this._runStartInProgress = false;
    this._drivingDisabledWarningRequestToken += 1;
    this._drivingDisabledWarningRunToken += 1;
    this._drivingDisabledWarningShownForRun = false;
    this._drivingDisabledWarningEvidenceInvalidRobot = null;
    this._systemStateRequestInFlight = false;
    this._systemStateResponseOwnershipAmbiguousRobot = null;
    this._systemStateResponseOwnershipRecoveryAt = 0;
    for (const waiter of this._systemStateDrainWaiters) {
      clearTimeout(waiter.timeoutId);
      waiter.resolve(false);
    }
    this._systemStateDrainWaiters = [];
    for (const entry of this._systemStateRequestQueue) {
      clearTimeout(entry.timeoutId);
      if (entry.responseListener && typeof entry.robot?._off === 'function') {
        entry.robot._off(entry.robot.getSystemStateResponse, entry.responseListener);
      }
      if (!entry.settled) {
        entry.settled = true;
        entry.resolve(null);
      }
    }
    this._systemStateRequestQueue = [];
    this._resetLiveSensorStreamState();
    this._clearPythonPrintQueue();
    this._resetPythonStreamState();
    this._clearRemoteDeviceSystemInfoIndexScan();
    this._invalidateSlotStorageQuery();
    this._deferSlotStorageQueryUntilObservedProgramEnds = false;
    this._observedProgramEndedSlotStorageQueryAttempted = false;
    this._slotStorageMetadataLoadedForConnection = false;
    this._slotStorageProgramNameQueryDisabledForConnection = false;
    this._pendingConnectionMetadataBootstrap = false;
    this._deferConnectionMetadataBootstrapUntilObservedProgramEnds = false;
    this._connectionMetadataBootstrapped = false;
    this._deferredConnectionValidationPending = false;
    this._deferredConnectionMetadataResumePromise = null;
    this._deferredObservedProgramCriticalDialogShown = false;
    this._pendingDeferredCriticalFirmwareDialog = false;
    this._validationObservedRuntimeProgramState = null;
    this._locallyStartedProgramThisConnection = false;
    this._awaitingLocalRunOverseerExecution = false;
    this._localRunStartedAt = null;
    this._localRunStoppedAt = null;
    this._localRunOverseerIdleSeen = false;
    this._localRunOverseerExecutionEstablished = false;
    this._localRunPythonStreamNotificationSeen = false;
    this._localRunPythonStreamBaselineFingerprint = null;
    this._localRunPythonStreamClearedSeen = false;
    this._localRunOverseerExecutionReentrySeen = false;
    this._localRunMicropythonStatusTransitionSeen = false;
    this._localRunMatchingExecutingIdentityEstablished = false;
    this._localRunExecutionBoundarySeen = false;
    this._localRunLastMicropythonStatus = null;
    this._observedExternalRunningProgramSynced = false;
    this._awaitObservedRuntimeReconfirmation = false;
    this._handoffLocalRunToObservedExternalPromise = null;
    this._executingPythonIdentityRequestPromise = null;
    this._deployReadinessWatchKey = '';
    this._clearPendingPortRemoteDeviceSystemInfoRequests();
    this._motorCheckRestoreByPort?.clear();

    isMonitoringBatteryLevel = false;
    batteryMonitorTimeout.stop();
    isMonitoringSystemState = false;
    systemStateMonitorTimeout.stop();

    this.dispatch(this.mainAppActions.onSetControllerConnected(false));
    this.dispatch(this.controllerDataActions.onClearControllerConnectionState());
    // this.currentVersions = null;
    // this.firmwareCheckInitialized = false;
  }

  /** Clears live-stream ingest state (timeline cursor). Does not clear Redux `liveSensorData`. */
  _resetLiveSensorStreamState(options = {}) {
    const { resetTimeline = true } = options;
    if (resetTimeline) {
      this._resetLiveStreamTimeline();
    }
  }

  _resetLiveStreamTimeline() {
    this._lastAcceptedLiveStreamTime = null;
  }

  _recordLiveStreamSampleTime(sample) {
    const acceptedTime = getAcceptedStreamSampleTime(this._lastAcceptedLiveStreamTime ?? null, sample);
    if (acceptedTime == null) {
      return false;
    }
    this._lastAcceptedLiveStreamTime = acceptedTime;
    return true;
  }

  _clearPythonPrintQueue() {
    if (this._pythonPrintFlushTimer) {
      clearTimeout(this._pythonPrintFlushTimer);
      this._pythonPrintFlushTimer = null;
    }
    this._pendingPythonPrintLines = [];
  }

  _flushPendingPythonPrintOutput(includePartialLine = false) {
    if (this._pythonPrintFlushTimer) {
      clearTimeout(this._pythonPrintFlushTimer);
      this._pythonPrintFlushTimer = null;
    }

    const lines = Array.isArray(this._pendingPythonPrintLines) ? [...this._pendingPythonPrintLines] : [];
    this._pendingPythonPrintLines = [];

    const tail = typeof this.pythonPrintBuffer === 'string' ? this.pythonPrintBuffer : '';
    if (includePartialLine && tail.length > 0) {
      lines.push(tail);
      this.pythonPrintBuffer = '';
    }

    if (lines.length === 0) {
      return;
    }

    if (lines.length === 1) {
      this.dispatch(this.programActions.onAddConsoleDisplay(lines[0]));
      return;
    }

    this.dispatch(this.programActions.onAddConsoleDisplayBatch(lines));
  }

  _resetPythonStreamState() {
    this._rememberPythonStreamDefinitions();
    this._pythonStreamDefinitionsCommittedForRun = false;
    this._pythonStreamDefinitionsAlignmentTrusted = false;
    this._pythonStreamDefinitionAlignmentUnprovableForRun = false;
    this.numberOfStreamedItems = 0;
    this.streamedItemDefinitions = [];
    this.streamedItemData = null;
    this.streamedItemName = null;
    this.streamedItemType = null;
    this.streamedItemStartingIndex = null;
    this.streamedItemDataPayload = null;
  }

  _clearPythonStreamDefinitionsForNewRun() {
    this._pythonStreamDefinitionsCommittedForRun = false;
    this._pythonStreamDefinitionsAlignmentTrusted = false;
    this._pythonStreamDefinitionAlignmentUnprovableForRun = false;
    this.numberOfStreamedItems = 0;
    this.streamedItemDefinitions = [];
    this.streamedItemData = null;
    this.streamedItemName = null;
    this.streamedItemType = null;
    this.streamedItemStartingIndex = null;
    this.streamedItemDataPayload = null;
  }

  /**
   * Drops this run's definitions after alignment reports the wire order is underivable.
   *
   * Returning early is not enough: definitions committed before the source resolved stay active,
   * and every `enablePythonStreaming(true)` path only checks that some definitions exist. Clearing
   * them and latching the run keeps frames from being decoded against a layout nothing proved.
   */
  _invalidatePythonStreamDefinitionsForUnprovableAlignment() {
    this._clearPythonStreamDefinitionsForNewRun();
    this._pythonStreamDefinitionAlignmentUnprovableForRun = true;
    // Provisional definitions may already have switched streaming on before the source resolved.
    if (this.robot?.enablePythonStreaming) {
      this.robot.enablePythonStreaming(false);
    }
  }

  /** False once this run's registration order proved underivable; the run streams nothing after that. */
  _canEnablePythonStreamingForRun() {
    return this._pythonStreamDefinitionAlignmentUnprovableForRun !== true;
  }

  _hasCommittedPythonStreamDefinitionsForRun() {
    return (
      this._pythonStreamDefinitionsCommittedForRun === true && Array.isArray(this.streamedItemDefinitions) && this.streamedItemDefinitions.length > 0
    );
  }

  _hasTrustedCommittedPythonStreamDefinitionsForRun() {
    return this._hasCommittedPythonStreamDefinitionsForRun() && this._pythonStreamDefinitionsAlignmentTrusted === true;
  }

  _isResolvedProgramCodeForStreamAlignment(programCode) {
    return typeof programCode === 'string' && programCode.trim().length > 0;
  }

  /**
   * True when an awaited alignment step belongs to a run that is no longer current.
   *
   * Resolving deployed source can take seconds, and stopping one program and starting another keeps
   * the same robot object, so the robot identity alone does not separate runs. Acting on a stale
   * result would commit the old run's layout over the new one, or latch the new run as unprovable
   * from the old run's source, leaving a dashboard that cannot recover for the rest of the run.
   */
  _isStalePythonStreamAlignmentRequest(requestRobot, requestToken) {
    return this.robot !== requestRobot || this._runningProgramNotificationContextRequestToken !== requestToken;
  }

  _commitPythonStreamDefinitionsForRun(definitions, { alignmentTrusted = false } = {}) {
    if (!Array.isArray(definitions) || definitions.length === 0 || !this._canEnablePythonStreamingForRun()) {
      return false;
    }

    this.streamedItemDefinitions = definitions.map((definition) => ({ ...definition }));
    this._rememberPythonStreamDefinitions(this.streamedItemDefinitions);
    this._pythonStreamDefinitionsCommittedForRun = true;
    this._pythonStreamDefinitionsAlignmentTrusted = alignmentTrusted;
    return true;
  }

  async _realignCommittedPythonStreamDefinitionsIfNeeded(requestRobot = this.robot) {
    if (!this._hasCommittedPythonStreamDefinitionsForRun() || this._pythonStreamDefinitionsAlignmentTrusted) {
      return this._hasTrustedCommittedPythonStreamDefinitionsForRun();
    }

    const requestToken = this._runningProgramNotificationContextRequestToken;
    const programCode = await this._resolveProgramCodeForStreamDefinitionAlignment(requestRobot);
    if (this._isStalePythonStreamAlignmentRequest(requestRobot, requestToken)) {
      return false;
    }

    const realignedDefinitions = this._alignPythonStreamDefinitionsToProgramRequestList(this.streamedItemDefinitions, programCode);
    if (realignedDefinitions == null) {
      this._invalidatePythonStreamDefinitionsForUnprovableAlignment();
      return false;
    }
    const alignmentTrusted =
      this._isResolvedProgramCodeForStreamAlignment(programCode) && this._coversDerivedPythonStreamRegistration(realignedDefinitions, programCode);
    this._commitPythonStreamDefinitionsForRun(realignedDefinitions, { alignmentTrusted });
    return alignmentTrusted;
  }

  _getSelectedSlotContext() {
    const state = this.store.getState();
    const loadedProject = state.project?.loadedProject;
    const selectedSlotNumber = getValidSaveSlotNumber(state.project?.sessionSaveSlot);
    if (selectedSlotNumber == null) {
      return { projectId: loadedProject?.id ?? null, slotName: null, filename: '' };
    }

    const slotName = `slot${selectedSlotNumber}`;
    const slotInfo = this.store.getState().robotData?.slotStorage?.[slotName];
    return {
      projectId: loadedProject?.id ?? null,
      slotName,
      filename: normalizeSlotFilename(slotInfo, slotName),
    };
  }

  _applyExecutingPythonIdentityToCurrentRunTarget(executingName) {
    const slotNumber = slotNumberFromExecutingPythonPath(executingName);
    if (slotNumber == null) {
      return false;
    }

    const filename = this._normalizeExecutingPythonProgramName(executingName);
    if (!filename) {
      return false;
    }

    this.currentSlot = `slot${slotNumber}`;
    this.currentFilename = filename;
    return true;
  }

  async _ensureExecutingPythonIdentityForStreamAlignment(requestRobot = this.robot) {
    if (!requestRobot) {
      return false;
    }

    if (this.currentSlot && this.currentFilename) {
      return true;
    }

    if (
      typeof requestRobot.getExecutingPythonTypeAndName !== 'function' ||
      !requestRobot.getExecutingPythonTypeAndNameResponse ||
      requestRobot.hasCommand?.('getExecutingPythonTypeAndName') !== true
    ) {
      return false;
    }

    try {
      const responsePromise = this._waitForRobotResponse(
        requestRobot.getExecutingPythonTypeAndNameResponse,
        STREAMED_ITEM_DEFINITION_QUERY_TIMEOUT_MS,
        requestRobot
      );
      requestRobot.getExecutingPythonTypeAndName();
      const response = await responsePromise;

      if (this.robot !== requestRobot || response?.success !== true || response.data == null) {
        return false;
      }

      const executingName = response.data?.name;
      if (typeof executingName !== 'string' || !executingName.trim()) {
        return false;
      }

      return this._applyExecutingPythonIdentityToCurrentRunTarget(executingName);
    } catch (error) {
      console.error('Failed to resolve executing Python identity for stream alignment:', error);
      return false;
    }
  }

  _getStreamDefinitionAlignmentExecutionContext() {
    const { projectId, slotName, filename } = this._getSelectedSlotContext();
    if (!slotName) {
      return null;
    }

    const state = this.store?.getState?.();
    const slotInfo = state?.robotData?.slotStorage?.[slotName];
    if (!slotInfo || slotInfo.isEmpty === true) {
      return null;
    }

    const expectedFilename = normalizeSlotFilename(slotInfo, slotName);
    const normalizedExpectedFilename = this._normalizeExecutingPythonProgramName(expectedFilename);
    if (!normalizedExpectedFilename) {
      return null;
    }

    const executingSlot = this.currentSlot;
    if (!executingSlot || executingSlot !== slotName) {
      return null;
    }

    const executingFilename = this._normalizeExecutingPythonProgramName(this.currentFilename);
    if (!executingFilename || executingFilename !== normalizedExpectedFilename) {
      return null;
    }

    return {
      projectId: projectId ?? null,
      slotName,
      filename: normalizedExpectedFilename,
      slotInfo,
      loadedProject: state?.project?.loadedProject ?? null,
    };
  }

  _getProgramCodeForStreamDefinitionAlignment() {
    const context = this._getStreamDefinitionAlignmentExecutionContext();
    if (!context) {
      return null;
    }

    const loadedProjectLastModifiedUtcMs = parseProjectLastModifiedToUtcMs(context.loadedProject?.lastModified);
    if (saveSlotMatchesProject(context.slotInfo, context.projectId, loadedProjectLastModifiedUtcMs)) {
      const editorCode = context.loadedProject?.program?.code;
      if (typeof editorCode === 'string' && editorCode.trim().length > 0) {
        return editorCode;
      }
    }

    const sessionDeploy = this._sessionDeployedProgramCodeBySlot?.[context.slotName];
    if (
      sessionDeploy &&
      typeof sessionDeploy.code === 'string' &&
      sessionDeploy.code.trim().length > 0 &&
      this._normalizeExecutingPythonProgramName(sessionDeploy.filename) === context.filename
    ) {
      return sessionDeploy.code;
    }

    return null;
  }

  async _resolveProgramCodeForStreamDefinitionAlignment(requestRobot = this.robot) {
    const trustedCode = this._getProgramCodeForStreamDefinitionAlignment();
    if (trustedCode) {
      return trustedCode;
    }

    const context = this._getStreamDefinitionAlignmentExecutionContext();
    if (!context || requestRobot !== this.robot) {
      return null;
    }

    const mayReadDeployedSource = this._locallyStartedProgramThisConnection || this._observedExternalRunningProgramSynced;
    if (!mayReadDeployedSource) {
      return null;
    }

    try {
      const deployedSource = await this.readPythonAsset(context.slotName);
      return typeof deployedSource === 'string' && deployedSource.trim().length > 0 ? deployedSource : null;
    } catch (error) {
      console.error('Failed to read deployed Python for stream definition alignment:', error);
      return null;
    }
  }

  _alignPythonStreamDefinitionsToProgramRequestList(definitions, programCode = null) {
    if (!Array.isArray(definitions) || definitions.length === 0) {
      return definitions ?? [];
    }
    const code = programCode ?? this._getProgramCodeForStreamDefinitionAlignment();
    // Prefer the order derived across every update_monitor_data() pass; a program can set a new
    // request list between passes, so the first list alone does not describe the wire.
    const wireOrder = code ? resolveExpectedStreamWireOrder(code) ?? parseDataStreamRequestList(code) : null;
    const aligned = reorderPythonStreamDefinitionsToRequestList(definitions, wireOrder);
    if (aligned != null) {
      return aligned;
    }

    // The robot registered more passes than the source shows. That is only orderable when the
    // source registers a single request-list order, in which case the wire is that order repeated
    // and the pass count stops mattering. Re-running the strict reorder against the repeated order
    // keeps the name checks, so a repeat that does not actually line up still fails closed.
    const tiledOrder = code ? tileStreamWireUnitToSlotCount(resolveRepeatableStreamWireUnit(code), definitions.length) : null;
    // null means the wire order could not be established. Callers must not commit or stream these
    // definitions: an unprovable order can still match the frame length and mislabel every value.
    return tiledOrder ? reorderPythonStreamDefinitionsToRequestList(definitions, tiledOrder) : null;
  }

  /**
   * True when `definitions` account for every registration pass the source performs.
   *
   * Firmware reports cumulatively, so a program that registers twice can notify once per pass. The
   * first report is a correctly ordered prefix but not the whole wire, and trusting a prefix would
   * take the resync fast path and leave those later slots undefined for every frame. Sources with
   * no derivable pass sequence have nothing to wait for and count as complete.
   */
  _coversDerivedPythonStreamRegistration(definitions, programCode = null) {
    const code = programCode ?? this._getProgramCodeForStreamDefinitionAlignment();
    if (!code) {
      return false;
    }
    const wireOrder = resolveExpectedStreamWireOrder(code);
    return wireOrder == null || definitions.length >= wireOrder.length;
  }

  _rememberPythonStreamDefinitions(definitions = this.streamedItemDefinitions) {
    if (!Array.isArray(definitions) || definitions.length === 0) {
      return;
    }

    const { projectId, slotName, filename } = this._getSelectedSlotContext();
    const normalizedFilename = this._normalizeExecutingPythonProgramName(this.currentFilename || filename);
    this._lastKnownStreamedItemDefinitions = definitions.map((definition) => ({ ...definition }));
    this._lastKnownStreamedItemDefinitionsProjectId = projectId;
    this._lastKnownStreamedItemDefinitionsSlot = this.currentSlot || slotName;
    this._lastKnownStreamedItemDefinitionsFilename = normalizedFilename || null;
  }

  _getCachedPythonStreamDefinitionsForObservedRun() {
    if (!Array.isArray(this._lastKnownStreamedItemDefinitions) || this._lastKnownStreamedItemDefinitions.length === 0) {
      return [];
    }

    const { projectId, slotName, filename } = this._getSelectedSlotContext();
    const selectedSlotName = this.currentSlot || slotName;
    const normalizedFilename = this._normalizeExecutingPythonProgramName(this.currentFilename || filename);
    const matchesProject =
      projectId != null && this._lastKnownStreamedItemDefinitionsProjectId != null && this._lastKnownStreamedItemDefinitionsProjectId === projectId;
    const matchesSlot =
      selectedSlotName != null &&
      this._lastKnownStreamedItemDefinitionsSlot != null &&
      this._lastKnownStreamedItemDefinitionsSlot === selectedSlotName;
    const matchesFilename =
      normalizedFilename &&
      this._lastKnownStreamedItemDefinitionsFilename != null &&
      this._lastKnownStreamedItemDefinitionsFilename === normalizedFilename;

    if (!matchesProject || !matchesSlot || !matchesFilename) {
      return [];
    }

    return this._lastKnownStreamedItemDefinitions.map((definition) => ({ ...definition }));
  }

  _waitForRobotResponse(eventName, timeoutMs = STREAMED_ITEM_DEFINITION_QUERY_TIMEOUT_MS, requestRobot = this.robot) {
    if (!requestRobot || !eventName || typeof requestRobot._once !== 'function') {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve(value ?? null);
      };
      const timeoutId = setTimeout(() => {
        if (typeof requestRobot._off === 'function') {
          requestRobot._off(eventName, finish);
        }
        finish(null);
      }, timeoutMs);
      requestRobot._once(eventName, finish);
    });
  }

  _showDrivingDisabledWarningFromSystemStateResponse(response) {
    if (this._isDrivingDisabledWarningEvidenceInvalid() || this._drivingDisabledWarningShownForRun || response?.success !== true || !response.data) {
      return false;
    }

    const reason = resolveDrivingDisabledWarningReason(response.data, this.store.getState().robot?.connectionType);
    if (reason) {
      this._drivingDisabledWarningShownForRun = true;
      showDrivingDisabledWarning(reason);
      return true;
    }

    return false;
  }

  _beginDrivingDisabledWarningRun() {
    this._drivingDisabledWarningRequestToken += 1;
    this._drivingDisabledWarningRunToken += 1;
    this._drivingDisabledWarningShownForRun = false;
  }

  _maybeShowDrivingDisabledWarningForActiveProgram(response, isProgramFsmRunning = this._isProgramFsmRunning(), responseRunToken) {
    if (responseRunToken !== this._drivingDisabledWarningRunToken || !isProgramFsmRunning || this._isProgramStoppingState()) {
      return false;
    }

    return this._showDrivingDisabledWarningFromSystemStateResponse(response);
  }

  _syncSystemStateRequestInFlight() {
    this._systemStateRequestInFlight = this._systemStateRequestQueue.length > 0;
  }

  _isDrivingDisabledWarningEvidenceInvalid(requestRobot = this.robot) {
    return requestRobot != null && this._drivingDisabledWarningEvidenceInvalidRobot === requestRobot;
  }

  _markSystemStateResponseOwnershipAmbiguous(requestRobot) {
    if (!requestRobot) {
      return;
    }

    // The response event has no request ID. Polling may recover after a quiet
    // interval, but warning evidence remains untrusted until this connection resets.
    this._drivingDisabledWarningEvidenceInvalidRobot = requestRobot;
    this._systemStateResponseOwnershipAmbiguousRobot = requestRobot;
    this._systemStateResponseOwnershipRecoveryAt = Date.now() + SYSTEM_STATE_MONITORING_TIMEOUT_MS;
  }

  _isSystemStateResponseOwnershipAmbiguous(requestRobot) {
    if (this._systemStateResponseOwnershipAmbiguousRobot !== requestRobot) {
      return false;
    }

    if (Date.now() >= this._systemStateResponseOwnershipRecoveryAt) {
      this._systemStateResponseOwnershipAmbiguousRobot = null;
      this._systemStateResponseOwnershipRecoveryAt = 0;
      return false;
    }

    return true;
  }

  _retireSystemStateRequestsForRobot(requestRobot) {
    for (const entry of [...this._systemStateRequestQueue]) {
      if (entry.robot !== requestRobot) {
        continue;
      }
      clearTimeout(entry.timeoutId);
      if (entry.responseListener && typeof entry.robot?._off === 'function') {
        entry.robot._off(entry.robot.getSystemStateResponse, entry.responseListener);
      }
      this._systemStateRequestQueue.splice(this._systemStateRequestQueue.indexOf(entry), 1);
      if (!entry.settled) {
        entry.settled = true;
        entry.resolve(null);
      }
    }
    this._syncSystemStateRequestInFlight();
    this._resolveSystemStateDrainWaitersForRobot(requestRobot, false);
  }

  _resolveSystemStateDrainWaitersForRobot(requestRobot, drained) {
    for (const waiter of [...this._systemStateDrainWaiters]) {
      if (waiter.robot !== requestRobot) {
        continue;
      }
      clearTimeout(waiter.timeoutId);
      this._systemStateDrainWaiters.splice(this._systemStateDrainWaiters.indexOf(waiter), 1);
      waiter.resolve(drained);
    }
  }

  _notifySystemStateDrainWaiters(responseReceived = false) {
    for (const waiter of [...this._systemStateDrainWaiters]) {
      if (this._systemStateRequestQueue.some((entry) => entry.robot === waiter.robot)) {
        if (responseReceived) {
          clearTimeout(waiter.timeoutId);
          waiter.timeoutId = setTimeout(waiter.onQuiescence, waiter.timeoutMs);
        }
        continue;
      }
      clearTimeout(waiter.timeoutId);
      this._systemStateDrainWaiters.splice(this._systemStateDrainWaiters.indexOf(waiter), 1);
      waiter.resolve(true);
    }
  }

  _settleSystemStateRequest(entry, response) {
    const entryIndex = this._systemStateRequestQueue.indexOf(entry);
    if (entryIndex === -1) {
      return;
    }

    clearTimeout(entry.timeoutId);
    if (entry.responseListener && typeof entry.robot?._off === 'function') {
      entry.robot._off(entry.robot.getSystemStateResponse, entry.responseListener);
    }
    this._systemStateRequestQueue.splice(entryIndex, 1);
    if (!entry.settled) {
      entry.settled = true;
      entry.resolve(response ?? null);
    }
    this._syncSystemStateRequestInFlight();
    this._notifySystemStateDrainWaiters(response != null);
  }

  _handleSystemStateRequestTimeout(entry) {
    if (!this._systemStateRequestQueue.includes(entry)) {
      return;
    }

    this._markSystemStateResponseOwnershipAmbiguous(entry.robot);
    this._retireSystemStateRequestsForRobot(entry.robot);
  }

  _settleSystemStateResponse(entry, response) {
    if (!this._systemStateRequestQueue.includes(entry)) {
      return null;
    }

    if (this._isSystemStateResponseOwnershipAmbiguous(entry.robot)) {
      this._markSystemStateResponseOwnershipAmbiguous(entry.robot);
      this._settleSystemStateRequest(entry, null);
      return null;
    }

    this._settleSystemStateRequest(entry, response);
    return entry;
  }

  _trackSystemStateRequest(requestRobot = this.robot, timeoutMs = SYSTEM_STATE_MONITORING_TIMEOUT_MS, listenForResponse = false) {
    if (!requestRobot || typeof requestRobot.getSystemState !== 'function' || this._isSystemStateResponseOwnershipAmbiguous(requestRobot)) {
      return { entry: null, responsePromise: Promise.resolve(null) };
    }

    let resolveResponse;
    const responsePromise = new Promise((resolve) => {
      resolveResponse = resolve;
    });
    const entry = {
      robot: requestRobot,
      drivingDisabledWarningRunToken: this._drivingDisabledWarningRunToken,
      settled: false,
      resolve: resolveResponse,
      responseListener: null,
      timeoutId: null,
    };
    entry.timeoutId = setTimeout(() => this._handleSystemStateRequestTimeout(entry), timeoutMs);
    if (listenForResponse && requestRobot.getSystemStateResponse && typeof requestRobot._once === 'function') {
      entry.responseListener = (response) => {
        const wasOldestOutstandingRequest = this._systemStateRequestQueue.find((candidate) => candidate.robot === requestRobot) === entry;
        queueMicrotask(() => {
          if (wasOldestOutstandingRequest) {
            this._settleSystemStateResponse(entry, response);
          }
        });
      };
      requestRobot._once(requestRobot.getSystemStateResponse, entry.responseListener);
    }
    this._systemStateRequestQueue.push(entry);
    this._syncSystemStateRequestInFlight();

    try {
      requestRobot.getSystemState();
    } catch (error) {
      this._settleSystemStateRequest(entry, null);
      throw error;
    }

    return { entry, responsePromise };
  }

  _consumeSystemStateResponse(response, requestRobot = this.robot) {
    const entry = this._systemStateRequestQueue.find((candidate) => candidate.robot === requestRobot);
    if (!entry) {
      this._markSystemStateResponseOwnershipAmbiguous(requestRobot);
      return null;
    }
    return this._settleSystemStateResponse(entry, response);
  }

  _expireActiveSystemStateRequest(requestRobot = this.robot) {
    const entry = this._systemStateRequestQueue.find((candidate) => candidate.robot === requestRobot);
    if (entry) {
      this._handleSystemStateRequestTimeout(entry);
    }
  }

  _waitForSystemStateRequestsToDrain(requestRobot, timeoutMs = SYSTEM_STATE_MONITORING_TIMEOUT_MS) {
    if (!this._systemStateRequestQueue.some((entry) => entry.robot === requestRobot)) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const waiter = { robot: requestRobot, resolve, timeoutId: null, timeoutMs, onQuiescence: null };
      waiter.onQuiescence = () => {
        this._systemStateDrainWaiters.splice(this._systemStateDrainWaiters.indexOf(waiter), 1);
        this._markSystemStateResponseOwnershipAmbiguous(requestRobot);
        this._retireSystemStateRequestsForRobot(requestRobot);
        resolve(false);
      };
      waiter.timeoutId = setTimeout(waiter.onQuiescence, timeoutMs);
      this._systemStateDrainWaiters.push(waiter);
    });
  }

  async _requestDrivingDisabledWarningForProgramStart() {
    const requestRobot = this.robot;
    const requestToken = ++this._drivingDisabledWarningRequestToken;

    if (
      this._getEffectiveRobotType() !== ROBOTS.BPRDU ||
      !requestRobot?.getSystemStateResponse ||
      typeof requestRobot.getSystemState !== 'function' ||
      this._isDrivingDisabledWarningEvidenceInvalid(requestRobot)
    ) {
      return;
    }

    const systemStateMonitoringWasActive = isMonitoringSystemState;
    if (systemStateMonitoringWasActive) {
      systemStateMonitorTimeout.stop();
    }

    if (this._isSystemStateResponseOwnershipAmbiguous(requestRobot)) {
      if (systemStateMonitoringWasActive) {
        this._scheduleNextSystemStateMonitoringRequest();
      }
      return;
    }

    if (this._systemStateRequestQueue.some((entry) => entry.robot === requestRobot)) {
      const pendingResponsesDrained = await this._waitForSystemStateRequestsToDrain(requestRobot);

      if (this.robot !== requestRobot || this._drivingDisabledWarningRequestToken !== requestToken) {
        if (!pendingResponsesDrained && systemStateMonitoringWasActive) {
          this._scheduleNextSystemStateMonitoringRequest();
        }
        return;
      }

      if (this._isSystemStateResponseOwnershipAmbiguous(requestRobot)) {
        if (systemStateMonitoringWasActive) {
          this._scheduleNextSystemStateMonitoringRequest();
        }
        return;
      }

      if (!pendingResponsesDrained) {
        if (systemStateMonitoringWasActive) {
          this._scheduleNextSystemStateMonitoringRequest();
        }
        return;
      }

      // The normal response handler re-arms polling; keep it paused until the dedicated request completes.
      if (systemStateMonitoringWasActive) {
        systemStateMonitorTimeout.stop();
      }
    }

    let responsePromise;
    try {
      ({ responsePromise } = this._trackSystemStateRequest(requestRobot, SYSTEM_STATE_MONITORING_TIMEOUT_MS, true));
    } catch {
      if (systemStateMonitoringWasActive) {
        this._scheduleNextSystemStateMonitoringRequest();
      }
      return;
    }

    const response = await responsePromise;
    if (!response && systemStateMonitoringWasActive) {
      this._scheduleNextSystemStateMonitoringRequest();
    }

    if (this.robot !== requestRobot || this._drivingDisabledWarningRequestToken !== requestToken) {
      return;
    }

    this._showDrivingDisabledWarningFromSystemStateResponse(response);
  }

  _notifyDrivingDisabledWarningForObservedProgramStart(systemStateResponse) {
    this._drivingDisabledWarningRequestToken += 1;
    if (systemStateResponse) {
      this._showDrivingDisabledWarningFromSystemStateResponse(systemStateResponse);
      return;
    }

    void this._requestDrivingDisabledWarningForProgramStart();
  }

  _normalizeExecutingPythonProgramPath(name) {
    if (typeof name !== 'string') {
      return '';
    }

    return name.trim().replace(/\\/g, '/').replace(/\/+/g, '/').toLowerCase();
  }

  _normalizeExecutingPythonProgramName(name) {
    const normalizedPath = this._normalizeExecutingPythonProgramPath(name);
    if (!normalizedPath) {
      return '';
    }

    const lastSlashIndex = normalizedPath.lastIndexOf('/');
    return lastSlashIndex === -1 ? normalizedPath : normalizedPath.slice(lastSlashIndex + 1);
  }

  _canQueryPythonProgramNameAtIndex(requestRobot) {
    return (
      typeof requestRobot?.getPythonProgramNameAtIndex === 'function' &&
      Boolean(requestRobot.getPythonProgramNameAtIndexResponse) &&
      requestRobot.hasCommand?.('getPythonProgramNameAtIndex') === true
    );
  }

  _extractPythonProgramNameFromResponse(response) {
    if (response?.success !== true || response.data == null) {
      return null;
    }

    const { data } = response;
    if (typeof data === 'string') {
      return data.trim();
    }

    if (typeof data !== 'object') {
      return null;
    }

    for (const key of ['name', 'filename', 'fileName', 'programName']) {
      if (typeof data[key] === 'string') {
        return data[key].trim();
      }
    }

    return null;
  }

  async _queryPythonProgramNameAtSlot(requestRobot, slotNumber) {
    let timeoutId;
    let response;
    try {
      response = await Promise.race([
        requestRobot.getPythonProgramNameAtIndex(slotNumber - 1, SLOT_STORAGE_PROGRAM_NAME_QUERY_TIMEOUT_MS),
        new Promise((resolve) => {
          timeoutId = setTimeout(() => resolve(null), SLOT_STORAGE_PROGRAM_NAME_QUERY_TIMEOUT_MS);
        }),
      ]);
    } finally {
      clearTimeout(timeoutId);
    }

    return this._extractPythonProgramNameFromResponse(response);
  }

  _getExpectedLocalExecutingPythonPath() {
    const expectedFilename = this._normalizeExecutingPythonProgramName(this.currentFilename);
    if (!expectedFilename) {
      return '';
    }

    if (!this.currentSlot) {
      return expectedFilename;
    }

    return `/python_root/${this.currentSlot}/${expectedFilename}`;
  }

  /**
   * BPS-443 local-run vs drive-unit execution reconciliation.
   *
   * When BPS starts a program, `_locallyStartedProgramThisConnection` keeps Stop/deploy logic
   * tied to that run until we hand off to an observed external execution. Handoff is driven by
   * execution-boundary heuristics plus guard conditions that compare local workflow state against
   * robot-reported runtime state: overseer idle, `executing_python` re-entry, micropythonStatus
   * change, and python stream clear/re-register or fingerprint change.
   *
   * `getExecutingPythonTypeAndName` only exposes filename/path and micropythonStatus — not a
   * run-instance id. Same-slot/same-filename DU reruns that never surface a boundary can leave
   * BPS attached to stale local state while Redux still shows STARTED/programExecuting. That case
   * is an unresolved platform limitation (BPS-443) until firmware exposes a distinct execution
   * instance; faster overseer polling only mitigates repros where overseer briefly reports idle.
   */

  _clearLocalRunTracking() {
    this._locallyStartedProgramThisConnection = false;
    this._awaitingLocalRunOverseerExecution = false;
    this._localRunStartedAt = null;
    this._localRunStoppedAt = null;
    this._localRunOverseerIdleSeen = false;
    this._localRunOverseerExecutionEstablished = false;
    this._localRunPythonStreamNotificationSeen = false;
    this._localRunPythonStreamBaselineFingerprint = null;
    this._localRunPythonStreamClearedSeen = false;
    this._localRunOverseerExecutionReentrySeen = false;
    this._localRunMicropythonStatusTransitionSeen = false;
    this._localRunMatchingExecutingIdentityEstablished = false;
    this._localRunExecutionBoundarySeen = false;
    this._localRunLastMicropythonStatus = null;
  }

  _localRunStreamFingerprint(streamItems) {
    if (!Array.isArray(streamItems) || streamItems.length === 0) {
      return '';
    }

    return streamItems.map((item) => `${item.micropythonType}:${item.name}`).join('|');
  }

  _hasLocalRunExecutionRestartEvidence() {
    return this._localRunOverseerIdleSeen || this._localRunOverseerExecutionReentrySeen || this._localRunMicropythonStatusTransitionSeen;
  }

  _getRuntimeProgramState() {
    return this.store.getState().robot?.runtimeProgramState ?? null;
  }

  _isRuntimeExecutingPython() {
    return isDrivebaseSystemStateExecutingPython(this._getRuntimeProgramState());
  }

  _isProgramFsmActive(state = this.state) {
    return (
      PROGRAM_BUSY_STATES.includes(state) ||
      state === FIRMWARE_PROGRAM_STATES.STARTING ||
      state === FIRMWARE_PROGRAM_STATES.EXECUTING ||
      state === FIRMWARE_PROGRAM_STATES.STOPPING
    );
  }

  _isProgramFsmRunning() {
    return (
      this.is(LOCAL_PROGRAM_STARTING_STATE) ||
      this.is(LOCAL_PROGRAM_EXECUTING_STATE) ||
      this.is(FIRMWARE_PROGRAM_STATES.STARTING) ||
      this.is(FIRMWARE_PROGRAM_STATES.EXECUTING) ||
      this.is('programStarting') ||
      this.is('programExecuting')
    );
  }

  _isObservedProgramExecutingState() {
    return this.is(OBSERVED_PROGRAM_EXECUTING_STATE);
  }

  _isProgramStoppingState() {
    return this.is(PROGRAM_STOPPING_STATE) || this.is('programStopping');
  }

  _isLocallyRunningProgram(localProgramState, isProgramFsmRunning = this._isProgramFsmRunning()) {
    return isProgramFsmRunning && (localProgramState === RobotActions.PROGRAM.STARTING || localProgramState === RobotActions.PROGRAM.STARTED);
  }

  /**
   * Guard for the ready -> programExecuting sync path when the DU is already running Python.
   * This stays as a derived condition rather than a durable FSM state because it depends on both
   * local workflow state and robot-reported runtime state, which intentionally diverge in BPS-443.
   */
  _canSyncObservedExternalRunningProgram(overseerProgramState, localProgramState, isProgramFsmRunning = this._isProgramFsmRunning()) {
    return (
      isDrivebaseSystemStateExecutingPython(overseerProgramState) &&
      this.is('ready') &&
      this.can('syncProgramExecuting') &&
      !this._isLocallyRunningProgram(localProgramState, isProgramFsmRunning) &&
      !this._isProgramStoppingState() &&
      !this._locallyStartedProgramThisConnection &&
      !this._observedExternalRunningProgramSynced
    );
  }

  _shouldObserveExecutingPythonIdentityForLocalRun(overseerProgramState, localProgramState, isProgramFsmRunning = this._isProgramFsmRunning()) {
    return (
      isDrivebaseSystemStateExecutingPython(overseerProgramState) &&
      this._locallyStartedProgramThisConnection &&
      !this._isProgramStoppingState() &&
      localProgramState === RobotActions.PROGRAM.STARTED &&
      !this._awaitingLocalRunOverseerExecution &&
      !this._localRunExecutionBoundarySeen &&
      isProgramFsmRunning
    );
  }

  _shouldHandoffLocalRunToObservedExternal(overseerProgramState, localProgramState, isProgramFsmRunning = this._isProgramFsmRunning()) {
    return (
      this._locallyStartedProgramThisConnection &&
      !this._isProgramStoppingState() &&
      !this._awaitingLocalRunOverseerExecution &&
      isDrivebaseSystemStateExecutingPython(overseerProgramState) &&
      localProgramState !== RobotActions.PROGRAM.STOPPED &&
      (this._localRunOverseerIdleSeen || this._localRunExecutionBoundarySeen || !isProgramFsmRunning)
    );
  }

  _noteLocalRunExecutionBoundary() {
    if (!this._locallyStartedProgramThisConnection || this._localRunExecutionBoundarySeen) {
      return;
    }

    this._localRunExecutionBoundarySeen = true;

    const projectState = this.store.getState().project;
    const isProgramFsmRunning = this._isProgramFsmRunning();
    if (
      !this._awaitingLocalRunOverseerExecution &&
      !this._isProgramStoppingState() &&
      projectState?.programState === RobotActions.PROGRAM.STARTED &&
      this._isRuntimeExecutingPython() &&
      isProgramFsmRunning
    ) {
      void this._handoffLocalRunToObservedExternal();
    }
  }

  _noteLocalRunPythonStreamNotification(streamItems = []) {
    if (!this._locallyStartedProgramThisConnection) {
      return;
    }

    const fingerprint = this._localRunStreamFingerprint(streamItems);
    const hasItems = streamItems.length > 0;

    if (!this._localRunPythonStreamNotificationSeen) {
      this._localRunPythonStreamNotificationSeen = true;
      if (hasItems) {
        this._localRunPythonStreamBaselineFingerprint = fingerprint;
      }
      return;
    }

    if (!this._localRunOverseerExecutionEstablished) {
      return;
    }

    if (this._hasLocalRunExecutionRestartEvidence()) {
      this._noteLocalRunExecutionBoundary();
      return;
    }

    if (!hasItems) {
      this._localRunPythonStreamClearedSeen = true;
      return;
    }

    if (this._localRunPythonStreamClearedSeen) {
      this._noteLocalRunExecutionBoundary();
      return;
    }

    if (this._localRunPythonStreamBaselineFingerprint != null && fingerprint !== this._localRunPythonStreamBaselineFingerprint) {
      this._noteLocalRunExecutionBoundary();
    }
  }

  _shouldSyncObservedExternalRunningProgramAfterLocalHandoff(
    overseerProgramState,
    localProgramState,
    { forceSyncObservedExecution = false } = {},
    isProgramFsmRunning = this._isProgramFsmRunning()
  ) {
    if (this._shouldHandoffLocalRunToObservedExternal(overseerProgramState, localProgramState, isProgramFsmRunning)) {
      return true;
    }

    if (!forceSyncObservedExecution) {
      return false;
    }

    const canSyncObservedExternalRunningProgramFromReady =
      isDrivebaseSystemStateExecutingPython(overseerProgramState) &&
      this.is('ready') &&
      this.can('syncProgramExecuting') &&
      !this._isLocallyRunningProgram(localProgramState, isProgramFsmRunning) &&
      !this._isProgramStoppingState() &&
      !this._observedExternalRunningProgramSynced;

    return (
      isDrivebaseSystemStateExecutingPython(overseerProgramState) &&
      localProgramState !== RobotActions.PROGRAM.STOP &&
      localProgramState !== RobotActions.PROGRAM.STOPPED &&
      !this._isProgramStoppingState() &&
      (this.is(LOCAL_PROGRAM_EXECUTING_STATE) || canSyncObservedExternalRunningProgramFromReady)
    );
  }

  async _handoffLocalRunToObservedExternal(options = {}) {
    if (!this._locallyStartedProgramThisConnection) {
      return;
    }

    if (this._handoffLocalRunToObservedExternalPromise) {
      return this._handoffLocalRunToObservedExternalPromise;
    }

    const handoffOptions = { ...options };
    const handoffRobot = this.robot;
    const handoffWarningRunToken = this._drivingDisabledWarningRunToken;
    const handoffWarningRequestToken = this._drivingDisabledWarningRequestToken;

    const handoffPromise = (async () => {
      await saveSensorRun();
      if (this.robot !== handoffRobot || this._robotDisconnectReleased || this._drivingDisabledWarningRunToken !== handoffWarningRunToken) {
        return;
      }

      const localProgramState = this.store.getState().project?.programState;
      const observedRuntimeProgramState = this._getObservedRuntimeProgramStateForSync();
      const isProgramFsmRunning = this._isProgramFsmRunning();
      const shouldSyncObservedExternalRunningProgram = this._shouldSyncObservedExternalRunningProgramAfterLocalHandoff(
        observedRuntimeProgramState,
        localProgramState,
        handoffOptions,
        isProgramFsmRunning
      );
      this._clearLocalRunTracking();
      if (shouldSyncObservedExternalRunningProgram) {
        const warningEvidenceIsCurrent = this._drivingDisabledWarningRequestToken === handoffWarningRequestToken;
        this._syncObservedExternalRunningProgram({
          ...handoffOptions,
          systemStateResponse: warningEvidenceIsCurrent ? handoffOptions.systemStateResponse : undefined,
          preserveDrivingDisabledWarningRun: true,
        });
      }
    })().finally(() => {
      if (this._handoffLocalRunToObservedExternalPromise === handoffPromise) {
        this._handoffLocalRunToObservedExternalPromise = null;
      }
    });

    this._handoffLocalRunToObservedExternalPromise = handoffPromise;
    return handoffPromise;
  }

  _syncObservedExternalRunningProgram(options = {}) {
    if (this._observedExternalRunningProgramSynced) {
      return;
    }

    this._awaitObservedRuntimeReconfirmation = false;
    this._observedExternalRunningProgramSynced = true;
    if (!options.preserveDrivingDisabledWarningRun) {
      this._beginDrivingDisabledWarningRun();
    }
    this._notifyDrivingDisabledWarningForObservedProgramStart(options.systemStateResponse);
    const notificationContextRequestToken = ++this._runningProgramNotificationContextRequestToken;
    void this._syncObservedRunningProgram();
    const executingName = options.executingName;
    if (typeof executingName === 'string' && executingName.trim()) {
      this._applyExecutingPythonIdentityToCurrentRunTarget(executingName);
      this._dispatchRunningProgramNotificationFromExecutingName(executingName);
    } else {
      void this._refreshRunningProgramNotificationContext(this.robot, notificationContextRequestToken);
    }
    this.dispatch(
      this.programActions.onUpdateProgramState({
        programState: RobotActions.PROGRAM.STARTED,
      })
    );
  }

  _dispatchRunningProgramNotificationFromExecutingName(executingName) {
    const slotNumber = slotNumberFromExecutingPythonPath(executingName);
    const slotStorage = this.store.getState().robotData?.slotStorage;
    const displayName = resolveExecutingPythonDisplayName(executingName, slotStorage, slotNumber);
    if (!displayName) {
      return;
    }

    this.dispatch(
      this.programActions.setActiveRunningProgramNotificationContext({
        displayName,
        slotNumber,
      })
    );
  }

  async _refreshRunningProgramNotificationContext(requestRobot = this.robot, requestToken = this._runningProgramNotificationContextRequestToken) {
    if (!requestRobot) {
      return;
    }

    if (
      typeof requestRobot.getExecutingPythonTypeAndName !== 'function' ||
      !requestRobot.getExecutingPythonTypeAndNameResponse ||
      requestRobot.hasCommand?.('getExecutingPythonTypeAndName') !== true
    ) {
      return;
    }

    try {
      const responsePromise = this._waitForRobotResponse(
        requestRobot.getExecutingPythonTypeAndNameResponse,
        STREAMED_ITEM_DEFINITION_QUERY_TIMEOUT_MS,
        requestRobot
      );
      requestRobot.getExecutingPythonTypeAndName();
      const response = await responsePromise;

      const projectProgramState = this.store.getState().project?.programState;
      if (
        this.robot !== requestRobot ||
        requestToken !== this._runningProgramNotificationContextRequestToken ||
        !this._observedExternalRunningProgramSynced ||
        projectProgramState !== RobotActions.PROGRAM.STARTED ||
        response?.success !== true ||
        response.data == null
      ) {
        return;
      }

      const executingName = response.data?.name;
      if (typeof executingName !== 'string' || !executingName.trim()) {
        return;
      }

      this._applyExecutingPythonIdentityToCurrentRunTarget(executingName);
      this._dispatchRunningProgramNotificationFromExecutingName(executingName);
    } catch (error) {
      console.error('Failed to refresh running program notification context:', error);
    }
  }

  _syncObservedExternalRunningProgramFromActivity() {
    if (this._awaitObservedRuntimeReconfirmation) {
      return false;
    }

    const projectProgramState = this.store.getState().project?.programState;
    if (!this._canSyncObservedExternalRunningProgram(DRIVEBASE_SYSTEM_STATE.EXECUTING_PYTHON, projectProgramState)) {
      return false;
    }

    this.dispatch(this.robotCommandActions.onUpdateRuntimeProgramState(DRIVEBASE_SYSTEM_STATE.EXECUTING_PYTHON));
    this._syncObservedExternalRunningProgram();
    return true;
  }

  _getObservedRuntimeProgramStateForSync() {
    return this._getRuntimeProgramState() ?? this._validationObservedRuntimeProgramState ?? null;
  }

  _scheduleObservedExternalRunningProgramSyncFromKnownRuntime() {
    queueMicrotask(() => {
      const projectProgramState = this.store.getState().project?.programState;
      const overseerProgramState = this._getObservedRuntimeProgramStateForSync();
      if (isDrivebaseSystemStateExecutingPython(overseerProgramState) && !this._isRuntimeExecutingPython()) {
        this.dispatch(this.robotCommandActions.onUpdateRuntimeProgramState(DRIVEBASE_SYSTEM_STATE.EXECUTING_PYTHON));
      }
      if (this._canSyncObservedExternalRunningProgram(overseerProgramState, projectProgramState)) {
        this._syncObservedExternalRunningProgram();
      }
    });
  }

  /**
   * True when executing-python identity should be treated as a DU-owned run (hand off local tracking).
   * Same path as the local run returns false until an execution boundary was observed — see BPS-443 note above.
   */
  _shouldTreatExecutingPythonIdentityAsExternal(identity) {
    if (identity == null || typeof identity !== 'object') {
      return false;
    }

    if (!this._locallyStartedProgramThisConnection || this._isProgramStoppingState() || this._awaitingLocalRunOverseerExecution) {
      return false;
    }

    const projectState = this.store.getState().project;
    if (projectState?.programState !== RobotActions.PROGRAM.STARTED || !this._isRuntimeExecutingPython()) {
      return false;
    }

    if (identity.micropythonStatus != null && identity.micropythonStatus !== 'execute_file') {
      return true;
    }

    if (this._localRunOverseerIdleSeen || this._localRunExecutionBoundarySeen) {
      return true;
    }

    const isProgramFsmRunning = this._isProgramFsmRunning();
    if (!isProgramFsmRunning) {
      return true;
    }

    const observedPath = this._normalizeExecutingPythonProgramPath(identity.name);
    if (!observedPath) {
      return false;
    }

    const expectedLocalPath = this._getExpectedLocalExecutingPythonPath();
    if (!expectedLocalPath) {
      return false;
    }

    if (observedPath.includes('/')) {
      return observedPath !== expectedLocalPath;
    }

    return this._normalizeExecutingPythonProgramName(observedPath) !== this._normalizeExecutingPythonProgramName(expectedLocalPath);
  }

  _observeExecutingPythonIdentityForLocalRun(requestRobot = this.robot) {
    if (!requestRobot || this.robot !== requestRobot || this._executingPythonIdentityRequestPromise) {
      return this._executingPythonIdentityRequestPromise;
    }

    if (
      typeof requestRobot.getExecutingPythonTypeAndName !== 'function' ||
      !requestRobot.getExecutingPythonTypeAndNameResponse ||
      requestRobot.hasCommand?.('getExecutingPythonTypeAndName') !== true
    ) {
      return null;
    }

    let requestPromise = null;
    requestPromise = (async () => {
      const responsePromise = this._waitForRobotResponse(
        requestRobot.getExecutingPythonTypeAndNameResponse,
        STREAMED_ITEM_DEFINITION_QUERY_TIMEOUT_MS,
        requestRobot
      );
      requestRobot.getExecutingPythonTypeAndName();
      const response = await responsePromise;

      if (this.robot !== requestRobot || response?.success !== true || response.data == null) {
        return;
      }

      const micropythonStatus = response.data?.micropythonStatus ?? null;
      if (this._localRunLastMicropythonStatus != null && micropythonStatus !== this._localRunLastMicropythonStatus) {
        this._localRunMicropythonStatusTransitionSeen = true;
        this._noteLocalRunExecutionBoundary();
      }
      if (micropythonStatus != null) {
        this._localRunLastMicropythonStatus = micropythonStatus;
      }

      const observedPath = this._normalizeExecutingPythonProgramPath(response.data?.name);
      const expectedLocalPath = this._getExpectedLocalExecutingPythonPath();
      const identityMatchesLocalRun =
        observedPath &&
        expectedLocalPath &&
        (observedPath.includes('/')
          ? observedPath === expectedLocalPath
          : this._normalizeExecutingPythonProgramName(observedPath) === this._normalizeExecutingPythonProgramName(expectedLocalPath));

      if (identityMatchesLocalRun && this._localRunOverseerExecutionEstablished && !this._awaitingLocalRunOverseerExecution) {
        if (this._localRunMatchingExecutingIdentityEstablished && this._localRunExecutionBoundarySeen) {
          await this._handoffLocalRunToObservedExternal();
          return;
        }

        this._localRunMatchingExecutingIdentityEstablished = true;
      }

      if (this._shouldTreatExecutingPythonIdentityAsExternal(response.data)) {
        await this._handoffLocalRunToObservedExternal({
          forceSyncObservedExecution: true,
          executingName: response.data?.name,
        });
      }
    })().finally(() => {
      if (this._executingPythonIdentityRequestPromise === requestPromise) {
        this._executingPythonIdentityRequestPromise = null;
      }
    });

    this._executingPythonIdentityRequestPromise = requestPromise;
    return requestPromise;
  }

  _selectBestLibraryUpdaterState(primaryPayload, fallbackFirmwareVersions) {
    const normalizedPrimary = normalizeLibraryUpdaterProgressPayload(primaryPayload);
    const normalizedFallback = normalizeLibraryUpdaterProgressPayload(fallbackFirmwareVersions);

    if (hasConcreteLibraryUpdaterPhase(normalizedPrimary)) {
      return normalizedPrimary;
    }

    if (isReconnectSafeLibraryUpdaterFallbackPhase(normalizedFallback?.state)) {
      return normalizedFallback;
    }

    return normalizedPrimary ?? (normalizedFallback?.criticalUpdate === true ? { criticalUpdate: true } : null);
  }

  _applyBestLibraryUpdaterState(primaryPayload, fallbackFirmwareVersions) {
    const bestUpdaterStatePayload = this._selectBestLibraryUpdaterState(primaryPayload, fallbackFirmwareVersions);
    const firmwareStatusInput = {
      updaterState: bestUpdaterStatePayload,
      versionMetadata: fallbackFirmwareVersions ?? null,
    };
    const nextFirmwareEvaluation = evaluateFirmwareStatus(firmwareStatusInput);
    const currentFirmwareState = this.store.getState().robot?.firmware?.firmwareState;
    if (isEvaluatedFirmwareStatus(nextFirmwareEvaluation.status) && isFirmwareFailureState(currentFirmwareState)) {
      this.dispatch(this.robotCommandActions.onFirmwareStateChange({ firmwareState: null }));
    }

    if (typeof this.robotFirmwareManager?.setFirmwareVersions === 'function') {
      this.robotFirmwareManager.setFirmwareVersions(firmwareStatusInput);
    }
    return bestUpdaterStatePayload;
  }

  _isStaleValidatingSession(requestRobot) {
    const stillValidating = (typeof this.is === 'function' && this.is('validating')) || this.state === 'validating';
    return !requestRobot || this.robot !== requestRobot || !stillValidating;
  }

  _isStaleDeferredConnectionValidation(requestRobot) {
    return !requestRobot || this.robot !== requestRobot || !this._deferredConnectionValidationPending;
  }

  async _syncPythonStreamDefinitionsFromRobot() {
    const requestRobot = this.robot;
    if (!requestRobot) {
      return [];
    }
    const syncRequestToken = this._runningProgramNotificationContextRequestToken;

    if (this._hasTrustedCommittedPythonStreamDefinitionsForRun()) {
      if (requestRobot.enablePythonStreaming) {
        requestRobot.enablePythonStreaming(true);
      }
      return this.streamedItemDefinitions;
    }

    if (await this._realignCommittedPythonStreamDefinitionsIfNeeded(requestRobot)) {
      if (requestRobot.enablePythonStreaming) {
        requestRobot.enablePythonStreaming(true);
      }
      return this.streamedItemDefinitions;
    }

    // Realignment can latch the run as unprovable. Stop before the cache, the query, and the state
    // reset below, all of which would otherwise recommit definitions and turn streaming back on.
    if (!this._canEnablePythonStreamingForRun()) {
      return this.streamedItemDefinitions;
    }

    const cachedDefinitions = this._getCachedPythonStreamDefinitionsForObservedRun();

    const supportsDefinitionQuery =
      typeof requestRobot.getNumberOfPythonStreamedItems === 'function' &&
      typeof requestRobot.getPythonStreamItemData === 'function' &&
      requestRobot.hasCommand?.('getNumberOfPythonStreamedItems') === true &&
      requestRobot.hasCommand?.('getPythonStreamItemData') === true &&
      requestRobot.getNumberOfStreamedItemsResponse &&
      requestRobot.getStreamItemDataResponse;

    if (!supportsDefinitionQuery) {
      if (cachedDefinitions.length > 0) {
        this._commitPythonStreamDefinitionsForRun(cachedDefinitions, { alignmentTrusted: true });
      }
      if (requestRobot.enablePythonStreaming) {
        requestRobot.enablePythonStreaming(true);
      }
      return this.streamedItemDefinitions;
    }

    if (!this._hasCommittedPythonStreamDefinitionsForRun()) {
      this._resetPythonStreamState();
      if (cachedDefinitions.length > 0) {
        this._commitPythonStreamDefinitionsForRun(cachedDefinitions, { alignmentTrusted: true });
      }
    }

    const countResponsePromise = this._waitForRobotResponse(
      requestRobot.getNumberOfStreamedItemsResponse,
      STREAMED_ITEM_DEFINITION_QUERY_TIMEOUT_MS,
      requestRobot
    );
    requestRobot.getNumberOfPythonStreamedItems();
    const countResponse = await countResponsePromise;

    if (this._isStalePythonStreamAlignmentRequest(requestRobot, syncRequestToken)) {
      return [];
    }

    const requestedCount = Number(countResponse?.data?.numberOfStreamItems);
    this.numberOfStreamedItems = Number.isFinite(requestedCount) && requestedCount > 0 ? Math.trunc(requestedCount) : 0;
    if (this.numberOfStreamedItems === 0) {
      if (requestRobot.enablePythonStreaming) {
        requestRobot.enablePythonStreaming(true);
      }
      return this.streamedItemDefinitions;
    }

    const definitions = Array(this.numberOfStreamedItems);
    for (let index = 0; index < this.numberOfStreamedItems; index++) {
      const definitionResponsePromise = this._waitForRobotResponse(
        requestRobot.getStreamItemDataResponse,
        STREAMED_ITEM_DEFINITION_QUERY_TIMEOUT_MS,
        requestRobot
      );
      requestRobot.getPythonStreamItemData(index);
      const definitionResponse = await definitionResponsePromise;

      if (this._isStalePythonStreamAlignmentRequest(requestRobot, syncRequestToken)) {
        return [];
      }

      const definitionData = definitionResponse?.data;
      const rawIndex = Number(definitionData?.startingIndex);
      const definitionIndex = Number.isInteger(rawIndex) && rawIndex >= 0 ? rawIndex : index;

      definitions[definitionIndex] = {
        name: definitionData?.name ?? `value_${definitionIndex}`,
        micropythonType: definitionData?.micropythonType ?? 'unknown',
      };
    }

    const compactDefinitions = definitions.filter((definition) => definition != null);
    const programCode = await this._resolveProgramCodeForStreamDefinitionAlignment(requestRobot);
    if (this._isStalePythonStreamAlignmentRequest(requestRobot, syncRequestToken)) {
      return [];
    }
    const alignedDefinitions = this._alignPythonStreamDefinitionsToProgramRequestList(compactDefinitions, programCode);
    if (alignedDefinitions == null) {
      this._invalidatePythonStreamDefinitionsForUnprovableAlignment();
      return [];
    }
    this._commitPythonStreamDefinitionsForRun(alignedDefinitions, {
      alignmentTrusted:
        this._isResolvedProgramCodeForStreamAlignment(programCode) && this._coversDerivedPythonStreamRegistration(alignedDefinitions, programCode),
    });
    if (requestRobot.enablePythonStreaming) {
      requestRobot.enablePythonStreaming(true);
    }

    return this.streamedItemDefinitions;
  }

  async _syncObservedRunningProgram() {
    const requestRobot = this.robot;
    if (!requestRobot) {
      return;
    }

    if (this.is('ready') && this.can('syncProgramExecuting')) {
      this.syncProgramExecuting();
    } else if (this.is(LOCAL_PROGRAM_EXECUTING_STATE) && this.can('handoffToObservedExecution')) {
      this.handoffToObservedExecution();
    }

    this.startTime = performance.now();
    this._resetLiveSensorStreamState();
    this._flushPendingPythonPrintOutput(true);
    this.dispatch(this.robotCommandActions.onUpdateLiveSensorData(null));

    if (typeof requestRobot.onProgramLoad === 'function') {
      requestRobot.onProgramLoad();
    }

    if (requestRobot.enablePythonPrintToConsole) {
      requestRobot.enablePythonPrintToConsole(true);
    }

    if (this.robot === requestRobot && this._getEffectiveRobotType() === ROBOTS.BPRDU) {
      if (typeof this.getNonPortRemoteDeviceSystemInfo === 'function') {
        this.getNonPortRemoteDeviceSystemInfo();
      }
      // Asset metadata is unreliable while Python is executing; defer one slot query until idle
      // only when this connection has not already loaded slot metadata.
      if (!this._slotStorageMetadataLoadedForConnection) {
        this._deferSlotStorageQueryUntilObservedProgramEnds = true;
        this._observedProgramEndedSlotStorageQueryAttempted = false;
      }
    }

    try {
      await this._ensureExecutingPythonIdentityForStreamAlignment(requestRobot);
      await this._syncPythonStreamDefinitionsFromRobot();
    } catch (error) {
      console.error('Failed to sync Python stream definitions for an observed running program:', error);
    }

    if (
      this.robot === requestRobot &&
      requestRobot.enablePythonStreaming &&
      this.streamedItemDefinitions.length > 0 &&
      this._canEnablePythonStreamingForRun()
    ) {
      requestRobot.enablePythonStreaming(true);
    }
  }

  _runConnectionMetadataBootstrapIfReady({ skipFirmwareVersions = false } = {}) {
    if (!this.robot || this._connectionMetadataBootstrapped) {
      return;
    }

    if (this._deferredConnectionValidationPending) {
      return;
    }

    this._pendingConnectionMetadataBootstrap = false;
    this._deferConnectionMetadataBootstrapUntilObservedProgramEnds = false;
    this._connectionMetadataBootstrapped = true;
    this._validationObservedRuntimeProgramState = null;

    if (!skipFirmwareVersions) {
      this.getRobotFirmwareVersions();
    }
    this.getRemoteDeviceTypes();

    // Sweep non-port remote device system info without blocking the ready transition;
    // requests run one-at-a-time, chained from responses.
    this.getNonPortRemoteDeviceSystemInfo();

    if (
      this._getEffectiveRobotType() === ROBOTS.BPRDU &&
      typeof this.queryRobotSlotStorage === 'function' &&
      this.robot?.hasCommand?.('getUid') === true &&
      this.store.getState().robotData?.uid &&
      !this._shouldDeferSlotStorageQueryForRunningProgram() &&
      !this._slotStorageMetadataLoadedForConnection
    ) {
      void this.queryRobotSlotStorage();
    }
  }

  _clearDisconnectCompletionTimeout() {
    if (this._disconnectCompletionTimeout) {
      clearTimeout(this._disconnectCompletionTimeout);
      this._disconnectCompletionTimeout = null;
    }
  }

  _scheduleDisconnectCompletion(context) {
    this._clearDisconnectCompletionTimeout();
    this._disconnectCompletionTimeout = setTimeout(() => {
      this._disconnectCompletionTimeout = null;
      _logMessage(`Disconnect timed out waiting for '${context}', forcing cleanup from '${this.state}'`);
      this._completeDisconnect('disconnect timeout');
    }, DISCONNECT_COMPLETION_TIMEOUT);
  }

  _clearPendingConnectionTimer() {
    if (this._pendingConnectionTimer) {
      clearTimeout(this._pendingConnectionTimer);
      this._pendingConnectionTimer = null;
    }
  }

  _clearBprFirmwareLoadTimer() {
    if (this._bprFirmwareLoadTimer) {
      clearTimeout(this._bprFirmwareLoadTimer);
      this._bprFirmwareLoadTimer = null;
    }
  }

  _clearRemoteDeviceTypesTimer() {
    if (this._remoteDeviceTypesTimer) {
      clearTimeout(this._remoteDeviceTypesTimer);
      this._remoteDeviceTypesTimer = null;
    }
  }

  _clearRemoteDeviceTypesTimeoutBlock() {
    this._remoteDeviceTypesTimeoutBlockedRobot = null;
  }

  _blockRemoteDeviceTypesForUnknownOwnership(requestRobot) {
    if (!requestRobot || requestRobot !== this.robot || this._robotDisconnectReleased) {
      return;
    }

    this._clearRemoteDeviceTypesTimer();
    this._remoteDeviceTypesTimeoutBlockedRobot = requestRobot;
    this._clearRemoteDeviceTypesBackgroundRecovery();
    const error = new Error(REMOTE_DEVICE_TYPES_TIMEOUT_BLOCKED_ERROR);
    error.code = REMOTE_DEVICE_TYPES_TIMEOUT_BLOCKED_ERROR_CODE;

    const activeRequest = this._remoteDeviceTypesActiveRequest;
    this._remoteDeviceTypesActiveRequest = null;
    if (activeRequest?.kind === 'one-shot') {
      activeRequest.reject(error);
    }
    for (const request of this._remoteDeviceTypesOneShotQueue) {
      request.reject(error);
    }
    this._remoteDeviceTypesOneShotQueue = [];
    this.dispatch(this.robotDataActions.onCompleteRemoteDeviceTypesQuery());
  }

  _isCurrentRemoteDeviceTypesRequest(request, attemptId = request?.attemptId) {
    return (
      request != null &&
      this._remoteDeviceTypesActiveRequest === request &&
      request.robot === this.robot &&
      request.connectionGeneration === this._remoteDeviceTypesConnectionGeneration &&
      request.attemptId === attemptId &&
      !this._robotDisconnectReleased &&
      this._remoteDeviceTypesTimeoutBlockedRobot !== this.robot
    );
  }

  _addUnresolvedRemoteDeviceTypesSequence(request, sequenceNumber) {
    if (
      !isValidRemoteDeviceTypesSequenceNumber(sequenceNumber) ||
      request?.robot !== this.robot ||
      request?.connectionGeneration !== this._remoteDeviceTypesConnectionGeneration ||
      this._robotDisconnectReleased
    ) {
      return;
    }

    if (!this._remoteDeviceTypesUnresolvedSequences.includes(sequenceNumber)) {
      this._remoteDeviceTypesUnresolvedSequences.push(sequenceNumber);
    }
  }

  _completeRemoteDeviceTypesAttemptWithError(request, error) {
    if (!this._isCurrentRemoteDeviceTypesRequest(request)) {
      return;
    }

    const resumeBackground = request.kind === 'background' && this._remoteDeviceTypesOneShotQueue.length > 0;
    this._completeRemoteDeviceTypesRequest({ error, resumeBackground });
    if (request.kind === 'background') {
      this.dispatch(this.robotDataActions.onCompleteRemoteDeviceTypesQuery());
    }
  }

  _requestRemoteDeviceTypesBackgroundRecovery() {
    if (!this.robot || this._robotDisconnectReleased || this.store.getState().robotData?.remoteDeviceTypesReady === true) {
      return;
    }

    this._remoteDeviceTypesBackgroundRecoveryPending = true;
  }

  _clearRemoteDeviceTypesBackgroundRecovery() {
    this._remoteDeviceTypesBackgroundRecoveryPending = false;
  }

  _maybeResumeRemoteDeviceTypesBackground() {
    if (!this._remoteDeviceTypesBackgroundRecoveryPending) {
      return;
    }

    if (!this.robot || this._robotDisconnectReleased || this.store.getState().robotData?.remoteDeviceTypesReady === true) {
      this._clearRemoteDeviceTypesBackgroundRecovery();
      return;
    }

    if (this._remoteDeviceTypesActiveRequest || this._remoteDeviceTypesTimeoutBlockedRobot || this._remoteDeviceTypesOneShotQueue.length > 0) {
      return;
    }

    this._clearRemoteDeviceTypesBackgroundRecovery();
    this._startRemoteDeviceTypesRequest({ kind: 'background', robot: this.robot });
  }

  _cancelRemoteDeviceTypesRequests(error = new Error('Remote device type request cancelled.')) {
    this._clearRemoteDeviceTypesTimer();
    this._clearRemoteDeviceTypesTimeoutBlock();
    this._clearRemoteDeviceTypesBackgroundRecovery();
    this._remoteDeviceTypesConnectionGeneration += 1;
    this._remoteDeviceTypesUnresolvedSequences = [];

    const activeRequest = this._remoteDeviceTypesActiveRequest;
    this._remoteDeviceTypesActiveRequest = null;
    if (activeRequest?.kind === 'one-shot') {
      activeRequest.reject(error);
    }

    for (const request of this._remoteDeviceTypesOneShotQueue) {
      request.reject(error);
    }
    this._remoteDeviceTypesOneShotQueue = [];
  }

  _startNextRemoteDeviceTypesOneShotRequest() {
    if (this._remoteDeviceTypesActiveRequest || this._remoteDeviceTypesTimeoutBlockedRobot) {
      return;
    }

    while (this._remoteDeviceTypesOneShotQueue.length > 0) {
      const request = this._remoteDeviceTypesOneShotQueue.shift();
      if (request.robot === this.robot && request.connectionGeneration === this._remoteDeviceTypesConnectionGeneration) {
        this._startRemoteDeviceTypesRequest(request);
        return;
      }
      request.reject(new Error('Drive unit disconnected before the sensor check started.'));
    }
  }

  _completeRemoteDeviceTypesRequest({ connectedPortTypes, error, resumeBackground = false } = {}) {
    const request = this._remoteDeviceTypesActiveRequest;
    if (!request) {
      return;
    }

    this._clearRemoteDeviceTypesTimer();
    this._remoteDeviceTypesActiveRequest = null;
    request.awaitingResponse = false;
    request.handlePending = false;
    request.bufferedResponses = [];
    request.completed = true;
    if (request.kind === 'one-shot') {
      if (error) {
        request.reject(error);
      } else {
        request.resolve(connectedPortTypes);
      }
    }
    if (resumeBackground) {
      this._requestRemoteDeviceTypesBackgroundRecovery();
    }
    this._startNextRemoteDeviceTypesOneShotRequest();
    this._maybeResumeRemoteDeviceTypesBackground();
  }

  _startRemoteDeviceTypesRequest(request) {
    request.connectionGeneration ??= this._remoteDeviceTypesConnectionGeneration;
    if (
      !request.robot ||
      request.robot !== this.robot ||
      request.connectionGeneration !== this._remoteDeviceTypesConnectionGeneration ||
      this._robotDisconnectReleased
    ) {
      if (request.kind === 'one-shot') {
        request.reject(new Error('No drive unit is connected.'));
      }
      return;
    }

    this._remoteDeviceTypesActiveRequest = request;
    this._sendRemoteDeviceTypesRequest(request);
  }

  _sendRemoteDeviceTypesRequest(request) {
    request.attemptId = (request.attemptId ?? 0) + 1;
    const attemptId = request.attemptId;
    request.awaitingResponse = true;
    request.handlePending = true;
    request.expectedSequence = null;
    request.bufferedResponses = [];
    request.retiredAfterTimeout = false;
    this._scheduleRemoteDeviceTypesTimer(REMOTE_DEVICE_TYPES_COMMAND_TIMEOUT_MS, request, 'command');

    let handleResult;
    try {
      handleResult = request.robot.getRemoteDeviceTypes();
    } catch (error) {
      request.awaitingResponse = false;
      request.handlePending = false;
      this._completeRemoteDeviceTypesRequest({ error });
      return;
    }

    const acceptHandle = (handle) => this._acceptRemoteDeviceTypesCommandHandle(request, attemptId, handle);
    const rejectHandle = (error) => this._rejectRemoteDeviceTypesCommandHandle(request, attemptId, error);
    if (handleResult && typeof handleResult.then === 'function') {
      handleResult.then(acceptHandle, rejectHandle);
    } else {
      acceptHandle(handleResult);
    }
  }

  _acceptRemoteDeviceTypesCommandHandle(request, attemptId, handle) {
    if (typeof handle === 'number' && handle < 0) {
      this._rejectRemoteDeviceTypesCommandHandle(request, attemptId, new Error(REMOTE_DEVICE_TYPES_REQUEST_ERROR));
      return;
    }

    const sequenceNumber = handle?.sequenceNumber;
    if (!isValidRemoteDeviceTypesSequenceNumber(sequenceNumber)) {
      if (
        request.robot === this.robot &&
        request.connectionGeneration === this._remoteDeviceTypesConnectionGeneration &&
        !this._robotDisconnectReleased
      ) {
        this._blockRemoteDeviceTypesForUnknownOwnership(request.robot);
      }
      return;
    }

    if (!this._isCurrentRemoteDeviceTypesRequest(request, attemptId)) {
      if (request.retiredAfterTimeout) {
        this._addUnresolvedRemoteDeviceTypesSequence(request, sequenceNumber);
      }
      return;
    }

    request.handlePending = false;
    if (this._remoteDeviceTypesUnresolvedSequences.includes(sequenceNumber)) {
      request.awaitingResponse = false;
      request.bufferedResponses = [];
      this._completeRemoteDeviceTypesAttemptWithError(request, new Error(REMOTE_DEVICE_TYPES_REQUEST_ERROR));
      return;
    }

    request.expectedSequence = sequenceNumber;
    const bufferedResponses = request.bufferedResponses;
    request.bufferedResponses = [];
    for (const response of bufferedResponses) {
      if (!this._isCurrentRemoteDeviceTypesRequest(request, attemptId)) {
        break;
      }
      if (response.sequenceNumber === sequenceNumber) {
        this._processOwnedRemoteDeviceTypesResponse(request, response);
      }
    }
  }

  _rejectRemoteDeviceTypesCommandHandle(request, attemptId, error) {
    if (!this._isCurrentRemoteDeviceTypesRequest(request, attemptId)) {
      return;
    }

    request.awaitingResponse = false;
    request.handlePending = false;
    this._completeRemoteDeviceTypesAttemptWithError(request, error instanceof Error ? error : new Error(String(error)));
  }

  _scheduleRemoteDeviceTypesTimer(delayMs, request, timerKind) {
    if (!request?.robot || this.robot !== request.robot) {
      return;
    }

    if (this._remoteDeviceTypesTimer || !this.robot) {
      return;
    }

    const attemptId = request.attemptId;
    this._remoteDeviceTypesTimer = setTimeout(() => {
      this._remoteDeviceTypesTimer = null;

      if (!this._isCurrentRemoteDeviceTypesRequest(request, attemptId)) {
        return;
      }

      if (timerKind === 'retry') {
        this._sendRemoteDeviceTypesRequest(request);
        return;
      }

      if (request.handlePending) {
        this._blockRemoteDeviceTypesForUnknownOwnership(request.robot);
        return;
      }

      request.awaitingResponse = false;
      request.retiredAfterTimeout = true;
      if (isValidRemoteDeviceTypesSequenceNumber(request.expectedSequence)) {
        this._addUnresolvedRemoteDeviceTypesSequence(request, request.expectedSequence);
      }

      const error = request.kind === 'one-shot' ? new Error(REMOTE_DEVICE_TYPES_REQUEST_ERROR) : undefined;
      const resumeBackground =
        request.kind === 'background' &&
        this._remoteDeviceTypesOneShotQueue.length > 0 &&
        this.store.getState().robotData?.remoteDeviceTypesReady !== true;
      this._completeRemoteDeviceTypesRequest({ error, resumeBackground });
      if (request.kind === 'background') {
        this.dispatch(this.robotDataActions.onCompleteRemoteDeviceTypesQuery());
      }
    }, delayMs);
  }

  _invalidateFirmwareMetadataRequests() {
    this._firmwareMetadataRequestToken += 1;
    this._getRobotFirmwareVersionsPromise = null;
    this._getRobotFirmwareVersionsPromiseToken = null;
    this._lastGetRobotFirmwareVersionsTime = null;
  }

  _releaseRobotForDisconnect(context) {
    if (!this.robot || this._robotDisconnectReleased) {
      return;
    }

    this._robotDisconnectReleased = true;

    try {
      this.robot.removeEventsListener(this);
    } catch (error) {
      _logMessage(`Failed to remove robot listeners during '${context}'`, error?.message || String(error));
    }

    try {
      this.robot.onDisconnect();
    } catch (error) {
      _logMessage(`Failed to run robot disconnect cleanup during '${context}'`, error?.message || String(error));
    }
  }

  _completeDisconnect(context) {
    btConnectionTimeout.stop();
    this._clearDisconnectCompletionTimeout();

    if (this.is('disconnected') || !this.can('didDisconnect')) {
      return;
    }

    _logMessage(`Completing disconnect via '${context}' from '${this.state}'`);
    this.didDisconnect();
  }

  _queuePendingConnection(params) {
    this._pendingConnectionRequest = {
      ...params,
      connectionType,
    };
    this._disconnectToReconnect = true;
  }

  _restartPendingConnection() {
    if (!this._disconnectToReconnect || !this._pendingConnectionRequest) {
      this._pendingConnectionRequest = null;
      this._disconnectToReconnect = false;
      return;
    }

    const pendingConnection = this._pendingConnectionRequest;
    this._pendingConnectionRequest = null;
    this._disconnectToReconnect = false;

    this._clearPendingConnectionTimer();
    this._pendingConnectionTimer = setTimeout(() => {
      this._pendingConnectionTimer = null;
      const { connectionType: pendingConnectionType, ...pendingRobot } = pendingConnection;
      this.dispatch(this.robotDataActions.onUpdateRobotType(pendingRobot));
      this.dispatch(
        this.robotCommandActions.onUpdateRobotStateAndConnectionType({
          robotState: RobotActions.STATE.CONNECT,
          connectionType: pendingConnectionType,
        })
      );
    }, 0);
  }

  // Live sensor: dispatch every accepted sample (no Redux throttle). Monotonic timeline
  // filtering happens in _recordLiveStreamSampleTime before this is called.
  _dispatchLiveSensorData(runData) {
    if (!runData) return;
    this.dispatch(this.robotCommandActions.onUpdateLiveSensorData(runData));
  }

  _dispatchPythonPrintLines(lines) {
    if (!Array.isArray(lines) || lines.length === 0) return;
    this._pendingPythonPrintLines.push(...lines);

    if (this._pythonPrintFlushTimer) return;

    this._pythonPrintFlushTimer = setTimeout(() => {
      this._pythonPrintFlushTimer = null;
      if (this._pendingPythonPrintLines.length === 0) return;
      const batch = this._pendingPythonPrintLines;
      this._pendingPythonPrintLines = [];
      this.dispatch(this.programActions.onAddConsoleDisplayBatch(batch));
    }, PYTHON_PRINT_CONSOLE_FLUSH_MS);
  }

  /**
   * Returns true if a robot is connected.
   * @returns {Boolean} is connected
   */
  isRobotConnected() {
    return InitializedStates.indexOf(this.state) !== -1 || FirmwareUpdateConnectedStates.indexOf(this.state) !== -1;
  }

  _hasConnectMetadataForProgramActions() {
    const robotData = this.store.getState().robotData;
    return robotData?.slotStorageReady === true && robotData?.remoteDeviceTypesQueryComplete === true;
  }

  _canAcceptProgramActionFromUi() {
    if (!this.robot || this._isCriticalFirmwareActionable()) {
      return false;
    }

    if (this._deferredConnectionValidationPending) {
      return false;
    }

    const canAcceptInCurrentState = this.is('ready') || this._isBackgroundFirmwareTransferActive();
    if (!canAcceptInCurrentState) {
      return false;
    }

    return this._hasConnectMetadataForProgramActions();
  }

  canAcceptProgramActionFromUi() {
    return this._canAcceptProgramActionFromUi();
  }

  canPauseBackgroundFirmwareForProgramAction() {
    return this._isBackgroundFirmwareTransferActive() && Boolean(this.robot && this.sdk?.toybox?.pauseAssetLoad);
  }

  _syncProgramActionsReady() {
    const nextReady = this._canAcceptProgramActionFromUi();
    if (this.store.getState().robot.programActionsReady !== nextReady) {
      this.dispatch(this.robotCommandActions.onUpdateProgramActionsReady(nextReady));
    }
  }

  _getCriticalFirmwareDialogReadinessFailure() {
    const isReady = (typeof this.is === 'function' && this.is('ready')) || this.state === 'ready';
    if (!this.robot || !isReady || this._deferredConnectionValidationPending) {
      return 'not-ready';
    }

    if (!this._isCriticalFirmwareManagerActionable()) {
      return 'not-actionable';
    }

    const currentDialog = this.store.getState().application?.openDialog?.dialog;
    if (currentDialog === ApplicationActions.Dialogs.CRITICAL_FIRMWARE_DIALOG.dialog) {
      return 'dialog-open';
    }

    return null;
  }

  _openDeferredCriticalFirmwareDialogIfReady() {
    if (!this._pendingDeferredCriticalFirmwareDialog || this._deferredObservedProgramCriticalDialogShown) {
      return false;
    }

    const readinessFailure = this._getCriticalFirmwareDialogReadinessFailure();
    if (readinessFailure === 'not-actionable') {
      this._pendingDeferredCriticalFirmwareDialog = false;
      return false;
    }
    if (readinessFailure) {
      return false;
    }

    this._pendingDeferredCriticalFirmwareDialog = false;
    this._deferredObservedProgramCriticalDialogShown = true;
    this.openCriticalFirmwareDialog();
    return true;
  }

  _openCriticalFirmwareDialogFromFreshMetadataIfReady() {
    if (this._criticalFirmwareUpdateFlow) {
      return false;
    }

    if (this._getCriticalFirmwareDialogReadinessFailure()) {
      return false;
    }

    this._pendingBackgroundFirmwareDownload = false;
    this.openCriticalFirmwareDialog();
    return true;
  }

  async _completeDeferredConnectionValidationIfNeeded() {
    if (!this._deferredConnectionValidationPending) {
      return true;
    }

    const requestRobot = this.robot;
    let firmwareVersions = null;
    try {
      firmwareVersions = await this.getRobotFirmwareVersions(true, true);

      if (this._isStaleDeferredConnectionValidation(requestRobot)) {
        return true;
      }

      let updaterStatePayload = null;
      try {
        updaterStatePayload = await this._getLibraryUpdaterState();
      } catch (error) {
        console.error('Error getting deferred library updater state after observed program stop:', error);
      }

      if (this._isStaleDeferredConnectionValidation(requestRobot)) {
        return true;
      }

      this._applyBestLibraryUpdaterState(updaterStatePayload, firmwareVersions);
      this._deferredConnectionValidationPending = false;
      if (
        this.robotFirmwareManager?.criticalUpdate?.() === true &&
        this.robotFirmwareManager?.hasActionableUpdate?.() === true &&
        !this._deferredObservedProgramCriticalDialogShown
      ) {
        this._pendingDeferredCriticalFirmwareDialog = true;
      }
      this._syncProgramActionsReady();
      this._openDeferredCriticalFirmwareDialogIfReady();
      this._startDeferredBackgroundFirmwareDownload();
      return true;
    } catch {
      if (!this._isStaleDeferredConnectionValidation(requestRobot)) {
        this.disconnect(_getErrorForCode(RobotErrorCode.FIRMWARE_VERSIONS_FAILED));
        return false;
      }
      return true;
    }
  }

  async _resumeDeferredConnectionMetadataBootstrapAfterObservedProgramEnds() {
    if (!this._deferConnectionMetadataBootstrapUntilObservedProgramEnds) {
      return;
    }

    if (this._deferredConnectionMetadataResumePromise) {
      return this._deferredConnectionMetadataResumePromise;
    }

    const resumePromise = (async () => {
      this._deferConnectionMetadataBootstrapUntilObservedProgramEnds = false;

      const validationCompleted = await this._completeDeferredConnectionValidationIfNeeded();
      if (!validationCompleted || !this.robot) {
        return;
      }

      this._deferSlotStorageQueryUntilObservedProgramEnds = false;
      this._runConnectionMetadataBootstrapIfReady({ skipFirmwareVersions: true });
    })().finally(() => {
      if (this._deferredConnectionMetadataResumePromise === resumePromise) {
        this._deferredConnectionMetadataResumePromise = null;
      }
    });

    this._deferredConnectionMetadataResumePromise = resumePromise;
    return resumePromise;
  }

  /** *************************************************** */
  /** *************** Program functions **************** */
  /** *************************************************** */

  /**
   * Resets the worker running on the connected this.robot.
   * @param err {?Object} an optional error object
   */
  stopProgram(err) {
    if (this.can('doStopProgram')) {
      if (err) {
        this.dispatch(
          this.programActions.onUpdateProgramState({
            programState: RobotActions.PROGRAM.ERROR,
            error: _getError(err.message),
          })
        );
      }
      this.doStopProgram();
    }
  }

  isRunningProgram() {
    return PROGRAM_EXECUTING_STATES.some((state) => this.is(state)) || this.is('programExecuting') || this.is(FIRMWARE_PROGRAM_STATES.EXECUTING);
  }

  /**
   * Deploys a program to the connected robot.
   * @param {String} program code represented as a string
   * @param programId
   */
  async deployProgram(program, targetSlotNumber) {
    if (!this.isRobotConnected()) {
      this.dispatch(
        this.programActions.onUpdateProgramState({
          programState: RobotActions.PROGRAM.SEND_FAILED,
          error: RobotErrorCode.NO_ROBOT,
        })
      );
      return;
    }

    this._resetLiveSensorStreamState();
    this._clearPythonPrintQueue();
    this.pythonPrintBuffer = '';
    this.dispatch(this.robotCommandActions.onUpdateLiveSensorData(null));
    this.dispatch(
      this.programActions.onUpdateProgramState({
        programState: RobotActions.PROGRAM.SEND_STARTING,
      })
    );

    try {
      const state = this.store.getState();
      const loadedProject = state.project.loadedProject;
      const projectId = loadedProject?.id;
      const projectName = loadedProject?.name;
      const frameworkVersion = loadedProject?.frameworkVersion;
      const projectLastModifiedUtcMs = parseProjectLastModifiedToUtcMs(loadedProject?.lastModified) ?? Date.now();
      const projectVersion = projectVersionToString(loadedProject?.version ?? CURRENT_PROJECT_VERSION);

      // Determine which save slot to use (1-8) from the current project.
      const selectedSlotNumber = getValidSaveSlotNumber(
        targetSlotNumber ?? state.project.activeProgramActionSlotNumber ?? state.project.sessionSaveSlot
      );
      if (selectedSlotNumber === null) {
        throw _getError('A save slot must be selected before deploying.', RobotErrorCode.CANCELLED);
      }
      const slotName = `slot${selectedSlotNumber}`; // slot1-slot8 on the device

      // Preserve the project name in the on-device Python filename.
      const displayProjectName = projectName || 'Untitled Program';
      const filename = projectNameToSlotDeviceFilename(displayProjectName);

      this.currentSlot = slotName;
      this.currentFilename = filename;

      const deployMetadata = {
        projectId,
        projectName,
        projectLastModifiedUtcMs,
        projectVersion,
        frameworkVersion,
        dashboardLayout: loadedProject?.dashboardLayout ?? [],
        paths: loadedProject?.paths ?? [],
        selectedPathId: loadedProject?.selectedPathId,
        selectedField: loadedProject?.selectedField,
        pathPreviewMode: loadedProject?.pathPreviewMode,
        pathBuilderFieldRotation: loadedProject?.pathBuilderFieldRotation,
      };
      const { python: deployedProgram, pathsOmitted } = appendBpsProjectMetadataToDeployedPythonWithReport(program, deployMetadata);
      await this.loadPythonProgram(deployedProgram, slotName, filename);
      this._sessionDeployedProgramCodeBySlot[slotName] = { code: deployedProgram, filename };

      // Only warn about omitted path metadata once the program has actually landed on the device, so a failed
      // deploy doesn't surface a spurious "path data omitted" toast alongside the deploy-failed error.
      if (pathsOmitted) {
        setPathMetadataOmittedWarning(
          i18next.t('pathMetadata.deploy.omittedWarning', {
            defaultValue: 'Path data was too large to save to the robot, so it was not included in this deploy.',
          })
        );
      }

      // Optimistically update local slot storage with the project name; will be refined by metadata.
      this.dispatch(
        this.robotDataActions.onUpdateSlotStorage({
          slotNumber: selectedSlotNumber,
          programTitle: displayProjectName,
          isEmpty: false,
          bpsProjectId: projectId || undefined,
          bpsProjectLastModifiedUtcMs: projectLastModifiedUtcMs,
          deviceFilename: filename,
        })
      );

      this.dispatch(
        this.programActions.onUpdateProgramState({
          programState: RobotActions.PROGRAM.SEND_SUCCEEDED,
        })
      );
    } catch (error) {
      if (error instanceof BpsDashboardDeployMetadataError) {
        this.dispatch(
          this.programActions.onAddConsoleDisplay(
            i18next.t('console-log.dashboard-deploy-metadata-too-large', {
              defaultValue: 'Deploy cancelled: dashboard layout is too large to send to the robot.',
            })
          )
        );
      } else if (error instanceof SlotDeviceFilenameValidationError) {
        this.dispatch(
          this.programActions.onAddConsoleDisplay(
            i18next.t('console-log.program-send-invalid-filename', {
              defaultValue:
                'Rename this project before deploying. Drive Unit filenames must be at most {{maxBytes}} UTF-8 bytes including .py and cannot contain /, \\, or a null character. The name empty.py is reserved.',
              maxBytes: MAX_SLOT_DEVICE_FILENAME_UTF8_BYTES,
            })
          )
        );
      }
      this.dispatch(
        this.programActions.onUpdateProgramState({
          programState: RobotActions.PROGRAM.SEND_FAILED,
          error: _normalizeErrorForDispatch(error),
        })
      );
      throw error;
    }
  }

  /**
   * Resolve the selected slot to a concrete DU filename for RUN actions.
   * RUN should target the program already stored on the drive unit, even if this session
   * has not deployed yet, so we derive `currentSlot/currentFilename` from slot metadata.
   */
  _resolveSelectedRunTarget({ applyResolvedTarget = true, targetSlotNumber } = {}) {
    const state = this.store.getState();
    const selectedSlotNumber = getValidSaveSlotNumber(
      targetSlotNumber ?? state.project?.activeProgramActionSlotNumber ?? state.project?.sessionSaveSlot
    );
    if (selectedSlotNumber === null) {
      return { error: _getError('A save slot must be selected before running.', RobotErrorCode.CANCELLED) };
    }

    const slotName = `slot${selectedSlotNumber}`;
    const slotInfo = state.robotData?.slotStorage?.[slotName];
    const filename = normalizeSlotFilename(slotInfo, slotName);
    const canUseOptimisticLocalDeployTarget =
      state.robotData?.slotStorageFresh !== true &&
      slotInfo?.isEmpty === false &&
      this.currentSlot === slotName &&
      typeof this.currentFilename === 'string' &&
      this.currentFilename.length > 0 &&
      filename === this.currentFilename;

    if (state.robotData?.slotStorageFresh !== true && !canUseOptimisticLocalDeployTarget) {
      return {
        error: _getError(
          'The selected slot could not be verified on this connection. Download the program again before running.',
          RobotErrorCode.CANCELLED
        ),
      };
    }

    if (slotInfo?.isEmpty !== false) {
      return { error: _getError('The selected slot does not contain a program to run.', RobotErrorCode.CANCELLED) };
    }

    if (applyResolvedTarget) {
      this.currentSlot = slotName;
      this.currentFilename = filename;
    }

    return { slotName, filename };
  }

  getSelectedRunTargetValidationError() {
    const resolvedRunTarget = this._resolveSelectedRunTarget({ applyResolvedTarget: false });
    return resolvedRunTarget?.error ? _normalizeErrorForDispatch(resolvedRunTarget.error) : null;
  }

  /**
   * Compiles and deploys a program to the connected this.robot.
   * @param {String} program code represented as a string
   * @param programId
   */
  runProgram(program, targetSlotNumber) {
    if (this._isCriticalFirmwareActionable()) {
      this._dispatchProgramBlockedByCriticalFirmware();
      return false;
    }

    const resolvedRunTarget = this._resolveSelectedRunTarget({ targetSlotNumber });
    if (resolvedRunTarget?.error) {
      this.dispatch(
        this.programActions.onUpdateProgramState({
          programState: RobotActions.PROGRAM.SEND_FAILED,
          error: _normalizeErrorForDispatch(resolvedRunTarget.error),
        })
      );
      return false;
    }

    this.robot.onProgramLoad();
    // Clear chart buffers before new run so old data doesn't flash or create diagonal artifacts
    this._clearPythonStreamDefinitionsForNewRun();
    this._resetLiveSensorStreamState();
    this._clearPythonPrintQueue();
    this.pythonPrintBuffer = '';
    this.dispatch(this.robotCommandActions.onUpdateLiveSensorData(null));
    this._runningProgramNotificationContextRequestToken += 1;
    this.dispatch(
      this.programActions.onUpdateProgramState({
        programState: RobotActions.PROGRAM.STARTING,
      })
    );
    if (this.isRobotConnected()) {
      if (this.can('doStartProgram')) {
        this.startTime = performance.now();
        const programId = this.store.getState().project.loadedProject?.id ?? 'unknown';
        createSensorRun(programId);
        this._locallyStartedProgramThisConnection = true;
        this._awaitingLocalRunOverseerExecution = true;
        this._localRunStartedAt = performance.now();
        this._beginDrivingDisabledWarningRun();
        void this._requestDrivingDisabledWarningForProgramStart();
        this.doStartProgram(program);
        return true;
      } else {
        const error = _getError('A program is already executing', RobotErrorCode.CANCELLED);
        this.dispatch(
          this.programActions.onUpdateProgramState({
            programState: RobotActions.PROGRAM.SEND_FAILED,
            error,
          })
        );
        return false;
      }
    } else {
      this.dispatch(
        this.programActions.onUpdateProgramState({
          programState: RobotActions.PROGRAM.SEND_FAILED,
          error: _normalizeErrorForDispatch(RobotErrorCode.NO_ROBOT),
        })
      );
      return false;
    }
  }

  async _terminateProgram(options = {}) {
    const { suppressStateUpdate = false } = options;

    if (this.can('didStopProgram')) {
      this.didStopProgram();
    }
    this._clearPythonStreamDefinitionsForNewRun();
    this._resetLiveSensorStreamState({ resetTimeline: false });
    this.dispatch(this.robotCommandActions.onUpdateLiveSensorData(null));
    // Save run to DB before dispatching STOP so Dashboard historical load has the latest run
    const rawLayout = this.store.getState().project.loadedProject?.dashboardLayout ?? [];
    const driveUnitPorts = this.store.getState().application.driveUnitPorts;
    const projectCode = this.store.getState().project.loadedProject?.program?.code ?? '';
    const dashboardLayout = enrichDashboardLayoutVariableNamesForRunSnapshot(rawLayout, driveUnitPorts, projectCode);
    setActiveRunDashboardLayout(dashboardLayout);
    await saveSensorRun();
    // Yield to let Dexie useLiveQuery propagate the new run before Dashboard loads historical
    await new Promise((r) => setTimeout(r, 0));
    if (!suppressStateUpdate && this.programState !== RobotActions.PROGRAM.STOP && this.programState !== RobotActions.PROGRAM.STOPPED) {
      this._runningProgramNotificationContextRequestToken += 1;
      this.dispatch(
        this.programActions.onUpdateProgramState({
          programState: RobotActions.PROGRAM.STOP,
        })
      );
    }
  }

  /** *************************************************** */
  /** *************** TOY EVENT HANDLERS **************** */
  /** *************************************************** */

  /**
   * Handle what happens every time the SDK finds a robot
   *
   * @param {object} robot - the robot discovered by the SDK
   */
  _onToyFoundHandler(_robot) {
    const robotInfo = _robot.discoveryInfo;
    if (robotInfo.rssi && robotInfo.rssi >= MINIMUM_RSSI && robotInfo.rssi < MAXIMUM_RSSI) {
      this.dispatch(
        this.robotCommandActions.onUpdateDiscovery({
          discoveryState: RobotActions.DISCOVERY.DISCOVERED,
          robotInfo,
        })
      );
    }
  }

  _onToyReadyHandler(result) {
    _logMessage('Toy Ready');
    btConnectionTimeout.stop();
    if (this.can('connected')) {
      this._invalidateFirmwareMetadataRequests();
      this.robot = RobotInterface(result.toy, this.robotType.type, this.store);
      this._robotDisconnectReleased = false;
      this.robotFirmwareManager = new RobotFirmwareManager(this.robotType.type, this.store);
      this.robot.addEventsListener(this);
      this.connected();
    } else {
      this.sdk.toybox.putAwayToy(result.toy);
    }
  }

  /**
   * Stops robot discovery and disconnects the robot.
   * @param {Object} err optional error
   */
  disconnect(err) {
    this._invalidateFirmwareMetadataRequests();
    this._drivingDisabledWarningRequestToken += 1;
    this._retireSystemStateRequestsForRobot(this.robot);
    if (err) {
      _logMessage('Disconnect with error:', err?.message || String(err));
      this.disconnectError = err;

      // Show firmware-related dialogs when disconnecting with a firmware error
      if (err.code === RobotErrorCode.UNSUPPORTED_FIRMWARE || err.code === RobotErrorCode.V2_FIRMWARE_OUTDATED) {
        if (this.firmwareProgress !== null) {
          _logMessage('Firmware update still in progress, reboot has not finished!');
          const { dialog } = ApplicationActions.Dialogs.FIRMWARE_UPDATE_REBOOTING;
          this.dispatch(this.mainAppActions.onOpenDialog({ dialog }));
        } else {
          const { dialog, options } = ApplicationActions.Dialogs.V2_FIRMWARE_OUTDATED;
          const robotTypeName = this.robot?.robotType?.name || this.robotType?.name;
          const firmwareDialog = { dialog, options: { ...options, robotTypeName } };
          this.dispatch(this.mainAppActions.onOpenDialog(firmwareDialog));
        }
      }
    }
    this.stopMonitoringBatteryLevel();
    this.stopMonitoringSystemState();
    this._cancelBprLogOperations(new BprLogOperationCancelledError('Drive Unit disconnected.'));
    this._lastElectronicsPortTypes = null;
    this._lastElectronicsPortConnectedMask = null;
    this._cancelRemoteDeviceTypesRequests(new Error('Drive unit disconnected.'));
    this.dispatch(this.robotDataActions.onClearRobotInfo());
    if (!this.robot) {
      // An error-free explicit disconnect (e.g. user clicks cancel/disconnect
      // before the robot is found) should return to DISCONNECTED cleanly, not
      // show a "connection failed" banner.  Mark it as user-initiated so
      // shouldReportConnectFailure returns false in onEnterDisconnected.
      // NOTE: this relies on all genuine failure paths passing an `err` object
      // to disconnect().  If a future caller omits the error during a real
      // failure, the CONNECT_FAILED banner will be incorrectly suppressed.
      if (!err && this.isInNotInitializedState()) {
        this.userCancelledConnection = true;
      }
      this._completeDisconnect('disconnect without robot');
      return;
    }

    this._scheduleDisconnectCompletion('putAwayToy');
    this._releaseRobotForDisconnect('immediate disconnect');
    const robot = this.robot;
    new Promise((resolve) => setTimeout(resolve, 500)).then(() => {
      try {
        this.sdk.toybox.putAwayToy(robot.sdkToy());
        _logMessage('putAwayToy succeeded during disconnect');
        this._completeDisconnect('putAwayToy success');
      } catch (error) {
        _logMessage('putAwayToy failed during disconnect', error?.message || String(error));
        this._completeDisconnect('putAwayToy failure');
      }
    });
  }

  _onToyCorruptHandler(result) {
    if (!this.is('connecting')) return;
    btConnectionTimeout.stop();
    this.robot = RobotInterface(result.toy, this.robotType, this.store);
    this._robotDisconnectReleased = false;
    this.robot.addEventsListener(this);
    this.robotFirmwareManager = new RobotFirmwareManager(this.robotType.type, this.store);
    if (!this.robotFirmwareManager.canUpdate()) {
      this.disconnect(_getErrorForCode(RobotErrorCode.V2_FIRMWARE_OUTDATED, true));
    } else {
      console.log('Firmware is corrupt but can be updated');
      this.updateFound();
    }
  }

  _onReadyToyFailedHandler(name) {
    if (this.is('connecting')) {
      if (connectionType === RobotActions.STATE.CONNECTION_TYPE.SERIAL) {
        _logMessage(`Ready toy failed (serial), will not attempt reconnect. '${name}'`);
        btConnectionTimeout.stop();
        this._completeDisconnect('ready toy failed serial');
      } else {
        if (numberReconnectAttempts < MAX_RECONNECTS) {
          numberReconnectAttempts += 1;
          _logMessage(`Ready toy failed, reconnecting attempt '${numberReconnectAttempts}' '${name}'`);
          this.sdk.toybox.reconnectToy(name);
        } else {
          _logMessage(`Ready toy failed, max reconnection attempts exhausted, disconnecting. '${name}'`);
          btConnectionTimeout.stop();
          this._completeDisconnect('ready toy failed max retries');
        }
      }
    } else if (this.can('didDisconnect')) {
      btConnectionTimeout.stop();
      this._completeDisconnect('ready toy failed outside connecting');
    }
  }

  _onActivationDataHandler(options) {
    setTimeout(async () => {
      try {
        const { httpFetch } = await import('../http');
        const response = await httpFetch(options.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(options.data),
          responseType: 'text',
        });
        const text = await response.text();
        _logMessage(`Activation complete: ${text}`);
        options.isValidStatusCodeCallback();
      } catch (error) {
        _logMessage('Activation Error', error);
      }
    }, 5000);
  }

  /**
   * Handle what happens when a user cancels the web bluetooth connection dialog
   *
   * @param {object} err - the message returned by the cancelled request
   */
  _onUserCancelledHandler() {
    _logMessage('User Cancelled');
    btConnectionTimeout.stop();
    this.sdk.toybox.resetToy(this.robotPrefix);
    this.userCancelledConnection = true;
    this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.USER_CANCELLED));
  }

  _onOperationFailedHandler(err) {
    _logMessage('SDK Operation Failed', err);
    this._onConnectionFailedHandler();
  }

  _onConnectionFailedHandler(err) {
    _logMessage('Connection Failed', err);

    // Check if this is a serial connection and we're still in connecting state
    if (connectionType === RobotActions.STATE.CONNECTION_TYPE.SERIAL && this.is('connecting')) {
      // Treat as user cancellation for serial connections when cancelled early
      this._onUserCancelledHandler();
      return;
    }

    btConnectionTimeout.stop();
    this.sdk.toybox.resetToy(this.robotPrefix);
    if (this.can('didDisconnect')) {
      this.connectionFailedInCharger = false;
      this._completeDisconnect('connection failed');
    }
  }

  _onReadyToyFailedInChargerHandler(name) {
    _logMessage(`Ready Toy Failed In Charger '${name}'`);
    btConnectionTimeout.stop();
    this.sdk.toybox.resetToy(this.robotPrefix);
    if (this.can('didDisconnect')) {
      this.connectionFailedInCharger = true;
      this._completeDisconnect('connection failed in charger');
    }
  }

  _onDisconnectedHandler() {
    this._clearBackgroundFirmwarePauseState();
    _logMessage('Toy put away');
    this._completeDisconnect('toy put away');
  }

  _onFirmwareProgressHandler(progress) {
    this.dispatch(this.robotCommandActions.onUpdateFirmwareProgress(progress));
  }

  _onBtConnectionTimeout(command) {
    return this.disconnect(this._getError(`Command timed out! ${command}`));
  }

  isInFirmareUpdateState(state = this.state) {
    return FirmwareUpdateStates.indexOf(state) !== -1;
  }

  isInNotInitializedState(state = this.state) {
    return NotInitializedStates.indexOf(state) !== -1;
  }

  // For now let's not query the asset config when we are loading firmware, programs,
  // or even running programs.  Seems to cause errors.
  isInQueryAssetState(state = this.state) {
    return state === 'ready';
  }

  _isCriticalFirmwareManagerActionable() {
    if (this.robotFirmwareManager?.criticalUpdate?.() !== true) {
      return false;
    }

    if (typeof this.robotFirmwareManager?.hasActionableUpdate === 'function') {
      return this.robotFirmwareManager.hasActionableUpdate() === true;
    }

    return true;
  }

  _isCriticalFirmwareActionable() {
    return this._criticalFirmwareUpdateFlow || this._isCriticalFirmwareManagerActionable();
  }

  _isSerialConnection() {
    return this.store.getState().robot.connectionType === RobotActions.STATE.CONNECTION_TYPE.SERIAL;
  }

  _isSdkToyConnected(sdkToy = this.robot?.sdkToy?.()) {
    if (!sdkToy || typeof sdkToy.getState !== 'function') {
      return Boolean(sdkToy);
    }

    return sdkToy.getState() === SDK_TOY_STATE_CONNECTED;
  }

  _isBackgroundFirmwareTransferActive(state = this.state) {
    if (this._criticalFirmwareUpdateFlow) {
      return false;
    }

    if ([FIRMWARE_PROGRAM_STATES.STARTING, FIRMWARE_PROGRAM_STATES.EXECUTING, FIRMWARE_PROGRAM_STATES.STOPPING].includes(state)) {
      return true;
    }

    // BLE waits before loadBprFirmware() runs while still in updatingFirmware; that is not yet a pausable transfer.
    if (state === 'updatingFirmware') {
      return this._bprFirmwareLoadTimer == null;
    }

    return false;
  }

  _isProgramBusyForBackgroundTransfer() {
    if (this._isProgramFsmActive()) {
      return true;
    }

    if (this._deployInProgress) {
      return true;
    }

    if (this._runStartInProgress) {
      return true;
    }

    const programState = this.store.getState().project.programState;
    return (
      programState === RobotActions.PROGRAM.RUN || programState === RobotActions.PROGRAM.STARTING || programState === RobotActions.PROGRAM.STARTED
    );
  }

  _dispatchProgramSendFailed(error) {
    this.dispatch(
      this.programActions.onUpdateProgramState({
        programState: RobotActions.PROGRAM.SEND_FAILED,
        error: _normalizeErrorForDispatch(error),
      })
    );
  }

  _dispatchProgramBlockedByCriticalFirmware() {
    this._dispatchProgramSendFailed(_getError('A critical firmware update must be completed before running programs.', RobotErrorCode.CANCELLED));
  }

  _dispatchProgramBlockedByBackgroundFirmwareTransfer(error) {
    if (error) {
      _logMessage(`Failed to pause background firmware transfer for program action: ${error?.message || String(error)}`);
    }

    this._dispatchProgramSendFailed(_getError('Background firmware transfer could not be paused. Please try again.', RobotErrorCode.CANCELLED));
  }

  _isProgramActionStillCurrent(actionSequence, programState) {
    return this._programActionSequence === actionSequence && this.store.getState().project?.programState === programState;
  }

  _isProgramActionDeployResultStillCurrent(actionSequence) {
    // A successful deploy emits SEND_STARTING and SEND_SUCCEEDED before DOWNLOAD_RUN can start.
    return this._programActionSequence === actionSequence + 2 && this.store.getState().project?.programState === RobotActions.PROGRAM.SEND_SUCCEEDED;
  }

  _clearBackgroundFirmwarePauseState() {
    this._backgroundFirmwarePauseHoldCount = 0;
  }

  _hasBackgroundFirmwarePauseHold() {
    return this._backgroundFirmwarePauseHoldCount > 0;
  }

  _acquireBackgroundFirmwarePauseHold() {
    this._backgroundFirmwarePauseHoldCount += 1;
  }

  _releaseBackgroundFirmwarePauseHold() {
    if (this._backgroundFirmwarePauseHoldCount > 0) {
      this._backgroundFirmwarePauseHoldCount -= 1;
    }

    return this._backgroundFirmwarePauseHoldCount === 0;
  }

  async _ensureBackgroundFirmwarePausedForProgramAction() {
    if (!this._isBackgroundFirmwareTransferActive()) {
      return { canProceed: true, heldPause: false };
    }

    if (this._hasBackgroundFirmwarePauseHold()) {
      this._acquireBackgroundFirmwarePauseHold();
      return { canProceed: true, heldPause: true };
    }

    try {
      const didPause = await this._pauseBackgroundFirmwareTransferForProgram();
      if (didPause || !this._isBackgroundFirmwareTransferActive()) {
        return { canProceed: true, heldPause: didPause };
      }
    } catch (error) {
      if (!this._isBackgroundFirmwareTransferActive()) {
        return { canProceed: true, heldPause: false };
      }

      this._dispatchProgramBlockedByBackgroundFirmwareTransfer(error);
      return { canProceed: false, heldPause: false };
    }

    this._dispatchProgramBlockedByBackgroundFirmwareTransfer();
    return { canProceed: false, heldPause: false };
  }

  async _pauseBackgroundFirmwareTransferForProgram() {
    if (this._hasBackgroundFirmwarePauseHold() || !this._isBackgroundFirmwareTransferActive() || !this.sdk?.toybox?.pauseAssetLoad || !this.robot) {
      return false;
    }

    const result = await this.sdk.toybox.pauseAssetLoad(this.robot.sdkToy());
    const didPause = result?.handled === true;
    if (didPause) {
      this._acquireBackgroundFirmwarePauseHold();
    }
    return didPause;
  }

  async _resumeBackgroundFirmwareTransferAfterProgram() {
    if (!this._hasBackgroundFirmwarePauseHold()) {
      return false;
    }

    const shouldResume = this._releaseBackgroundFirmwarePauseHold();
    if (!shouldResume) {
      return false;
    }

    if (!this.sdk?.toybox?.resumeAssetLoad || !this.robot) {
      return false;
    }

    try {
      const result = await this.sdk.toybox.resumeAssetLoad(this.robot.sdkToy());
      return result?.handled === true;
    } catch (error) {
      _logMessage(`Failed to resume background firmware transfer: ${error?.message || String(error)}`);
      return false;
    }
  }

  async _cleanupProgramActionWithoutRunning() {
    await this._resumeBackgroundFirmwareTransferAfterProgram();
    this._startDeferredBackgroundFirmwareDownload();
    this._startDeferredCriticalFirmwareDownload();
    this._startDeferredFirmwareFinalization();
  }

  _startDeferredBackgroundFirmwareDownload() {
    if (!this._pendingBackgroundFirmwareDownload || this._isCriticalFirmwareActionable()) {
      return;
    }

    if (typeof this.robotFirmwareManager?.needsDownload === 'function' && this.robotFirmwareManager.needsDownload() !== true) {
      this._pendingBackgroundFirmwareDownload = false;
      this._syncProgramActionsReady();
      return;
    }

    if (this._isProgramBusyForBackgroundTransfer() || isDrivebaseRuntimeBusyForFirmwareActions(this._getObservedRuntimeProgramStateForSync())) {
      return;
    }

    if (!this.is('ready') || !this._hasConnectMetadataForProgramActions()) {
      return;
    }

    queueMicrotask(() => {
      if (!this._pendingBackgroundFirmwareDownload || this._isCriticalFirmwareActionable()) {
        this._syncProgramActionsReady();
        return;
      }

      if (typeof this.robotFirmwareManager?.needsDownload === 'function' && this.robotFirmwareManager.needsDownload() !== true) {
        this._pendingBackgroundFirmwareDownload = false;
        this._syncProgramActionsReady();
        return;
      }

      if (!this.is('ready') || !this._hasConnectMetadataForProgramActions()) {
        this._syncProgramActionsReady();
        return;
      }

      this._pendingBackgroundFirmwareDownload = false;
      this.forceFirmwareUpdate();
      this._syncProgramActionsReady();
    });
  }

  _startDeferredFirmwareFinalization() {
    if (!this._pendingFirmwareFinalization || isDrivebaseRuntimeBusyForFirmwareActions(this._getObservedRuntimeProgramStateForSync())) {
      return;
    }

    this._pendingFirmwareFinalization = false;
    this.finalizeUpdates();
  }

  _startDeferredCriticalFirmwareDownload() {
    if (!this._pendingCriticalFirmwareDownload || isDrivebaseRuntimeBusyForFirmwareActions(this._getObservedRuntimeProgramStateForSync())) {
      return;
    }

    this._pendingCriticalFirmwareDownload = false;
    this._criticalFirmwareUpdateFlow = this._isCriticalFirmwareManagerActionable();
    if (!this._criticalFirmwareUpdateFlow) {
      this._pendingBackgroundFirmwareDownload = true;
      this._startDeferredBackgroundFirmwareDownload();
      return;
    }

    if (!this._isSerialConnection()) {
      this.openCriticalFirmwareDialog();
      return;
    }

    if (this.is('firmwareUpdateCheck') && this.can('updateFound')) {
      this.updateFound();
    } else {
      this.forceFirmwareUpdate();
    }
  }

  /**
   * Sets up state machine
   */
  _initStateMachine() {
    const handleEnterProgramStarting = () => {
      console.log('StateMachine: handleEnterProgramStarting');
      // Use the last deployed slot/filename when starting a Python program on the drive unit.
      if (this.robot && typeof this.robot.onProgramStart === 'function') {
        this.robot.onProgramStart(this.currentSlot, this.currentFilename);
      }
      if (this.robot && this.robot.enablePythonStreaming) {
        // need to turn off streaming before starting program to register with FW streaming items added notification
        // once the notification is received and we can confirm the streaming data names/types, we can turn on streaming again
        this.robot.enablePythonStreaming(false);
      }
      // enable python print to console
      if (this.robot && this.robot.enablePythonPrintToConsole) {
        this.robot.enablePythonPrintToConsole(true);
      }
      if (this.can('startProgramSuccess')) {
        this.startProgramSuccess();
      }
    };

    const handleEnterProgramStopping = () => {
      console.log('StateMachine: handleEnterProgramStopping');
      if (this.robot && typeof this.robot.abortPythonExecution === 'function') {
        this.robot.abortPythonExecution();
      }
      setTimeout(async () => {
        // Terminate first: save run to DB, then dispatch STOP, so Dashboard has latest run before loading historical
        await this._terminateProgram();
        this._flushPendingPythonPrintOutput(true);
        if (this.robot && typeof this.robot.onProgramStop === 'function') {
          this.robot.onProgramStop();
        }
        if (this.robot && this.robot.enablePythonStreaming) {
          this.robot.enablePythonStreaming(false);
        }
        if (this.robot && this.robot.enablePythonPrintToConsole) {
          this.robot.enablePythonPrintToConsole(false);
        }
        await this._resumeBackgroundFirmwareTransferAfterProgram();
        this._startDeferredBackgroundFirmwareDownload();
        this._startDeferredCriticalFirmwareDownload();
        this._startDeferredFirmwareFinalization();
      });
    };

    const handleEnterProgramExecuting = () => {
      console.log('StateMachine: handleEnterProgramExecuting');
    };

    const handleLeaveProgramExecuting = (lifecycle) => {
      console.log('StateMachine: handleLeaveProgramExecuting', lifecycle?.transition);
      if (
        lifecycle?.transition === 'firmwareUpdateSucceeded' ||
        lifecycle?.transition === 'backgroundFirmwareUpdateStopped' ||
        lifecycle?.transition === 'handoffToObservedExecution'
      ) {
        return;
      }
      this.dispatch(this.mainAppActions.exitProgram(true));
    };

    FSM.apply(this, {
      init: 'disconnected',
      transitions: [
        {
          name: 'connect',
          from: ['disconnected'],
          to: 'connecting',
        },
        {
          name: 'connected',
          from: ['connecting'],
          to: 'validating',
        },
        { name: 'validated', from: 'validating', to: 'firmwareUpdateCheck' },
        {
          name: 'invalidated',
          from: ['validating', 'firmwareUpdateCheck'],
          to: 'notSupported',
        },
        {
          name: 'updateFound',
          from: ['firmwareUpdateCheck', 'connecting'],
          to: 'updatingFirmware',
        },
        {
          name: 'noUpdateFound',
          from: 'firmwareUpdateCheck',
          to: 'querying',
        },
        {
          name: 'firmwareUpdateFailed',
          from: ['updatingFirmware', FIRMWARE_PROGRAM_STATES.STARTING, FIRMWARE_PROGRAM_STATES.EXECUTING, FIRMWARE_PROGRAM_STATES.STOPPING],
          to: 'disconnected',
        },
        {
          name: 'firmwareUpdateSucceeded',
          from: ['updatingFirmware', FIRMWARE_PROGRAM_STATES.STARTING, FIRMWARE_PROGRAM_STATES.EXECUTING, FIRMWARE_PROGRAM_STATES.STOPPING],
          to: () => {
            if (this.state === FIRMWARE_PROGRAM_STATES.STARTING) return LOCAL_PROGRAM_STARTING_STATE;
            if (this.state === FIRMWARE_PROGRAM_STATES.EXECUTING) return LOCAL_PROGRAM_EXECUTING_STATE;
            if (this.state === FIRMWARE_PROGRAM_STATES.STOPPING) return PROGRAM_STOPPING_STATE;
            return 'ready';
          },
        },
        {
          name: 'backgroundFirmwareUpdateStopped',
          from: ['updatingFirmware', FIRMWARE_PROGRAM_STATES.STARTING, FIRMWARE_PROGRAM_STATES.EXECUTING, FIRMWARE_PROGRAM_STATES.STOPPING],
          to: () => {
            if (this.state === FIRMWARE_PROGRAM_STATES.STARTING) return LOCAL_PROGRAM_STARTING_STATE;
            if (this.state === FIRMWARE_PROGRAM_STATES.EXECUTING) return LOCAL_PROGRAM_EXECUTING_STATE;
            if (this.state === FIRMWARE_PROGRAM_STATES.STOPPING) return PROGRAM_STOPPING_STATE;
            return 'ready';
          },
        },
        {
          name: 'criticalFirmwareUpdateSucceeded',
          from: ['updatingFirmware'],
          to: 'finalizingUpdates',
        },
        { name: 'queried', from: ['querying'], to: 'ready' },
        {
          name: 'forcedFirmwareUpdate',
          from: ['connecting', 'ready'],
          to: 'updatingFirmware',
        },
        {
          name: 'startFinalizingUpdates',
          from: ['connecting', 'ready'],
          to: 'finalizingUpdates',
        },
        { name: 'finalizingUpdatesFailed', from: 'finalizingUpdates', to: 'disconnected' },
        { name: 'finalizingUpdatesSucceeded', from: 'finalizingUpdates', to: 'ready' },
        {
          name: 'doStartProgram',
          from: ['ready', 'updatingFirmware'],
          to: () => (this.state === 'updatingFirmware' ? FIRMWARE_PROGRAM_STATES.STARTING : LOCAL_PROGRAM_STARTING_STATE),
        },
        {
          name: 'startProgramSuccess',
          from: [LOCAL_PROGRAM_STARTING_STATE, FIRMWARE_PROGRAM_STATES.STARTING],
          to: () => (this.state === FIRMWARE_PROGRAM_STATES.STARTING ? FIRMWARE_PROGRAM_STATES.EXECUTING : LOCAL_PROGRAM_EXECUTING_STATE),
        },
        {
          name: 'syncProgramExecuting',
          from: ['ready'],
          to: OBSERVED_PROGRAM_EXECUTING_STATE,
        },
        {
          name: 'handoffToObservedExecution',
          from: [LOCAL_PROGRAM_EXECUTING_STATE],
          to: OBSERVED_PROGRAM_EXECUTING_STATE,
        },
        {
          name: 'startProgramFailed',
          from: [LOCAL_PROGRAM_STARTING_STATE, FIRMWARE_PROGRAM_STATES.STARTING],
          to: () => (this.state === FIRMWARE_PROGRAM_STATES.STARTING ? FIRMWARE_PROGRAM_STATES.STOPPING : PROGRAM_STOPPING_STATE),
        },
        {
          name: 'doStopProgram',
          from: [
            LOCAL_PROGRAM_STARTING_STATE,
            LOCAL_PROGRAM_EXECUTING_STATE,
            OBSERVED_PROGRAM_EXECUTING_STATE,
            FIRMWARE_PROGRAM_STATES.STARTING,
            FIRMWARE_PROGRAM_STATES.EXECUTING,
          ],
          to: () =>
            this.state === FIRMWARE_PROGRAM_STATES.STARTING || this.state === FIRMWARE_PROGRAM_STATES.EXECUTING
              ? FIRMWARE_PROGRAM_STATES.STOPPING
              : PROGRAM_STOPPING_STATE,
        },
        {
          name: 'didStopProgram',
          from: [PROGRAM_STOPPING_STATE, FIRMWARE_PROGRAM_STATES.STOPPING],
          to: () => (this.state === FIRMWARE_PROGRAM_STATES.STOPPING ? 'updatingFirmware' : 'ready'),
        },
        { name: 'didDisconnect', from: allStates, to: 'disconnected' },
      ],
      methods: {
        onEnterState: (lifecycle) => {
          _logMessage(`Entered '${lifecycle.to}' from '${lifecycle.from}' on transition '${lifecycle.transition}'`);
          this._syncProgramActionsReady();
          this._openDeferredCriticalFirmwareDialogIfReady();
        },
        onEnterConnecting: (lifecycle, robot) => {
          _logMessage(`Attempting to connect to robot ${robot.name}`);
          numberReconnectAttempts = 0;
          this.robotPrefix = robot.prefix;
          if (connectionType === RobotActions.STATE.CONNECTION_TYPE.SERIAL) {
            this.sdk.toybox.readyToy(robot.prefix, this.sdk.connectionTypes.Serial, SPHERO_VID, SPHERO_PID);
          } else {
            this.sdk.toybox.readyToy(robot.prefix, this.sdk.connectionTypes.BLE, {
              serviceUuid: BPR_BLE_SERVICE_UUID,
            });
          }
        },
        onLeaveConnecting: () => {
          console.log('StateMachine: onLeaveConnecting');
          btConnectionTimeout.stop();
          this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.CONNECTING));
        },
        onEnterValidating: () => {
          console.log('StateMachine: onEnterValidating');
          btConnectionTimeout.start(() => this.onBtConnectionTimeout('Get app versions'));
          this.getRobotValidationInformation();
        },
        onEnterFirmwareUpdateCheck: () => {
          console.log('StateMachine: onEnterFirmwareUpdateCheck');
          if (this.robotFirmwareManager?.criticalUpdate?.() === true && this.robotFirmwareManager?.hasActionableUpdate?.() === true) {
            if (this._deferredConnectionValidationPending) {
              this._pendingDeferredCriticalFirmwareDialog = true;
              queueMicrotask(() => {
                if (this.can('noUpdateFound')) {
                  this.noUpdateFound();
                }
              });
              return;
            }
            this._criticalFirmwareUpdateFlow = false;
            queueMicrotask(() => {
              if (this.is('firmwareUpdateCheck')) {
                this.openCriticalFirmwareDialog();
              }
            });
            return;
          }
          // Defer so we are not inside the validating → firmwareUpdateCheck transition
          // when noUpdateFound runs (FSM forbids nested transitions from onEnter handlers).
          queueMicrotask(() => {
            if (this.can('noUpdateFound')) {
              this.noUpdateFound();
            }
          });
        },
        onEnterQuerying: async () => {
          console.log('StateMachine: onEnterQuerying');
          btConnectionTimeout.stop();

          // Guard against the querying state stalling if the self-transition never
          // fires (e.g. due to a rapid disconnection in the 10 ms window).
          btConnectionTimeout.start(() => this.onBtConnectionTimeout('Querying battery state'));

          // Add a small delay to ensure any pending state transitions complete
          setTimeout(async () => {
            if (!this.is('querying')) {
              console.log('Not in querying state, current state:', this.state);
              return;
            }

            try {
              const updaterStatePayload = await this._getLibraryUpdaterState();
              if (!this.is('querying')) {
                return;
              }

              console.log('updaterStatePayload', updaterStatePayload);

              this._applyBestLibraryUpdaterState(updaterStatePayload, this.store.getState().robotData?.driveUnitFirmwareVersions);
            } catch (error) {
              if (this.is('querying')) {
                console.error('Error getting library updater state during querying:', error);
              }
            }

            if (!this.is('querying')) {
              return;
            }

            btConnectionTimeout.stop();

            try {
              this.queried();
            } catch (error) {
              console.error('Error calling queried():', error);
            }
          }, 10); // Small delay to allow state transition to complete
        },
        onEnterNotSupported: () => {
          console.log('StateMachine: onEnterNotSupported');
          this.disconnectError = _getErrorForCode(RobotErrorCode.UNSUPPORTED_FIRMWARE);
          this.disconnect(this.disconnectError);
        },
        onEnterUpdatingFirmware: async (lifecycle) => {
          console.log('StateMachine: onEnterUpdatingFirmware');
          if (lifecycle.transition === 'didStopProgram') {
            return;
          }

          this._clearBprFirmwareLoadTimer();
          this.dispatch(this.robotCommandActions.onUpdateProgramActionsReady(false));
          const startBprFirmwareLoad = () => {
            this._bprFirmwareLoadTimer = null;
            if (!this.is('updatingFirmware')) {
              return;
            }
            this.loadBprFirmware();
          };
          if (connectionType === RobotActions.STATE.CONNECTION_TYPE.BLE) {
            this._bprFirmwareLoadTimer = setTimeout(startBprFirmwareLoad, BPR_FIRMWARE_LOAD_DELAY_MS);
          } else {
            startBprFirmwareLoad();
          }
        },
        onLeaveUpdatingFirmware: (lifecycle) => {
          console.log('StateMachine: onLeaveUpdatingFirmware');
          this._clearBprFirmwareLoadTimer();
          if (lifecycle.transition === 'didDisconnect') {
            this.dispatch(
              this.robotCommandActions.onFirmwareStateChange({
                firmwareState: RobotActions.FIRMWARE_UPDATE.FAILED,
                errorCode: _getErrorForCode(RobotErrorCode.NO_ROBOT),
              })
            );
          }
        },
        onEnterFinalizingUpdates: () => {
          console.log('StateMachine: onEnterFinalizingUpdates');
          this.finalizeLibraryUpdate();
        },
        onLeaveFinalizingUpdates: (lifecycle) => {
          console.log('StateMachine: onLeaveFinalizingUpdates', lifecycle?.transition);
        },
        onEnterReady: (lifecycle) => {
          console.log('StateMachine: onEnterReady', lifecycle.transition);

          // We intentionally do not call queryRobotSlotStorage when we enter the ready state.
          // At this point we will not yet have the robots UID.
          // We will call queryRobotSlotStorage when we have the robots UID in the getUid response.

          if (lifecycle.transition === 'firmwareUpdateSucceeded') {
            // A non-critical/manual transfer returns directly to ready without reconnecting.
            // Refresh DU metadata after firmware settles, but do not re-run connect-time side effects.
            setTimeout(() => {
              if (!this.is('ready') || !this.robot) {
                return;
              }
              this.getRobotFirmwareVersions();
            }, 250);
            return;
          }

          // We should only run the connection logic once per connection.
          // This would be after queried or finalizingUpdatesSucceeded.
          // We should not run the connection logic if we are already in the ready state.
          // The firmwareUpdateSucceeded transition should not run the connection logic becasue that state
          // transition does not force the robot to disconnect and/or connect again.

          if (lifecycle.transition === 'queried' || lifecycle.transition === 'finalizingUpdatesSucceeded') {
            const connectRobot = () => {
              if (!this.robot) return;
              this.robot.onConnect();
              if (this.robot.getBluetoothAdvertisingName) {
                this.robot.getBluetoothAdvertisingName();
              }
              if (this._getEffectiveRobotType() === ROBOTS.BPRDU) {
                this._resetPythonStreamState();
                if (this.robot.enablePythonStreaming) {
                  this.robot.enablePythonStreaming(true);
                }
                if (this.robot.enablePythonPrintToConsole) {
                  this.robot.enablePythonPrintToConsole(true);
                }
              }
              // window.Editor.setConnectedRobot(this.robotType);
              this.beginMonitoringBatteryLevel();
              this.beginMonitoringSystemState();
              this._scheduleObservedExternalRunningProgramSyncFromKnownRuntime();
              this.dispatch(
                this.robotCommandActions.onConnect({
                  robotState: RobotActions.STATE.CONNECTED,
                  robotType: this.robotType,
                  robotName: this.robot.getName(),
                })
              );
              if (this._getEffectiveRobotType() === ROBOTS.BPRDU && this.robot?.hasCommand?.('getDrivebaseSystemState') === true) {
                this._pendingConnectionMetadataBootstrap = this._deferConnectionMetadataBootstrapUntilObservedProgramEnds !== true;
                this._connectionMetadataBootstrapped = false;
              } else {
                this._runConnectionMetadataBootstrapIfReady();
              }

              // This is the intentional teardown: when the robot actually reaches ready after
              // finalizingUpdatesSucceeded and this was the critical flow, dispatch onOpenDialog({ dialog: null })
              // so the user is not stuck behind a modal, then clear the flag.
              if (lifecycle.transition === 'finalizingUpdatesSucceeded' && this._criticalFirmwareUpdateFlow) {
                this.dispatch(this.mainAppActions.onOpenDialog({ dialog: null, options: {} }));
                this._criticalFirmwareUpdateFlow = false;
              }

              // If the initial connect-time updater check found optional download work,
              // stage it for later once validation and connection metadata (slot storage,
              // remote device types) are loaded. The transfer is background-only and must
              // not block deploy/run readiness while connect-time queries are still settling.
              if (lifecycle.transition === 'queried' && this.robotFirmwareManager?.canUpdate()) {
                queueMicrotask(() => {
                  if (!this.is('ready') || !this.robotFirmwareManager?.canUpdate() || this.robotFirmwareManager.needsDownload?.() !== true) {
                    return;
                  }
                  this._pendingBackgroundFirmwareDownload = true;
                  this._startDeferredBackgroundFirmwareDownload();
                });
              }
            };

            if (lifecycle.transition === 'finalizingUpdatesSucceeded') {
              // Defer 250ms to allow firmware to settle without blocking the FSM transition
              setTimeout(connectRobot, 250);
            } else {
              connectRobot();
            }
          }
        },
        onEnterProgramStartingLocal: handleEnterProgramStarting,
        onEnterUpdatingFirmwareProgramStarting: handleEnterProgramStarting,
        onEnterProgramStopping: handleEnterProgramStopping,
        onEnterUpdatingFirmwareProgramStopping: handleEnterProgramStopping,
        onEnterProgramExecutingLocal: handleEnterProgramExecuting,
        onEnterProgramExecutingObserved: handleEnterProgramExecuting,
        onEnterUpdatingFirmwareProgramExecuting: handleEnterProgramExecuting,
        onLeaveProgramExecutingLocal: handleLeaveProgramExecuting,
        onLeaveProgramExecutingObserved: handleLeaveProgramExecuting,
        onLeaveUpdatingFirmwareProgramExecuting: handleLeaveProgramExecuting,
        onEnterDisconnected: (lifecycle) => {
          console.log('StateMachine: onEnterDisconnected', lifecycle.transition);
          const err = this.disconnectError;
          const shouldShowConnectFailure = shouldReportConnectFailure({
            disconnectToReconnect: this._disconnectToReconnect,
            isFirmwareUpdateState: this.isInFirmareUpdateState(lifecycle.from),
            isNotInitializedState: this.isInNotInitializedState(lifecycle.from),
            userCancelledConnection: this.userCancelledConnection,
          });
          this._clearDisconnectCompletionTimeout();
          this._releaseRobotForDisconnect('entered disconnected');
          this._terminateProgram({ suppressStateUpdate: true });
          this._reset();
          this.dispatch(this.programActions.onOpenDrawer(null));
          if (lifecycle.transition !== 'init') {
            if (shouldShowConnectFailure) {
              this.dispatch(this.robotCommandActions.onDisconnect());
              if (this.connectionFailedInCharger) {
                this.connectionFailedInCharger = false;
                this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.CONNECT_FAILED_IN_CHARGER));
              } else if (err?.code === RobotErrorCode.V2_FIRMWARE_OUTDATED || err?.code === RobotErrorCode.UNSUPPORTED_FIRMWARE) {
                this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.V2_FIRMWARE_OUTDATED));
              } else {
                this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.CONNECT_FAILED));
              }
            } else {
              this.connectionFailedInCharger = false;
              this.userCancelledConnection = false;
              this.dispatch(this.robotCommandActions.onDisconnect());
            }
          }
          this._restartPendingConnection();
        },
      },
    });
  }

  async _initSdk() {
    // Import and instantiate the SDK
    const sdk = await SpheroSDK({
      sendActivationData: true,
    });

    console.info('SDK version: %s, SDK core version: %s', sdk.version, sdk.coreVersion);

    // Configure callbacks
    this.configSdkCallbacks(sdk);
    // TODO: once sdk is working, test if this line is needed.

    this.sdk = sdk;
  }

  // After a firmware update we need to update the callbacks
  configSdkCallbacks(sdk) {
    if (sdk) {
      sdk.on(sdk.EVENT.ERROR, _onErrorHandler);
      sdk.on(sdk.EVENT.OPERATION_FAILED, this.onOperationFailedHandler);
      sdk.on(sdk.EVENT.CONNECTION_FAILED, this.onConnectionFailedHandler);
      sdk.on(sdk.EVENT.USER_CANCELLED, this.onUserCancelledHandler);
      sdk.on(sdk.EVENT.ACTIVATION_DATA, this.onActivationDataHandler);
      sdk.toybox.on(sdk.toybox.EVENT.TOY_FOUND, this.onToyFoundHandler);
      sdk.toybox.on(sdk.toybox.EVENT.TOY_READY, this.onToyReadyHandler);
      sdk.toybox.on(sdk.toybox.EVENT.TOY_MAIN_APP_CORRUPT, this.onToyCorruptHandler);
      sdk.toybox.on(sdk.toybox.EVENT.READY_TOY_FAILED, this.onReadyToyFailedHandler);
      sdk.toybox.on(sdk.toybox.EVENT.READY_TOY_FAILED_CONNECTION_IN_CHARGER, this.onReadyToyFailedInChargerHandler);
      sdk.toybox.on(sdk.toybox.EVENT.TOY_PUT_AWAY, this.onDisconnectedHandler);
      sdk.firmware.on(sdk.firmware.EVENT.PROGRESS, this.onFirmwareProgressHandler);
    }
  }

  /**
   * Sets the connected robot's color to the user's saved value.
   * @param {Object} color (optional) object with {r, g, b, a}
   */
  setUserRobotColor(color) {
    if (color) {
      this.dispatch(
        this.robotDataActions.onUpdateUserRobotColor({
          r: color.r,
          g: color.g,
          b: color.b,
          a: color.a,
        })
      );
    }
    if (this.is('ready')) {
      const c = color;
      this.robot.setMainLED(c.r * c.a, c.g * c.a, c.b * c.a);
    }
  }

  /** *************************************************** */
  /** *************** Robot Commands **************** */
  /** *************************************************** */

  /**
   * Sets the connected robot's color.
   * @param {Number} r - red value, 0 - 255
   * @param {Number} g - green value, 0 - 255
   * @param {Number} b - blue value, 0 - 255
   */
  setRobotColor(r, g, b) {
    if (this.is('ready')) {
      this.robot.setMainLED(r, g, b);
    }
  }

  /**
   * Sets the front LED on supported robots
   * @param {Number} r - red value, 0 - 255
   * @param {Number} g - green value, 0 - 255
   * @param {Number} b - blue value, 0 - 255
   */
  setFrontLED(r, g, b) {
    if (this.robot) {
      this.robot.setFrontLED(r, g, b);
    }
  }

  /**
   * Sets the rear LED on supported robots
   * @param {Number} r - red value, 0 - 255
   * @param {Number} g - green value, 0 - 255
   * @param {Number} b - blue value, 0 - 255
   */
  setBackLED(r, g, b) {
    if (this.robot) {
      this.robot.setBackLED(r, g, b);
    }
  }

  /**
   * Sets the rear LED on supported robots
   * @param {Number} brightness, 0 - 255
   */
  setBackLEDBrightness(brightness) {
    if (this.robot) {
      this.robot.setBackLEDBrightness(brightness);
    }
  }

  setSingleLED(values) {
    if (this.robot) {
      if (values.led === 'MAIN') {
        this.robot.setMainLED(values.r, values.g, values.b);
      } else {
        this.robot.setSingleLED(values.led, values.r, values.g, values.b);
      }
    }
  }

  turnOffLights() {
    if (this.robot) {
      this.robot.turnOffLights();
    }
  }

  /**
   * Turns lights on a robot to the default state
   */
  turnOnLights() {
    if (this.robot) {
      const rgb = this.userRobotColor;
      this.robot.turnOnLights(rgb);
    }
  }

  pingRobot() {
    this.robot.ping();
  }

  _onAudioFinished() {}

  /**
   * Was isRobotSupoorted, but doesn't seem to actually check if robot is valid.
   * Returns true if robot connectivity is supported.
   * @return {Boolean} supported state
   */
  hasSDK() {
    return this.sdk !== null;
  }

  async ensureSdkReady() {
    if (this.sdk) {
      return true;
    }

    if (!this.sdkInitPromise) {
      return false;
    }

    const initialized = await this.sdkInitPromise;
    return initialized && this.sdk !== null;
  }

  _getBluetoothAdapterState(_robotType, callback) {
    const removeStateListeners = () => {
      this.sdk.off(this.sdk.EVENT.BLUETOOTH_NOT_SUPPORTED, onNotSupported);

      this.sdk.off(this.sdk.EVENT.BLUETOOTH_DISABLED, onDisabled);

      this.sdk.off(this.sdk.EVENT.BLUETOOTH_ENABLED, onEnabled);
    };

    const onEnabled = () => {
      removeStateListeners();
      callback();
    };
    const onNotSupported = () => {
      removeStateListeners();
      const errorCode = RobotErrorCode.NO_BLE || RobotErrorCode.NO_BLUETOOTH;
      callback(_getErrorForCode(errorCode, _robotType.name));
    };
    const onDisabled = () => {
      removeStateListeners();
      callback(_getErrorForCode(RobotErrorCode.BLUETOOTH_DISABLED, _robotType.name));
    };
    this.sdk.on(this.sdk.EVENT.BLUETOOTH_NOT_SUPPORTED, onNotSupported);
    this.sdk.on(this.sdk.EVENT.BLUETOOTH_DISABLED, onDisabled);
    this.sdk.on(this.sdk.EVENT.BLUETOOTH_ENABLED, onEnabled);

    const btType = this.sdk.connectionTypes.BLE;
    if (this.sdk.getBluetoothStateOverride) {
      // TEMP: EDU-11813 - Workaround for broken SDK call (EDU-11378).
      this.sdk.getBluetoothStateOverride(btType, (supported, enabled) => {
        if (!supported) onNotSupported();
        else if (!enabled) onDisabled();
        else onEnabled();
      });
    } else if (this.sdk.getBluetoothState) {
      this.sdk.getBluetoothState(btType);
    } else {
      onEnabled();
    }
  }

  /**
   * Starts robot discovery.
   * @param {Object} params to find
   */
  async startDiscovery(params) {
    if (!params || !params.type) {
      return;
    }

    const sdkReady = await this.ensureSdkReady();
    if (!sdkReady) {
      this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.CONNECT_FAILED));
      return;
    }

    if (!this.can('startScanning')) {
      return;
    }

    this._getBluetoothAdapterState(params.type, (error) => {
      if (error) {
        this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.CONNECT_FAILED));
      } else {
        this.startScanning(params.type);
      }
    });
  }

  /**
   * Starts robot  connection.
   * @param {Object} params to connect to
   */
  async startConnection(params) {
    if (!params || !params.type) {
      return;
    }

    if (shouldQueueReconnect({ canConnect: this.can('connect'), currentState: this.state })) {
      _logMessage(`Connect requested while in '${this.state}', disconnecting current session first`);
      this._queuePendingConnection(params);
      this.disconnect(null);
      return;
    }

    const sdkReady = await this.ensureSdkReady();
    if (!sdkReady) {
      this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.CONNECT_FAILED));
      return;
    }

    if (!this.can('connect')) {
      return;
    }

    this.dispatch(
      this.robotCommandActions.onUpdateDiscovery({
        discoveryState: RobotActions.DISCOVERY.START,
        robotInfo: { robotType: params.type },
      })
    );
    if (connectionType === RobotActions.STATE.CONNECTION_TYPE.BLE) {
      this._getBluetoothAdapterState(params.type, (error) => {
        if (error) {
          this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.CONNECT_FAILED));
        } else if (params.name) {
          this.connect(params);
        }
      });
    } else {
      this._getSerialState(params.type, (error) => {
        if (error) {
          this.dispatch(this.robotCommandActions.onUpdateRobotState(RobotActions.STATE.CONNECT_FAILED));
        } else if (params.name) {
          this.connect(params);
        }
      });
    }
  }

  _getSerialState(_robotType, callback) {
    const removeStateListeners = () => {
      this.sdk.off(this.sdk.EVENT.SERIAL_NOT_AVAILABLE, onNotAvailable);
      this.sdk.off(this.sdk.EVENT.SERIAL_AVAILABLE, onAvailable);
    };

    const onAvailable = () => {
      removeStateListeners();
      callback();
    };

    const onNotAvailable = () => {
      removeStateListeners();
      const errorCode = RobotErrorCode.NO_BLE || RobotErrorCode.NO_BLUETOOTH;
      callback(_getErrorForCode(errorCode, _robotType.name));
    };

    this.sdk.on(this.sdk.EVENT.SERIAL_NOT_AVAILABLE, onNotAvailable);
    this.sdk.on(this.sdk.EVENT.SERIAL_AVAILABLE, onAvailable);

    if (this.sdk.getSerialState) {
      this.sdk.getSerialState();
    } else {
      onAvailable();
    }
  }

  beginMonitoringBatteryLevel() {
    isMonitoringBatteryLevel = true;
    this.monitoringBatteryLevel();
  }

  beginMonitoringSystemState() {
    isMonitoringSystemState = true;
    this.monitoringSystemState();
  }

  _inferRemoteDeviceSystemInfoIndex(key, device) {
    if (Number.isInteger(device?.deviceIndex)) {
      return device.deviceIndex;
    }

    const normalizedKey = String(key ?? '').toUpperCase();
    const portMatch = /^PORT_(\d+)$/.exec(normalizedKey);
    if (portMatch) {
      const port = Number(portMatch[1]);
      if (Number.isInteger(port) && port >= 1 && port <= 8) {
        return REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START + port - 1;
      }
    }

    if (normalizedKey.includes('CONTROLLER')) {
      return REMOTE_DEVICE_SYSTEM_INFO_NON_PORT_INDEX_MAX;
    }

    if (normalizedKey.includes('NORDIC') || normalizedKey.includes('BLUETOOTH')) {
      return 0;
    }

    const swerveMatch = /SWERVE[_\s-]?([1-4])/.exec(normalizedKey);
    if (swerveMatch) {
      return Number(swerveMatch[1]);
    }

    return null;
  }

  _inferRemoteDeviceSystemInfoType(key, device) {
    if (typeof device?.deviceType === 'string' && device.deviceType !== '') {
      return device.deviceType;
    }

    if (Number.isInteger(device?.deviceIndex)) {
      if (device.deviceIndex === REMOTE_DEVICE_SYSTEM_INFO_NON_PORT_INDEX_MAX) {
        return 'controller';
      }
      if (device.deviceIndex === 0) {
        return 'nordic';
      }
      if (device.deviceIndex >= 1 && device.deviceIndex <= 4) {
        return 'swerve';
      }
    }

    const normalizedKey = String(key ?? '').toUpperCase();
    if (normalizedKey.includes('CONTROLLER')) {
      return 'controller';
    }
    if (normalizedKey.includes('NORDIC') || normalizedKey.includes('BLUETOOTH')) {
      return 'nordic';
    }
    if (/SWERVE[_\s-]?[1-4]/.test(normalizedKey)) {
      return 'swerve';
    }

    return null;
  }

  _applyRemoteDeviceSystemInfo(device, options = {}) {
    if (!device || !Number.isInteger(device.deviceIndex)) {
      return;
    }

    const { preserveMissingUpdateFields = false, preserveExistingPortInfo = false } = options;
    const normalizedDeviceType = this._inferRemoteDeviceSystemInfoType(null, device);
    const normalizedDevice = {
      ...device,
      ...(normalizedDeviceType !== device.deviceType ? { deviceType: normalizedDeviceType } : {}),
      ...(!preserveMissingUpdateFields && !Object.prototype.hasOwnProperty.call(device, 'updateInProgress') ? { updateInProgress: false } : {}),
      ...(!preserveMissingUpdateFields && !Object.prototype.hasOwnProperty.call(device, 'updateProgress') ? { updateProgress: 0 } : {}),
    };
    const { deviceType, isConnected, deviceIndex, updateInProgress, updateProgress } = normalizedDevice;
    const hasFirmwareInstallProgress =
      Object.prototype.hasOwnProperty.call(normalizedDevice, 'updateInProgress') ||
      Object.prototype.hasOwnProperty.call(normalizedDevice, 'updateProgress');
    if (isConnected && deviceType === 'controller') {
      this.dispatch(
        this.controllerDataActions.onUpdateControllerInfo({
          devicePartNumber: normalizedDevice.devicePartNumber,
          firmwareMainApp: normalizedDevice.firmwareMainApp,
          maMajor: normalizedDevice.maMajor,
          maMinor: normalizedDevice.maMinor,
          maRevision: normalizedDevice.maRevision,
          firmwareBootloader: normalizedDevice.firmwareBootloader,
          blMajor: normalizedDevice.blMajor,
          blMinor: normalizedDevice.blMinor,
          blRevision: normalizedDevice.blRevision,
          uid: normalizedDevice.uid,
          uidSize: normalizedDevice.uidSize,
          boardRevision: normalizedDevice.boardRevision,
        })
      );
    }

    if (hasFirmwareInstallProgress) {
      this.dispatch(
        this.robotDataActions.onUpdateFirmwareInstallProgress(
          this._normalizeFirmwareInstallProgress(deviceIndex, deviceType, isConnected, updateInProgress, updateProgress)
        )
      );
    }

    this.dispatch(this.robotDataActions.onUpdateRemoteDeviceSystemInfo(normalizedDevice));

    const portIndex = deviceIndex - REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START + 1;
    if (portIndex <= 0) {
      return;
    }

    const stateElectronics = this.store.getState().robotData?.electronics;
    const updatedElectronics = Array.isArray(stateElectronics) ? stateElectronics.slice() : [];
    const idx = portIndex - 1;
    while (updatedElectronics.length <= idx) {
      updatedElectronics.push({ electronicType: null, info: {} });
    }

    const previous = updatedElectronics[idx] || { electronicType: null, info: {} };
    const previousType = previous.electronicType ?? null;

    const {
      devicePartNumber,
      firmwareBootloader,
      firmwareMainApp,
      blMajor,
      blMinor,
      blRevision,
      maMajor,
      maMinor,
      maRevision,
      boardRevision,
      uid,
      uidSize,
    } = normalizedDevice;

    const formattedUid = formatDeviceUidHex(uid, uidSize);
    const formattedBoardRevision = formatBoardRevision(boardRevision);
    const resolvedFirmwareBootloader = firmwareBootloader ?? formatFirmwareVersionOrUndefined(blMajor, blMinor, blRevision);
    const resolvedFirmwareMainApp = firmwareMainApp ?? formatFirmwareVersionOrUndefined(maMajor, maMinor, maRevision);
    const canMergePreviousInfo =
      preserveExistingPortInfo && previousType != null && normalizedDevice.deviceType != null && normalizedDevice.deviceType === previousType;
    const previousInfo = canMergePreviousInfo && previous.info && typeof previous.info === 'object' ? { ...previous.info } : {};
    const info = { ...previousInfo };
    const hasOwn = (field) => Object.prototype.hasOwnProperty.call(device, field);
    const hasBootloaderSourceField = hasOwn('firmwareBootloader') || hasOwn('blMajor') || hasOwn('blMinor') || hasOwn('blRevision');
    const hasMainAppSourceField = hasOwn('firmwareMainApp') || hasOwn('maMajor') || hasOwn('maMinor') || hasOwn('maRevision');
    const applyInfoField = (fieldName, value, sourcePresent) => {
      if (preserveExistingPortInfo) {
        if (!sourcePresent) {
          return;
        }
        if (value == null) {
          delete info[fieldName];
          return;
        }
      } else if (value == null) {
        return;
      }
      info[fieldName] = value;
    };

    applyInfoField('devicePartNumber', devicePartNumber, hasOwn('devicePartNumber'));
    applyInfoField('firmwareBootloader', resolvedFirmwareBootloader, hasBootloaderSourceField);
    applyInfoField('firmwareMainApp', resolvedFirmwareMainApp, hasMainAppSourceField);
    applyInfoField('boardRevision', formattedBoardRevision, hasOwn('boardRevision'));
    applyInfoField('formattedUid', formattedUid, hasOwn('uid') || hasOwn('uidSize'));

    updatedElectronics[idx] = {
      electronicType: previousType,
      info,
    };

    this.dispatch(this.robotDataActions.onUpdateElectronics(updatedElectronics));
  }

  _hydrateRemoteDeviceSystemInfoFromFirmwareVersions(allVersions) {
    const byKey = allVersions?.remoteDeviceSystemInfo;
    if (byKey == null || typeof byKey !== 'object') {
      return;
    }

    const allowUntypedPortHydrationPorts = this._portUntypedFirmwareHydrationPorts;

    for (const [key, rawDevice] of Object.entries(byKey)) {
      if (rawDevice == null || typeof rawDevice !== 'object') {
        continue;
      }

      const deviceIndex = this._inferRemoteDeviceSystemInfoIndex(key, rawDevice);
      if (!Number.isInteger(deviceIndex)) {
        continue;
      }

      let incomingDeviceType = rawDevice.deviceType ?? this._inferRemoteDeviceSystemInfoType(key, rawDevice);
      const portIndex = deviceIndex - REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START + 1;
      if (portIndex >= 1 && portIndex <= 8) {
        const connectedMask = this._lastElectronicsPortConnectedMask;
        const portIsConnected = connectedMask == null ? null : ((connectedMask >>> (portIndex - 1)) & 1) === 1;
        if (portIsConnected === false) {
          continue;
        }
        const currentPortType = this.store.getState().robotData?.electronics?.[portIndex - 1]?.electronicType ?? null;
        const allowUntypedPortHydration = allowUntypedPortHydrationPorts?.has(portIndex) === true;
        if (incomingDeviceType == null && currentPortType != null && allowUntypedPortHydration) {
          incomingDeviceType = currentPortType;
        }
        if (currentPortType != null && incomingDeviceType != null && incomingDeviceType !== currentPortType) {
          continue;
        }
        if (currentPortType != null && incomingDeviceType == null) {
          continue;
        }
        if (allowUntypedPortHydration) {
          const supersededInfo = this._getPortSupersededAccessoryInfo(deviceIndex);
          if (supersededInfo && this._portIndexedResponseMatchesAccessoryInfo(rawDevice, supersededInfo)) {
            continue;
          }
        }
      }

      this._applyRemoteDeviceSystemInfo(
        {
          ...rawDevice,
          deviceIndex,
          deviceType: incomingDeviceType,
          isConnected: rawDevice.isConnected ?? true,
          ...(Object.prototype.hasOwnProperty.call(rawDevice, 'updateInProgress') ? { updateInProgress: rawDevice.updateInProgress } : {}),
          ...(Object.prototype.hasOwnProperty.call(rawDevice, 'updateProgress') ? { updateProgress: rawDevice.updateProgress } : {}),
        },
        { preserveMissingUpdateFields: true, preserveExistingPortInfo: true }
      );
    }
  }

  async getRobotValidationInformation() {
    const requestRobot = this.robot;
    // Prime overseer polling early; validation must not wait on this response.
    let systemStateResponsePromise = null;
    if (this._getEffectiveRobotType() === ROBOTS.BPRDU && requestRobot?.getSystemStateResponse) {
      try {
        ({ responsePromise: systemStateResponsePromise } = this._trackSystemStateRequest(
          requestRobot,
          VALIDATION_SYSTEM_STATE_RESPONSE_TIMEOUT_MS,
          true
        ));
      } catch {
        systemStateResponsePromise = Promise.resolve(null);
      }
    } else {
      requestRobot?.getSystemState?.();
    }

    try {
      const initialSystemStateResponse = systemStateResponsePromise ? await systemStateResponsePromise : null;
      if (!requestRobot || this.robot !== requestRobot || this._robotDisconnectReleased) {
        return;
      }

      const validationObservedState = normalizeDrivebaseSystemState(initialSystemStateResponse?.data?.state);
      const shouldDeferFirmwareValidationForObservedProgram =
        initialSystemStateResponse?.success === true &&
        isDrivebasePythonProgramBusy(validationObservedState) &&
        this._getEffectiveRobotType() === ROBOTS.BPRDU;

      if (shouldDeferFirmwareValidationForObservedProgram) {
        this._deferredConnectionValidationPending = true;
        this._deferConnectionMetadataBootstrapUntilObservedProgramEnds = true;
        this._deferSlotStorageQueryUntilObservedProgramEnds = true;
        this._observedProgramEndedSlotStorageQueryAttempted = false;
        this._deferredObservedProgramCriticalDialogShown = false;
        this._validationObservedRuntimeProgramState = validationObservedState;
      }

      let firmwareVersions = null;
      if (!shouldDeferFirmwareValidationForObservedProgram) {
        const pending = this.getRobotFirmwareVersions(true);
        if (pending == null || typeof pending.then !== 'function') {
          btConnectionTimeout.stop();
          return;
        }
        firmwareVersions = await pending;
      }

      if (this._isStaleValidatingSession(requestRobot)) {
        return;
      }

      let updaterStatePayload = null;
      try {
        updaterStatePayload = await this._getLibraryUpdaterState();
      } catch (error) {
        console.error('Error getting library updater state during validating:', error);
      }

      if (this._isStaleValidatingSession(requestRobot)) {
        return;
      }

      this._applyBestLibraryUpdaterState(updaterStatePayload, firmwareVersions);
      if (
        shouldDeferFirmwareValidationForObservedProgram &&
        this.robotFirmwareManager?.criticalUpdate?.() === true &&
        this.robotFirmwareManager?.hasActionableUpdate?.() === true &&
        !this._deferredObservedProgramCriticalDialogShown
      ) {
        this._pendingDeferredCriticalFirmwareDialog = true;
      }
      if (this.can('validated')) {
        this.validated();
      } else if (!this._isStaleValidatingSession(requestRobot)) {
        this.disconnect(_getErrorForCode(RobotErrorCode.FIRMWARE_VERSIONS_FAILED));
      }
    } catch {
      if (!this._isStaleValidatingSession(requestRobot)) {
        this.disconnect(_getErrorForCode(RobotErrorCode.FIRMWARE_VERSIONS_FAILED));
      }
    } finally {
      btConnectionTimeout.stop();
    }
  }

  async getRobotFirmwareVersions(includeAll = false, force = false) {
    if (force === true) {
      this._invalidateFirmwareMetadataRequests();
    }

    const requestToken = this._firmwareMetadataRequestToken;
    if (this._getRobotFirmwareVersionsPromise && this._getRobotFirmwareVersionsPromiseToken === requestToken) {
      return this._getRobotFirmwareVersionsPromise;
    }

    const shouldDebounce = force !== true && includeAll !== true && this.state !== 'validating';
    if (shouldDebounce && this._lastGetRobotFirmwareVersionsTime && Date.now() - this._lastGetRobotFirmwareVersionsTime < 5000) {
      return null;
    }
    this._lastGetRobotFirmwareVersionsTime = Date.now();

    const requestRobot = this.robot;
    const requestRobotName = requestRobot?.getName?.() ?? null;
    const requestPromise = (async () => {
      let allVersions = null;
      try {
        if (!this.isRobotConnected() && this.state !== 'validating') {
          console.error('Robot not connected, skipping firmware version query', this.state);
          return null;
        }

        if (!requestRobot || typeof requestRobot.sdkToy !== 'function') {
          console.error('Robot unavailable, skipping firmware version query', this.state);
          return null;
        }

        const all = includeAll || this.isInQueryAssetState();
        const options = {
          includeStaged: all,
          includeAssetVersions: all,
          includeFirmwareVersionByType: all,
          includeLibraryUpdaterState: all,
          includeRemoteDeviceSystemInfo: true,
        };

        const sdkToy = requestRobot.sdkToy();
        let assetConfig;
        try {
          assetConfig = await getAssetConfig();
        } catch (fetchError) {
          _logMessage('Firmware versions unavailable: asset config could not be loaded (network or CORS). Using cached config when available.');
          return null;
        }

        allVersions = await this.sdk.bprControl.getFirmwareVersions(sdkToy, assetConfig, options);
        console.log('Firmware versions response.data:', allVersions);

        if (this._firmwareMetadataRequestToken !== requestToken || this.robot !== requestRobot) {
          _logMessage(`Ignoring stale drive-unit firmware metadata from '${requestRobotName || 'unknown robot'}'`);
          return allVersions;
        }

        this.dispatch(this.robotDataActions.onUpdateDriveUnitFirmwareVersions(allVersions));
        if (all) {
          this._applyBestLibraryUpdaterState(null, allVersions);
          this._openCriticalFirmwareDialogFromFreshMetadataIfReady();
        }
        this._hydrateRemoteDeviceSystemInfoFromFirmwareVersions(allVersions);
      } catch (error) {
        if (requestRobotName) {
          console.error('Error getting robot firmware version:', error, this.state, requestRobotName);
        }
      } finally {
        this._portUntypedFirmwareHydrationPorts = null;
      }

      return allVersions;
    })();

    this._getRobotFirmwareVersionsPromise = requestPromise;
    this._getRobotFirmwareVersionsPromiseToken = requestToken;

    try {
      return await requestPromise;
    } finally {
      if (this._getRobotFirmwareVersionsPromise === requestPromise) {
        this._getRobotFirmwareVersionsPromise = null;
        this._getRobotFirmwareVersionsPromiseToken = null;
      }
    }
  }

  /**
   * Read deployed Python source for a single drive-unit slot (slot1–slot8).
   * This is relatively expensive; do not call for every slot on connect.
   * Use when slot contents are needed (move source, preview, load-from-slot, etc.) or via
   * {@link refreshSlotStorageProvenanceFromDevice} when Redux provenance must match the device.
   */
  async readPythonAsset(slot) {
    if (!this.robot) {
      return;
    }

    const sdkToy = this.robot.sdkToy();
    return await this.sdk.assetManager.readPythonAsset(sdkToy, { slot: slot });
  }

  /**
   * Read one slot from the device and merge BPS project metadata into `robotData.slotStorage`.
   * Call when accurate `bpsProjectId` / program title are required for that slot only.
   */
  async refreshSlotStorageProvenanceFromDevice(slotName) {
    if (!this.robot || this.robotType !== ROBOTS.BPRDU) {
      return;
    }

    if (!isValidSlotName(slotName)) {
      return;
    }

    const pythonSource = await this.readPythonAsset(slotName);
    if (typeof pythonSource !== 'string') {
      return;
    }

    const existing = this.store.getState().robotData?.slotStorage?.[slotName];
    const deviceFilename = normalizeSlotFilename(existing, slotName);
    this._dispatchSlotStorageFromPythonSource(slotName, pythonSource, deviceFilename);
  }

  _dispatchSlotStorageFromPythonSource(slotName, pythonSource, deviceFilename) {
    const slotNumber = Number(slotName.slice(4));
    const parsedProjectMetadata = parseBpsProjectMetadataFromPythonSource(pythonSource);
    const isEmptyPlaceholder = typeof deviceFilename === 'string' && deviceFilename.toLowerCase() === 'empty.py';

    if (isEmptyPlaceholder) {
      this.dispatch(
        this.robotDataActions.onUpdateSlotStorage({
          slotNumber,
          programTitle: '',
          isEmpty: true,
          deviceFilename,
          bpsProjectId: undefined,
          bpsProjectLastModifiedUtcMs: undefined,
        })
      );
      return;
    }

    const parsedProjectName = parsedProjectMetadata.projectName ?? undefined;
    this.dispatch(
      this.robotDataActions.onUpdateSlotStorage({
        slotNumber,
        programTitle: parsedProjectName || deviceFilename,
        isEmpty: false,
        deviceFilename,
        bpsProjectId: parsedProjectMetadata.projectId ?? undefined,
        bpsProjectLastModifiedUtcMs: parsedProjectMetadata.projectLastModifiedUtcMs ?? undefined,
      })
    );
  }

  _shouldDeferSlotStorageQueryForRunningProgram() {
    if (this._deferSlotStorageQueryUntilObservedProgramEnds) {
      return true;
    }

    if (isDrivebaseRuntimeBusyForSlotStorageDeferral(this._getRuntimeProgramState())) {
      return true;
    }

    return PROGRAM_RUNNING_STATES.some((runningState) => this.is?.(runningState) === true) || this.is?.('programExecuting') === true;
  }

  _fulfillDeferredSlotStorageQueryIfNeeded() {
    if (!this._deferSlotStorageQueryUntilObservedProgramEnds) {
      return false;
    }

    this._deferSlotStorageQueryUntilObservedProgramEnds = false;
    if (this._slotStorageMetadataLoadedForConnection || this._getEffectiveRobotType() !== ROBOTS.BPRDU || !this.robot) {
      return false;
    }

    if (typeof this.queryRobotSlotStorage === 'function') {
      this._observedProgramEndedSlotStorageQueryAttempted = true;
      void this.queryRobotSlotStorage();
      return true;
    }

    return false;
  }

  _slotStorageRecoveryNeeded(slots = this.store.getState().robotData?.slotStorage) {
    const slotValues = slots ? Object.values(slots) : [];
    return slotValues.length > 0 && slotValues.every((slot) => slot?.isEmpty !== false);
  }

  _shouldRecoverSlotStorageForObservedRunningProgram() {
    return (
      this._isRuntimeExecutingPython() || PROGRAM_RUNNING_STATES.some((state) => this.is?.(state) === true) || this.is?.('programExecuting') === true
    );
  }

  async _recoverSlotStorageFromPythonSourcesIfNeeded(requestRobot = this.robot) {
    if (!requestRobot || this.robot !== requestRobot || this._getEffectiveRobotType() !== ROBOTS.BPRDU) {
      this._lastSlotStorageRecoveryFresh = false;
      return false;
    }

    const activeSlotQueryPromise = this._queryRobotSlotStoragePromise;
    if (activeSlotQueryPromise) {
      await activeSlotQueryPromise;
      if (!requestRobot || this.robot !== requestRobot) {
        this._lastSlotStorageRecoveryFresh = false;
        return false;
      }
    }

    const robotDataState = this.store.getState().robotData || {};
    const existingSlots = robotDataState.slotStorage || {};
    if (!this._slotStorageRecoveryNeeded(existingSlots)) {
      this._lastSlotStorageRecoveryFresh = false;
      return false;
    }

    const currentRobotUidKey = robotDataState.uid;

    const recoveredSlots = Object.fromEntries(
      SLOT_NAMES.map((slotName) => {
        const existing = existingSlots[slotName];
        return [
          slotName,
          {
            slotNumber: Number(slotName.slice(4)),
            programTitle: existing?.programTitle || '',
            isEmpty: existing?.isEmpty ?? true,
            bpsProjectId: existing?.bpsProjectId,
            bpsProjectLastModifiedUtcMs: existing?.bpsProjectLastModifiedUtcMs,
            deviceFilename: existing?.deviceFilename,
          },
        ];
      })
    );

    let recoveredAnySlots = false;
    let recoveredSlotsFullyVerified = true;

    for (const slotName of SLOT_NAMES) {
      if (this.robot !== requestRobot) {
        this._lastSlotStorageRecoveryFresh = false;
        return false;
      }

      const existing = recoveredSlots[slotName];
      let pythonSource = null;
      try {
        pythonSource = await this.readPythonAsset(slotName);
      } catch (error) {
        console.error(`Failed to read Python source while recovering ${slotName}:`, error);
        recoveredSlotsFullyVerified = false;
        continue;
      }

      if (typeof pythonSource !== 'string' || pythonSource.trim() === '') {
        continue;
      }

      const parsedProjectMetadata = parseBpsProjectMetadataFromPythonSource(pythonSource);
      const hasVerifiedDeviceFilename = Boolean(existing?.deviceFilename && existing.deviceFilename.toLowerCase() !== 'empty.py');
      const resolvedFilename = normalizeSlotFilename(
        {
          deviceFilename: hasVerifiedDeviceFilename ? existing.deviceFilename : undefined,
          programTitle: parsedProjectMetadata.projectName || existing?.programTitle,
        },
        slotName
      );

      if (!hasVerifiedDeviceFilename) {
        recoveredSlotsFullyVerified = false;
      }

      recoveredSlots[slotName] = {
        slotNumber: Number(slotName.slice(4)),
        programTitle: parsedProjectMetadata.projectName || existing?.programTitle || resolvedFilename,
        isEmpty: false,
        deviceFilename: resolvedFilename,
        bpsProjectId: parsedProjectMetadata.projectId ?? existing?.bpsProjectId,
        bpsProjectLastModifiedUtcMs: parsedProjectMetadata.projectLastModifiedUtcMs ?? existing?.bpsProjectLastModifiedUtcMs,
      };

      if (this.currentSlot === slotName) {
        this.currentFilename = resolvedFilename;
      }

      recoveredAnySlots = true;
    }

    if (!recoveredAnySlots || this.robot !== requestRobot) {
      this._lastSlotStorageRecoveryFresh = false;
      return false;
    }

    this.dispatch(this.robotDataActions.onUpdateSlotStorage(recoveredSlots));
    if (currentRobotUidKey) {
      this.dispatch(this.robotDataActions.onUpdateSlotStorageOwnerUid(currentRobotUidKey));
    }
    if (recoveredSlotsFullyVerified) {
      this._deferSlotStorageQueryUntilObservedProgramEnds = false;
      this._slotStorageMetadataLoadedForConnection = true;
    }
    this._lastSlotStorageRecoveryFresh = recoveredSlotsFullyVerified;
    this.dispatch(this.robotDataActions.onSlotStorageReady(recoveredSlotsFullyVerified));
    return true;
  }

  async movePythonProgram(sourceSlot, destinationSlot) {
    if (!this.robot || this.robotType !== ROBOTS.BPRDU) {
      throw new Error('Python program moving is only available for BPR Drive Unit robots');
    }

    if (sourceSlot === destinationSlot) {
      throw new Error('Source and destination slots must be different');
    }

    if (!isValidSlotName(sourceSlot) || !isValidSlotName(destinationSlot)) {
      throw new Error('Invalid slot id');
    }

    const slotStorage = this.store.getState().robotData?.slotStorage || {};
    const sourceSlotInfo = slotStorage[sourceSlot];
    const pythonSource = await this.readPythonAsset(sourceSlot);
    if (typeof pythonSource !== 'string') {
      throw new Error('readPythonAsset returned no data');
    }

    const parsedSourceMetadata = parseBpsProjectMetadataFromPythonSource(pythonSource);
    const filename = normalizeSlotFilename(sourceSlotInfo, sourceSlot);
    const programTitle = parsedSourceMetadata.projectName || sourceSlotInfo?.programTitle || filename;
    const bpsProjectId = parsedSourceMetadata.projectId ?? sourceSlotInfo?.bpsProjectId;
    const bpsProjectLastModifiedUtcMs = parsedSourceMetadata.projectLastModifiedUtcMs ?? sourceSlotInfo?.bpsProjectLastModifiedUtcMs;

    try {
      await this.loadPythonProgram(pythonSource, destinationSlot, filename);
      await this.erasePythonProgram(sourceSlot);

      if (this.currentSlot === sourceSlot) {
        this.currentSlot = destinationSlot;
        this.currentFilename = filename;
      } else if (this.currentSlot === destinationSlot) {
        this.currentFilename = filename;
      }

      this.dispatch(
        this.robotDataActions.onUpdateSlotStorage({
          slotNumber: Number(destinationSlot.slice(4)),
          programTitle,
          isEmpty: false,
          bpsProjectId,
          bpsProjectLastModifiedUtcMs,
          deviceFilename: filename,
        })
      );
      this.dispatch(
        this.robotDataActions.onUpdateSlotStorage({
          slotNumber: Number(sourceSlot.slice(4)),
          programTitle: '',
          isEmpty: true,
          bpsProjectId: undefined,
          bpsProjectLastModifiedUtcMs: undefined,
          deviceFilename: 'empty.py',
        })
      );
    } finally {
      await this.queryRobotSlotStorage({ refreshIfRunning: true });
    }
  }

  /**
   * Query the drive unit (BPRDU) for Python asset metadata for each slot (slot1–slot8) and
   * update Redux `robotData.slotStorage` with filenames and empty/occupied state.
   *
   * Prefer `ToyBPRDriveUnit.getPythonProgramNameAtIndex(index)` (DID 0x24 / CID 0x0F),
   * which returns the only metadata this path needs: the program name. Falls back to
   * `this.sdk.bprControl.getAssetMetadata(sdkToy, assetId)` for older SDK/firmware surfaces.
   *
   * **Intentionally does not call `readPythonAsset` for every slot.** Reading deployed Python
   * is slow (especially over BLE) and was a major connect-time performance problem when done
   * here. Full source is only needed when acting on a specific slot (deploy, move, preview,
   * load-from-slot, etc.). Those code paths must call `readPythonAsset` or
   * `refreshSlotStorageProvenanceFromDevice` for the affected slot(s) before overwriting or
   * when parsed `bpsProjectId` / project title are required.
   *
   * On query, BPS project provenance (`bpsProjectId`, `programTitle`, last-modified) is taken
   * from the existing Redux cache when the robot uid matches `slotStorageOwnerUid` and the
   * device filename is unchanged; otherwise only the device-reported filename is applied
   * (typically the filename as the display title). Use `refreshSlotStorageProvenanceFromDevice`
   * to hydrate provenance for one slot from on-device Python when needed.
   */
  async queryRobotSlotStorage({ refreshIfRunning = false } = {}) {
    if (this._queryRobotSlotStoragePromise) {
      const activeRequestRobot = this._queryRobotSlotStorageRequestRobot;
      const requestRobotChanged = Boolean(this.robot) && Boolean(activeRequestRobot) && this.robot !== activeRequestRobot;

      if (refreshIfRunning || requestRobotChanged) {
        this._queryRobotSlotStorageNeedsRefresh = true;
        this._queryRobotSlotStorageRequestToken += 1;
      }
      return this._queryRobotSlotStoragePromise;
    }

    this.dispatch(this.robotDataActions.onSlotStorageQueryStarted());
    this._queryRobotSlotStorageNeedsRefresh = true;
    this._queryRobotSlotStorageRequestToken += 1;
    const requestRobot = this.robot;
    this._queryRobotSlotStorageRequestRobot = requestRobot;

    const refreshPromise = Promise.resolve().then(async () => {
      let attemptCount = 0;
      let shouldRecoverFromPythonSources = false;
      let slotMetadataLoadSucceeded = false;
      let slotMetadataFresh = false;

      try {
        try {
          while (this._queryRobotSlotStorageNeedsRefresh) {
            this._queryRobotSlotStorageNeedsRefresh = false;
            const requestToken = this._queryRobotSlotStorageRequestToken;
            const sweepResult = await this._queryRobotSlotStorageOnce(requestToken);

            if (
              sweepResult === SLOT_STORAGE_SWEEP_STALE ||
              requestToken !== this._queryRobotSlotStorageRequestToken ||
              !this.robot ||
              this.robot !== requestRobot
            ) {
              attemptCount = 0;
              continue;
            }

            if (sweepResult === true) {
              slotMetadataLoadSucceeded = true;
              slotMetadataFresh = true;
              attemptCount = 0;
              continue;
            }

            attemptCount += 1;
            if (attemptCount < SLOT_STORAGE_QUERY_MAX_ATTEMPTS) {
              this._queryRobotSlotStorageNeedsRefresh = true;
              await new Promise((resolve) => setTimeout(resolve, SLOT_STORAGE_QUERY_RETRY_DELAY_MS));
            }
          }
        } finally {
          if (this._queryRobotSlotStoragePromise === refreshPromise) {
            this._queryRobotSlotStoragePromise = null;
            this._queryRobotSlotStorageRequestRobot = null;
          }
          shouldRecoverFromPythonSources =
            Boolean(requestRobot) &&
            this.robot === requestRobot &&
            this._slotStorageRecoveryNeeded() &&
            this._shouldRecoverSlotStorageForObservedRunningProgram();
        }

        if (shouldRecoverFromPythonSources) {
          try {
            const recovered = await this._recoverSlotStorageFromPythonSourcesIfNeeded(requestRobot);
            if (recovered) {
              slotMetadataLoadSucceeded = true;
              slotMetadataFresh = this._lastSlotStorageRecoveryFresh === true;
            }
          } catch (error) {
            console.error('Failed to recover slot storage after metadata query:', error);
          }
        }
      } finally {
        if (this.robot !== requestRobot) {
          return;
        }

        if (slotMetadataFresh) {
          this._deferSlotStorageQueryUntilObservedProgramEnds = false;
          this._slotStorageMetadataLoadedForConnection = true;
        }
        // Unblock deploy/run even when metadata sweeps fail, but mark the cache stale so
        // slot selection stays conservative until this connection has verified metadata.
        this.dispatch(this.robotDataActions.onSlotStorageReady(slotMetadataFresh));
      }
    });

    this._queryRobotSlotStoragePromise = refreshPromise;
    return refreshPromise;
  }

  async _queryRobotSlotStorageOnce(requestToken) {
    const requestRobot = this.robot;
    const requestRobotName = requestRobot?.getName?.() ?? null;
    const isStaleRequest = () => requestToken !== this._queryRobotSlotStorageRequestToken || this.robot !== requestRobot;

    try {
      if (!requestRobot || this._getEffectiveRobotType() !== ROBOTS.BPRDU) {
        return false;
      }

      // If we are in any state of finalizing firmware, return and do not run this command.
      if (this.is && (this.is('finalizingUpdates') || this.is('finalizingUpdatesSucceeded') || this.is('finalizingUpdatesFailed'))) {
        return false;
      }

      const sdkToy = requestRobot.sdkToy?.();
      let canQueryPythonProgramName = !this._slotStorageProgramNameQueryDisabledForConnection && this._canQueryPythonProgramNameAtIndex(requestRobot);
      const canQueryAssetMetadata = sdkToy && this.sdk && this.sdk.bprControl && typeof this.sdk.bprControl.getAssetMetadata === 'function';

      if (!canQueryPythonProgramName && !canQueryAssetMetadata) {
        return false;
      }

      const state = this.store.getState();
      const existingSlots = (state.robotData && state.robotData.slotStorage) || {};
      const currentRobotUidKey = state.robotData?.uid;
      const cachedOwnerUid = state.robotData?.slotStorageOwnerUid;
      const canFallbackToCachedProvenance = currentRobotUidKey && cachedOwnerUid === currentRobotUidKey;

      const slots = Object.fromEntries(
        SLOT_NAMES.map((slotName) => {
          const existing = existingSlots[slotName];
          const existingDeviceFilename =
            typeof existing?.deviceFilename === 'string' ? sanitizeDeviceReportedSlotFilename(existing.deviceFilename) : null;
          return [
            slotName,
            {
              slotNumber: Number(slotName.slice(4)),
              programTitle: existing?.programTitle || '',
              isEmpty: existing?.isEmpty ?? true,
              bpsProjectId: existing?.bpsProjectId,
              bpsProjectLastModifiedUtcMs: existing?.bpsProjectLastModifiedUtcMs,
              deviceFilename: existingDeviceFilename ?? undefined,
            },
          ];
        })
      );
      let hadMetadataFailure = false;

      for (const slotName of SLOT_NAMES) {
        if (isStaleRequest()) {
          return SLOT_STORAGE_SWEEP_STALE;
        }

        const slotNumber = Number(slotName.slice(4));

        let filename = null;
        try {
          if (canQueryPythonProgramName) {
            filename = await this._queryPythonProgramNameAtSlot(requestRobot, slotNumber);
            if (filename == null) {
              // Program-name responses do not include the requested slot, so a timed-out reply
              // could be consumed by the next slot listener. Use indexed asset metadata instead.
              this._slotStorageProgramNameQueryDisabledForConnection = true;
              canQueryPythonProgramName = false;
            }
          }

          if (!canQueryPythonProgramName) {
            if (!canQueryAssetMetadata) {
              hadMetadataFailure = true;
              continue;
            }
            const metadata = await this.sdk.bprControl.getAssetMetadata(sdkToy, slotName);
            filename = metadata && typeof metadata.filename === 'string' ? metadata.filename.trim() : null;
          }
        } catch (error) {
          hadMetadataFailure = true;
          if (requestRobotName) {
            console.error(`Failed to query robot slot storage filename for ${slotName}:`, error, this.state, requestRobotName);
          }
          continue;
        }

        if (filename == null) {
          hadMetadataFailure = true;
          continue;
        }

        const sanitizedFilename = sanitizeDeviceReportedSlotFilename(filename);
        if (sanitizedFilename == null) {
          hadMetadataFailure = true;
          continue;
        }
        filename = sanitizedFilename;

        const isEmptyPlaceholder = filename === 'empty.py';
        const existing = existingSlots[slotName];
        const shouldFallbackToCachedProvenance = !isEmptyPlaceholder && canFallbackToCachedProvenance && existing?.deviceFilename === filename;

        if (isEmptyPlaceholder) {
          slots[slotName] = {
            slotNumber,
            programTitle: '',
            isEmpty: true,
            deviceFilename: filename || 'empty.py',
            bpsProjectId: undefined,
            bpsProjectLastModifiedUtcMs: undefined,
          };
        } else {
          slots[slotName] = {
            slotNumber,
            programTitle: shouldFallbackToCachedProvenance && existing?.programTitle ? existing.programTitle : filename,
            isEmpty: false,
            deviceFilename: filename,
            bpsProjectId: shouldFallbackToCachedProvenance ? existing?.bpsProjectId : undefined,
            bpsProjectLastModifiedUtcMs: shouldFallbackToCachedProvenance ? existing?.bpsProjectLastModifiedUtcMs : undefined,
          };
        }

        if (!isEmptyPlaceholder && this.currentSlot === slotName) {
          this.currentFilename = filename;
        }
      }

      if (isStaleRequest()) {
        return SLOT_STORAGE_SWEEP_STALE;
      }

      this.dispatch(this.robotDataActions.onUpdateSlotStorage(slots));
      if (currentRobotUidKey) {
        this.dispatch(this.robotDataActions.onUpdateSlotStorageOwnerUid(currentRobotUidKey));
      }
      return !hadMetadataFailure;
    } catch (error) {
      if (requestRobotName) {
        console.error('Failed to query robot slot storage:', error, this.state, requestRobotName);
      }
      return false;
    }
  }

  getBprLogClient(signal) {
    if (!this.robot || this.robotType !== ROBOTS.BPRDU) {
      throw new Error('Drive Unit logs are only available while a drive unit is connected.');
    }

    const sdkToy = this.robot.sdkToy?.();
    if (!sdkToy) {
      throw new Error('Drive Unit log commands are unavailable for the current robot connection.');
    }

    const client = new BprToyLogs(sdkToy, { signal });
    if (!client.supportsCommands()) {
      throw new Error('The connected drive unit firmware does not support log commands.');
    }

    return client;
  }

  _getBprLogAbortController() {
    if (!this._pendingBprLogAbortController || this._pendingBprLogAbortController.signal.aborted) {
      this._pendingBprLogAbortController = new AbortController();
    }

    return this._pendingBprLogAbortController;
  }

  _cancelBprLogOperations(reason = new BprLogOperationCancelledError('Drive Unit disconnected.')) {
    const cancellationReason =
      reason instanceof Error ? reason : new BprLogOperationCancelledError(typeof reason === 'string' ? reason : 'Drive Unit disconnected.');

    if (this._pendingBprLogAbortController && !this._pendingBprLogAbortController.signal.aborted) {
      this._pendingBprLogAbortController.abort(cancellationReason);
    }

    this._pendingBprLogAbortController = null;
    this._pendingBprLogOperation = null;
    this._bprLogOperationGeneration += 1;
  }

  _getCallerAbortError(signal) {
    const reason = signal?.reason;
    if (reason instanceof Error) {
      return reason;
    }
    if (reason && typeof reason === 'object' && reason.name === 'AbortError') {
      return new DOMException(reason.message || 'The operation was aborted.', 'AbortError');
    }
    return new DOMException('The operation was aborted.', 'AbortError');
  }

  _throwIfBprLogCallerAborted(signal) {
    if (signal?.aborted) {
      throw this._getCallerAbortError(signal);
    }
  }

  _createBprLogPublicPromise(queuedOperation, signal) {
    if (!signal) {
      return queuedOperation;
    }

    return new Promise((resolve, reject) => {
      let settled = false;

      const cleanup = () => {
        signal.removeEventListener('abort', handleAbort);
      };

      const settle = (callback) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        callback();
      };

      const handleAbort = () => {
        settle(() => {
          reject(this._getCallerAbortError(signal));
        });
      };

      if (signal.aborted) {
        handleAbort();
        return;
      }

      signal.addEventListener('abort', handleAbort, { once: true });
      queuedOperation.then(
        (value) => {
          settle(() => {
            resolve(value);
          });
        },
        (error) => {
          settle(() => {
            reject(error);
          });
        }
      );
    });
  }

  _createBprLogOperationSignal(callerSignal) {
    const connectionSignal = this._getBprLogAbortController().signal;
    if (!callerSignal || callerSignal === connectionSignal) {
      return {
        signal: connectionSignal,
        cleanup: () => {},
      };
    }

    const controller = new AbortController();
    const listeners = [
      [callerSignal, () => controller.abort(this._getCallerAbortError(callerSignal))],
      [connectionSignal, () => controller.abort(connectionSignal.reason ?? new BprLogOperationCancelledError())],
    ];

    const cleanup = () => {
      listeners.forEach(([signal, listener]) => {
        signal.removeEventListener('abort', listener);
      });
    };

    for (const [signal, listener] of listeners) {
      if (signal.aborted) {
        listener();
        cleanup();
        return {
          signal: controller.signal,
          cleanup: () => {},
        };
      }
      signal.addEventListener('abort', listener, { once: true });
    }

    return {
      signal: controller.signal,
      cleanup,
    };
  }

  async _runBprLogOperation(operation, signal) {
    if (signal?.aborted) {
      throw this._getCallerAbortError(signal);
    }

    const generation = this._bprLogOperationGeneration;
    const previousOperation = this._pendingBprLogOperation ?? Promise.resolve();
    const queuedOperation = previousOperation
      .catch(() => undefined)
      .then(async () => {
        if (generation !== this._bprLogOperationGeneration) {
          throw new BprLogOperationCancelledError('Drive Unit disconnected.');
        }
        this._throwIfBprLogCallerAborted(signal);

        const { signal: operationSignal, cleanup } = this._createBprLogOperationSignal(signal);
        try {
          return await operation(operationSignal);
        } finally {
          cleanup();
        }
      });

    this._pendingBprLogOperation = queuedOperation.then(
      () => undefined,
      () => undefined
    );

    return await this._createBprLogPublicPromise(queuedOperation, signal);
  }

  async getBprEventLogs(signal) {
    return await this._runBprLogOperation((operationSignal) => this.getBprLogClient(operationSignal).readAllLogs(), signal);
  }

  async clearBprEventLog(index, signal) {
    return await this._runBprLogOperation((operationSignal) => this.getBprLogClient(operationSignal).clearLogAndWait(index), signal);
  }

  getRemoteDeviceTypes() {
    if (this._robotDisconnectReleased || !this.robot || this._remoteDeviceTypesActiveRequest || this._remoteDeviceTypesTimeoutBlockedRobot) {
      return;
    }

    this._startRemoteDeviceTypesRequest({
      kind: 'background',
      robot: this.robot,
      connectionGeneration: this._remoteDeviceTypesConnectionGeneration,
    });
  }

  getRemoteDeviceTypesOnce() {
    return new Promise((resolve, reject) => {
      if (!this.robot || this._robotDisconnectReleased) {
        reject(new Error('No drive unit is connected.'));
        return;
      }
      if (this._remoteDeviceTypesTimeoutBlockedRobot === this.robot) {
        const error = new Error(REMOTE_DEVICE_TYPES_TIMEOUT_BLOCKED_ERROR);
        error.code = REMOTE_DEVICE_TYPES_TIMEOUT_BLOCKED_ERROR_CODE;
        reject(error);
        return;
      }
      const request = {
        kind: 'one-shot',
        robot: this.robot,
        connectionGeneration: this._remoteDeviceTypesConnectionGeneration,
        resolve,
        reject,
      };
      if (this._remoteDeviceTypesActiveRequest) {
        this._remoteDeviceTypesOneShotQueue.push(request);
        return;
      }

      this._startRemoteDeviceTypesRequest(request);
    });
  }

  getRemoteDeviceSystemInfoByIndex(index, options = {}) {
    if (this.robot) {
      const trackPortRequest = options.trackPortRequest === true;
      if (trackPortRequest && Number.isInteger(index) && index >= REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START) {
        const started = this._beginTrackedPortRemoteDeviceSystemInfoRequest(index, options.expectedElectronicType ?? null);
        if (!started) {
          return;
        }
        if (this._getPortSupersededAccessoryInfo(index)) {
          if (!this._portRemoteDeviceSystemInfoAllowIdenticalReplacementByIndex) {
            this._portRemoteDeviceSystemInfoAllowIdenticalReplacementByIndex = {};
          }
          this._portRemoteDeviceSystemInfoAllowIdenticalReplacementByIndex[index] = true;
        }
      }
      this.robot.getRemoteDeviceSystemInfoByIndex(index);
    }
  }

  _derivePortAccessoryInfoFromIndexedDevice(device) {
    if (device == null || typeof device !== 'object') {
      return {};
    }

    const hasOwn = (field) => Object.prototype.hasOwnProperty.call(device, field);
    const hasBootloaderSourceField = hasOwn('firmwareBootloader') || hasOwn('blMajor') || hasOwn('blMinor') || hasOwn('blRevision');
    const hasMainAppSourceField = hasOwn('firmwareMainApp') || hasOwn('maMajor') || hasOwn('maMinor') || hasOwn('maRevision');
    const info = {};
    if (hasOwn('devicePartNumber') && device.devicePartNumber != null) {
      info.devicePartNumber = device.devicePartNumber;
    }
    const firmwareBootloader = device.firmwareBootloader ?? formatFirmwareVersionOrUndefined(device.blMajor, device.blMinor, device.blRevision);
    const firmwareMainApp = device.firmwareMainApp ?? formatFirmwareVersionOrUndefined(device.maMajor, device.maMinor, device.maRevision);
    if (hasBootloaderSourceField && firmwareBootloader != null) {
      info.firmwareBootloader = firmwareBootloader;
    }
    if (hasMainAppSourceField && firmwareMainApp != null) {
      info.firmwareMainApp = firmwareMainApp;
    }
    if (hasOwn('boardRevision') && device.boardRevision != null) {
      info.boardRevision = formatBoardRevision(device.boardRevision);
    }
    if ((hasOwn('uid') || hasOwn('uidSize')) && device.uid != null) {
      const formattedUid = formatDeviceUidHex(device.uid, device.uidSize);
      if (formattedUid != null) {
        info.formattedUid = formattedUid;
      }
    }
    return info;
  }

  _portIndexedResponseMatchesAccessoryInfo(device, info) {
    if (info == null || typeof info !== 'object') {
      return false;
    }

    const derived = this._derivePortAccessoryInfoFromIndexedDevice(device);
    const comparableFields = ['devicePartNumber', 'firmwareBootloader', 'firmwareMainApp', 'formattedUid', 'boardRevision'];
    const fieldsToCompare = comparableFields.filter((field) => info[field] != null && derived[field] != null);
    if (fieldsToCompare.length === 0) {
      return false;
    }
    return fieldsToCompare.every((field) => derived[field] === info[field]);
  }

  _getPortSupersededAccessoryInfo(deviceIndex) {
    const entry = this._portRemoteDeviceSystemInfoSupersededInfoByIndex?.[deviceIndex];
    if (entry == null) {
      return null;
    }
    if (entry.info != null && typeof entry.info === 'object') {
      return entry.info;
    }
    return typeof entry === 'object' ? entry : null;
  }

  _isPortRemoteDeviceSystemInfoInvalidationGraceActive(deviceIndex) {
    return this._portRemoteDeviceSystemInfoInvalidationGraceByIndex?.[deviceIndex] != null;
  }

  _clearPortRemoteDeviceSystemInfoInvalidationGrace(deviceIndex) {
    const grace = this._portRemoteDeviceSystemInfoInvalidationGraceByIndex?.[deviceIndex];
    if (grace?.timerId != null) {
      clearTimeout(grace.timerId);
    }
    if (this._portRemoteDeviceSystemInfoInvalidationGraceByIndex) {
      delete this._portRemoteDeviceSystemInfoInvalidationGraceByIndex[deviceIndex];
    }
  }

  _clearAllPortRemoteDeviceSystemInfoInvalidationGrace() {
    if (!this._portRemoteDeviceSystemInfoInvalidationGraceByIndex) {
      this._portRemoteDeviceSystemInfoInvalidationGraceByIndex = {};
      return;
    }
    for (const deviceIndex of Object.keys(this._portRemoteDeviceSystemInfoInvalidationGraceByIndex)) {
      this._clearPortRemoteDeviceSystemInfoInvalidationGrace(Number(deviceIndex));
    }
    this._portRemoteDeviceSystemInfoInvalidationGraceByIndex = {};
  }

  _beginPortRemoteDeviceSystemInfoInvalidationGrace(deviceIndex, onGraceElapsed) {
    if (!this._portRemoteDeviceSystemInfoInvalidationGraceByIndex) {
      this._portRemoteDeviceSystemInfoInvalidationGraceByIndex = {};
    }
    this._clearPortRemoteDeviceSystemInfoInvalidationGrace(deviceIndex);
    const timerId = setTimeout(() => {
      this._clearPortRemoteDeviceSystemInfoInvalidationGrace(deviceIndex);
      onGraceElapsed?.();
    }, PORT_REMOTE_DEVICE_SYSTEM_INFO_STALE_REPLY_GRACE_MS);
    this._portRemoteDeviceSystemInfoInvalidationGraceByIndex[deviceIndex] = { timerId };
  }

  _beginTrackedPortRemoteDeviceSystemInfoRequest(deviceIndex, expectedElectronicType) {
    if (this._isPortRemoteDeviceSystemInfoInvalidationGraceActive(deviceIndex)) {
      return false;
    }
    const existing = this._portRemoteDeviceSystemInfoRequestsByIndex?.[deviceIndex];
    if (existing?.awaitingStaleReplyGrace === true) {
      return false;
    }
    this._clearPortRemoteDeviceSystemInfoRequest(deviceIndex);
    if (!this._portRemoteDeviceSystemInfoGenerationByIndex) {
      this._portRemoteDeviceSystemInfoGenerationByIndex = {};
    }
    const requestGeneration = (this._portRemoteDeviceSystemInfoGenerationByIndex[deviceIndex] ?? 0) + 1;
    this._portRemoteDeviceSystemInfoGenerationByIndex[deviceIndex] = requestGeneration;
    const timeoutId = setTimeout(() => {
      this._onPortRemoteDeviceSystemInfoRequestTimeout(deviceIndex, requestGeneration);
    }, getPortRemoteDeviceSystemInfoTimeoutMs(this.state));

    if (!this._portRemoteDeviceSystemInfoRequestsByIndex) {
      this._portRemoteDeviceSystemInfoRequestsByIndex = {};
    }
    this._portRemoteDeviceSystemInfoRequestsByIndex[deviceIndex] = {
      robot: this.robot,
      requestGeneration,
      expectedElectronicType,
      timeoutId,
    };
    this._portRemoteDeviceSystemInfoLastRequestedIndex = deviceIndex;
    return true;
  }

  _clearPortRemoteDeviceSystemInfoRequest(deviceIndex) {
    const pending = this._portRemoteDeviceSystemInfoRequestsByIndex?.[deviceIndex];
    if (pending?.timeoutId != null) {
      clearTimeout(pending.timeoutId);
    }
    if (this._portRemoteDeviceSystemInfoRequestsByIndex) {
      delete this._portRemoteDeviceSystemInfoRequestsByIndex[deviceIndex];
    }
    this._clearPortRemoteDeviceSystemInfoBufferedResponse(deviceIndex);
  }

  _clearPortRemoteDeviceSystemInfoBufferedResponse(deviceIndex) {
    const flushTimerId = this._portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex?.[deviceIndex];
    if (flushTimerId != null) {
      clearTimeout(flushTimerId);
    }
    if (this._portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex) {
      delete this._portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex[deviceIndex];
    }
    if (this._portRemoteDeviceSystemInfoBufferedResponsesByIndex) {
      delete this._portRemoteDeviceSystemInfoBufferedResponsesByIndex[deviceIndex];
    }
  }

  _clearPendingPortRemoteDeviceSystemInfoRequests() {
    if (!this._portRemoteDeviceSystemInfoRequestsByIndex) {
      this._portRemoteDeviceSystemInfoRequestsByIndex = {};
      this._portRemoteDeviceSystemInfoGenerationByIndex = {};
      this._portRemoteDeviceSystemInfoAttemptCompleteByIndex = {};
      this._portRemoteDeviceSystemInfoLastRequestedIndex = null;
      this._portRemoteDeviceSystemInfoBufferedResponsesByIndex = {};
      this._portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex = {};
      this._clearAllPortRemoteDeviceSystemInfoInvalidationGrace();
      return;
    }
    for (const deviceIndex of Object.keys(this._portRemoteDeviceSystemInfoRequestsByIndex)) {
      this._clearPortRemoteDeviceSystemInfoRequest(Number(deviceIndex));
    }
    this._portRemoteDeviceSystemInfoRequestsByIndex = {};
    this._portRemoteDeviceSystemInfoGenerationByIndex = {};
    this._portRemoteDeviceSystemInfoAttemptCompleteByIndex = {};
    this._portRemoteDeviceSystemInfoLastRequestedIndex = null;
    this._portRemoteDeviceSystemInfoBufferedResponsesByIndex = {};
    this._portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex = {};
    this._clearAllPortRemoteDeviceSystemInfoInvalidationGrace();
    this._portRemoteDeviceSystemInfoSupersededInfoByIndex = {};
    this._portRemoteDeviceSystemInfoSupersededRejectWaveByIndex = {};
    this._portRemoteDeviceSystemInfoAllowIdenticalReplacementByIndex = {};
  }

  _invalidatePortRemoteDeviceSystemInfoForChangedPorts(previousPortTypes, nextPortTypes, previousConnectedMask, nextConnectedMask) {
    if (!this._portRemoteDeviceSystemInfoGenerationByIndex) {
      this._portRemoteDeviceSystemInfoGenerationByIndex = {};
    }
    if (!this._portRemoteDeviceSystemInfoAttemptCompleteByIndex) {
      this._portRemoteDeviceSystemInfoAttemptCompleteByIndex = {};
    }
    const stateElectronics = this.store.getState().robotData?.electronics;
    const updatedElectronics = Array.isArray(stateElectronics) ? stateElectronics.slice() : [];
    let electronicsChanged = false;
    let deferPortSystemInfoRequests = false;
    const untypedFirmwareHydrationPorts = new Set();
    for (let port = 1; port <= 8; port++) {
      const previousType = previousPortTypes?.[port - 1] ?? null;
      const nextType = nextPortTypes[port - 1] ?? null;
      const previousConnected = previousConnectedMask != null ? ((previousConnectedMask >>> (port - 1)) & 1) === 1 : previousType != null;
      const nextConnected = nextConnectedMask != null ? ((nextConnectedMask >>> (port - 1)) & 1) === 1 : nextType != null;
      const typeChanged = previousType !== nextType;
      const connectionChanged = previousConnectedMask != null && nextConnectedMask != null && previousConnected !== nextConnected;
      if (!typeChanged && !connectionChanged) {
        continue;
      }
      const deviceIndex = REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START + port - 1;
      const idx = port - 1;
      this._portRemoteDeviceSystemInfoGenerationByIndex[deviceIndex] = (this._portRemoteDeviceSystemInfoGenerationByIndex[deviceIndex] ?? 0) + 1;
      this._clearPortRemoteDeviceSystemInfoRequest(deviceIndex);
      delete this._portRemoteDeviceSystemInfoAttemptCompleteByIndex[deviceIndex];
      deferPortSystemInfoRequests = true;
      this._beginPortRemoteDeviceSystemInfoInvalidationGrace(deviceIndex, () => {
        const portTypes = this._lastElectronicsPortTypes;
        if (portTypes) {
          this._requestPortRemoteDeviceSystemInfoForConnectedPorts(portTypes, { force: true });
        }
      });
      if (connectionChanged && !typeChanged) {
        const previousInfo = stateElectronics?.[idx]?.info;
        if (previousInfo != null && typeof previousInfo === 'object' && Object.keys(previousInfo).length > 0) {
          if (!this._portRemoteDeviceSystemInfoSupersededInfoByIndex) {
            this._portRemoteDeviceSystemInfoSupersededInfoByIndex = {};
          }
          if (!this._portRemoteDeviceSystemInfoSupersededRejectWaveByIndex) {
            this._portRemoteDeviceSystemInfoSupersededRejectWaveByIndex = {};
          }
          const rejectWave = (this._portRemoteDeviceSystemInfoSupersededRejectWaveByIndex[deviceIndex] ?? 0) + 1;
          this._portRemoteDeviceSystemInfoSupersededRejectWaveByIndex[deviceIndex] = rejectWave;
          this._portRemoteDeviceSystemInfoSupersededInfoByIndex[deviceIndex] = { info: { ...previousInfo } };
          delete this._portRemoteDeviceSystemInfoAllowIdenticalReplacementByIndex?.[deviceIndex];
        }
        if (nextConnected && nextType != null) {
          untypedFirmwareHydrationPorts.add(port);
        }
        while (updatedElectronics.length <= idx) {
          updatedElectronics.push({ electronicType: null, info: {} });
        }
        updatedElectronics[idx] = {
          electronicType: nextConnected ? nextType : null,
          info: {},
        };
        electronicsChanged = true;
      }
    }
    if (untypedFirmwareHydrationPorts.size > 0) {
      this._portUntypedFirmwareHydrationPorts = untypedFirmwareHydrationPorts;
    }
    if (electronicsChanged) {
      this.dispatch(this.robotDataActions.onUpdateElectronics(updatedElectronics));
    }
    return deferPortSystemInfoRequests;
  }

  _onPortRemoteDeviceSystemInfoRequestTimeout(deviceIndex, requestGeneration) {
    const pending = this._portRemoteDeviceSystemInfoRequestsByIndex?.[deviceIndex];
    if (!pending || pending.requestGeneration !== requestGeneration) {
      return;
    }
    if (this.robot !== pending.robot) {
      this._clearPortRemoteDeviceSystemInfoRequest(deviceIndex);
      return;
    }
    if (pending.timeoutId != null) {
      clearTimeout(pending.timeoutId);
      pending.timeoutId = null;
    }
    pending.awaitingStaleReplyGrace = true;
    pending.timeoutId = setTimeout(() => {
      this._onPortRemoteDeviceSystemInfoStaleReplyGraceElapsed(deviceIndex, requestGeneration);
    }, PORT_REMOTE_DEVICE_SYSTEM_INFO_STALE_REPLY_GRACE_MS);
  }

  _onPortRemoteDeviceSystemInfoStaleReplyGraceElapsed(deviceIndex, requestGeneration) {
    const pending = this._portRemoteDeviceSystemInfoRequestsByIndex?.[deviceIndex];
    if (!pending || pending.requestGeneration !== requestGeneration || pending.awaitingStaleReplyGrace !== true) {
      return;
    }
    this._clearPortRemoteDeviceSystemInfoRequest(deviceIndex);
  }

  _resolvePortRemoteDeviceSystemInfoResponse(completedDeviceIndex, response) {
    if (this._isPortRemoteDeviceSystemInfoInvalidationGraceActive(completedDeviceIndex)) {
      return { isPortDirectResponse: true, shouldApply: false };
    }

    const pending = this._portRemoteDeviceSystemInfoRequestsByIndex?.[completedDeviceIndex];
    if (!pending || this.robot !== pending.robot) {
      return { isPortDirectResponse: false, shouldApply: false };
    }

    const currentGeneration = this._portRemoteDeviceSystemInfoGenerationByIndex?.[completedDeviceIndex] ?? pending.requestGeneration;
    if (pending.requestGeneration !== currentGeneration) {
      return { isPortDirectResponse: true, shouldApply: false };
    }

    const portIndex = completedDeviceIndex - REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START;
    const currentType = this.store.getState().robotData?.electronics?.[portIndex]?.electronicType ?? null;
    if (currentType !== pending.expectedElectronicType) {
      return { isPortDirectResponse: true, shouldApply: false };
    }

    const responseType = response?.data?.deviceType;
    if (responseType != null && responseType !== '' && pending.expectedElectronicType != null && responseType !== pending.expectedElectronicType) {
      return { isPortDirectResponse: true, shouldApply: false };
    }

    if (!response?.success || !response?.data) {
      return { isPortDirectResponse: true, shouldApply: false };
    }

    const supersededInfo = this._getPortSupersededAccessoryInfo(completedDeviceIndex);
    const appliedPortInfo = this.store.getState().robotData?.electronics?.[portIndex]?.info;
    const portInfoStillCleared = appliedPortInfo == null || typeof appliedPortInfo !== 'object' || Object.keys(appliedPortInfo).length === 0;
    if (portInfoStillCleared && supersededInfo && this._portIndexedResponseMatchesAccessoryInfo(response.data, supersededInfo)) {
      const rejectWave = this._portRemoteDeviceSystemInfoSupersededRejectWaveByIndex?.[completedDeviceIndex];
      const allowIdenticalReplacement = this._portRemoteDeviceSystemInfoAllowIdenticalReplacementByIndex?.[completedDeviceIndex] === true;
      if (rejectWave != null && !allowIdenticalReplacement) {
        return { isPortDirectResponse: true, shouldApply: false };
      }
    }

    return { isPortDirectResponse: true, shouldApply: true };
  }

  _bufferPortRemoteDeviceSystemInfoResponse(deviceIndex, response) {
    if (!this._portRemoteDeviceSystemInfoBufferedResponsesByIndex) {
      this._portRemoteDeviceSystemInfoBufferedResponsesByIndex = {};
    }
    if (!this._portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex) {
      this._portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex = {};
    }
    this._portRemoteDeviceSystemInfoBufferedResponsesByIndex[deviceIndex] = response;
    const existingTimerId = this._portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex[deviceIndex];
    if (existingTimerId != null) {
      clearTimeout(existingTimerId);
    }
    this._portRemoteDeviceSystemInfoBufferedResponseFlushTimerByIndex[deviceIndex] = setTimeout(() => {
      this._flushPortRemoteDeviceSystemInfoBufferedResponse(deviceIndex);
    }, PORT_REMOTE_DEVICE_SYSTEM_INFO_RESPONSE_COALESCE_MS);
  }

  _flushPortRemoteDeviceSystemInfoBufferedResponse(deviceIndex) {
    const response = this._portRemoteDeviceSystemInfoBufferedResponsesByIndex?.[deviceIndex];
    this._clearPortRemoteDeviceSystemInfoBufferedResponse(deviceIndex);
    if (!response) {
      return;
    }

    const resolution = this._resolvePortRemoteDeviceSystemInfoResponse(deviceIndex, response);
    if (!resolution.shouldApply) {
      return;
    }

    const pending = this._portRemoteDeviceSystemInfoRequestsByIndex?.[deviceIndex];
    if (pending?.timeoutId != null) {
      clearTimeout(pending.timeoutId);
    }
    if (this._portRemoteDeviceSystemInfoRequestsByIndex) {
      delete this._portRemoteDeviceSystemInfoRequestsByIndex[deviceIndex];
    }

    this._applyPortRemoteDeviceSystemInfoResponse(deviceIndex, response);
  }

  _applyPortRemoteDeviceSystemInfoResponse(completedDeviceIndex, response) {
    if (!response.success || !response.data) {
      return;
    }

    const device = response.data;
    this._applyRemoteDeviceSystemInfo(device, { preserveExistingPortInfo: true });
    if (!this._portRemoteDeviceSystemInfoAttemptCompleteByIndex) {
      this._portRemoteDeviceSystemInfoAttemptCompleteByIndex = {};
    }
    const portIndex = completedDeviceIndex - REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START;
    const appliedInfo = this.store.getState().robotData?.electronics?.[portIndex]?.info;
    if (!this._isPortAccessoryInfoIncomplete(appliedInfo)) {
      this._portRemoteDeviceSystemInfoAttemptCompleteByIndex[completedDeviceIndex] = true;
    } else {
      delete this._portRemoteDeviceSystemInfoAttemptCompleteByIndex[completedDeviceIndex];
    }
    if (this._portRemoteDeviceSystemInfoSupersededInfoByIndex) {
      delete this._portRemoteDeviceSystemInfoSupersededInfoByIndex[completedDeviceIndex];
    }
    if (this._portRemoteDeviceSystemInfoSupersededRejectWaveByIndex) {
      delete this._portRemoteDeviceSystemInfoSupersededRejectWaveByIndex[completedDeviceIndex];
    }
    if (this._portRemoteDeviceSystemInfoAllowIdenticalReplacementByIndex) {
      delete this._portRemoteDeviceSystemInfoAllowIdenticalReplacementByIndex[completedDeviceIndex];
    }
  }

  _isPortRemoteDeviceSystemInfoIndex(deviceIndex) {
    return Number.isInteger(deviceIndex) && deviceIndex >= REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START;
  }

  _isPortAccessoryInfoIncomplete(info) {
    if (info == null || typeof info !== 'object') {
      return true;
    }
    return !info.devicePartNumber || !info.firmwareBootloader || !info.firmwareMainApp || !info.formattedUid;
  }

  _hasPendingPortRemoteDeviceSystemInfoRequest(requestRobot = this.robot) {
    if (!requestRobot || !this._portRemoteDeviceSystemInfoRequestsByIndex) {
      return false;
    }
    return Object.values(this._portRemoteDeviceSystemInfoRequestsByIndex).some((pending) => pending?.robot === requestRobot);
  }

  _requestPortRemoteDeviceSystemInfoForConnectedPorts(portTypes, options = {}) {
    const { force = false } = options;
    if (!this.robot?.hasCommand?.('getRemoteDeviceSystemInfoByIndex')) {
      return;
    }
    if (this._remoteDeviceSystemInfoIndexScanActive || this._hasPendingPortRemoteDeviceSystemInfoRequest(this.robot)) {
      return;
    }
    const electronics = this.store.getState().robotData?.electronics;
    const connectedMask = this._lastElectronicsPortConnectedMask;
    const lastRequestedDeviceIndex = this._portRemoteDeviceSystemInfoLastRequestedIndex;
    const startPort =
      Number.isInteger(lastRequestedDeviceIndex) &&
      lastRequestedDeviceIndex >= REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START &&
      lastRequestedDeviceIndex < REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START + 8
        ? lastRequestedDeviceIndex - REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START + 2
        : 1;
    for (let offset = 0; offset < 8; offset++) {
      const port = ((startPort - 1 + offset) % 8) + 1;
      const electronicType = portTypes[port - 1];
      if (electronicType == null) {
        continue;
      }
      if (connectedMask != null && ((connectedMask >>> (port - 1)) & 1) !== 1) {
        continue;
      }
      const deviceIndex = REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START + port - 1;
      const existingInfo = electronics?.[port - 1]?.info;
      if (!force && !this._isPortAccessoryInfoIncomplete(existingInfo)) {
        continue;
      }
      if (!force && this._portRemoteDeviceSystemInfoAttemptCompleteByIndex?.[deviceIndex]) {
        continue;
      }
      if (this._portRemoteDeviceSystemInfoRequestsByIndex?.[deviceIndex]?.robot === this.robot) {
        continue;
      }
      this.getRemoteDeviceSystemInfoByIndex(deviceIndex, { trackPortRequest: true, expectedElectronicType: electronicType });
      return;
    }
  }

  getNonPortRemoteDeviceSystemInfo() {
    if (this._remoteDeviceSystemInfoIndexScanActive || this._hasPendingPortRemoteDeviceSystemInfoRequest(this.robot)) {
      return;
    }
    this._beginBackgroundRemoteDeviceSystemInfoIndexScan();
  }

  _clearRemoteDeviceSystemInfoIndexScan() {
    if (this._remoteDeviceSystemInfoIndexScanTimeoutId !== null) {
      clearTimeout(this._remoteDeviceSystemInfoIndexScanTimeoutId);
      this._remoteDeviceSystemInfoIndexScanTimeoutId = null;
    }
    this._remoteDeviceSystemInfoIndexScanActive = false;
    this._remoteDeviceSystemInfoIndexScanAwaitingIndex = null;
    this._remoteDeviceSystemInfoIndexScanRobot = null;
  }

  /**
   * After connect, requests getRemoteDeviceSystemInfoByIndex(0..REMOTE_DEVICE_SYSTEM_INFO_NON_PORT_INDEX_MAX) sequentially.
   * Started via microtask so onEnterReady / connectRobot return immediately; each step waits for the prior response.
   */
  _beginBackgroundRemoteDeviceSystemInfoIndexScan() {
    if (!this.robot?.hasCommand?.('getRemoteDeviceSystemInfoByIndex')) {
      return;
    }
    const requestRobot = this.robot;
    this._clearRemoteDeviceSystemInfoIndexScan();
    this._remoteDeviceSystemInfoIndexScanActive = true;
    this._remoteDeviceSystemInfoIndexScanRobot = requestRobot;
    this._remoteDeviceSystemInfoIndexScanGeneration += 1;
    const generation = this._remoteDeviceSystemInfoIndexScanGeneration;
    queueMicrotask(() => this._runRemoteDeviceSystemInfoIndexScanStep(0, generation));
  }

  _canRunRemoteDeviceSystemInfoIndexScan() {
    return canQueryRemoteDeviceSystemInfoInState(this.state) || this.state === 'updatingFirmware';
  }

  _runRemoteDeviceSystemInfoIndexScanStep(index, generation = this._remoteDeviceSystemInfoIndexScanGeneration) {
    if (!this._remoteDeviceSystemInfoIndexScanActive) {
      return;
    }
    if (generation !== this._remoteDeviceSystemInfoIndexScanGeneration) {
      return;
    }
    if (!this._canRunRemoteDeviceSystemInfoIndexScan() || !this.robot || this.robot !== this._remoteDeviceSystemInfoIndexScanRobot) {
      this._clearRemoteDeviceSystemInfoIndexScan();
      return;
    }
    if (index > REMOTE_DEVICE_SYSTEM_INFO_NON_PORT_INDEX_MAX) {
      this._clearRemoteDeviceSystemInfoIndexScan();
      return;
    }
    this._remoteDeviceSystemInfoIndexScanAwaitingIndex = index;
    this.getRemoteDeviceSystemInfoByIndex(index);
    this._remoteDeviceSystemInfoIndexScanTimeoutId = setTimeout(() => {
      this._onRemoteDeviceSystemInfoIndexScanTimeout(index, generation);
    }, getRemoteDeviceSystemInfoIndexScanTimeoutMs(this.state));
  }

  _onRemoteDeviceSystemInfoIndexScanTimeout(index, generation) {
    if (!this._remoteDeviceSystemInfoIndexScanActive) {
      return;
    }
    if (generation !== this._remoteDeviceSystemInfoIndexScanGeneration) {
      return;
    }
    if (this._remoteDeviceSystemInfoIndexScanAwaitingIndex !== index) {
      return;
    }
    this._advanceRemoteDeviceSystemInfoIndexScan(index);
  }

  _advanceRemoteDeviceSystemInfoIndexScanAfterResponse(deviceIndex) {
    if (!Number.isInteger(deviceIndex)) {
      return;
    }
    this._advanceRemoteDeviceSystemInfoIndexScan(deviceIndex);
  }

  _advanceRemoteDeviceSystemInfoIndexScan(completedIndex) {
    if (!this._remoteDeviceSystemInfoIndexScanActive || this._remoteDeviceSystemInfoIndexScanAwaitingIndex === null) {
      return;
    }
    if (completedIndex !== this._remoteDeviceSystemInfoIndexScanAwaitingIndex) {
      return;
    }
    if (this._remoteDeviceSystemInfoIndexScanTimeoutId !== null) {
      clearTimeout(this._remoteDeviceSystemInfoIndexScanTimeoutId);
      this._remoteDeviceSystemInfoIndexScanTimeoutId = null;
    }
    const completed = this._remoteDeviceSystemInfoIndexScanAwaitingIndex;
    this._remoteDeviceSystemInfoIndexScanAwaitingIndex = null;
    const next = completed + 1;
    if (next > REMOTE_DEVICE_SYSTEM_INFO_NON_PORT_INDEX_MAX) {
      this._clearRemoteDeviceSystemInfoIndexScan();
      return;
    }
    const generation = this._remoteDeviceSystemInfoIndexScanGeneration;
    queueMicrotask(() => this._runRemoteDeviceSystemInfoIndexScanStep(next, generation));
  }

  getDistanceSensorSettings(port) {
    if (this.robot) {
      this.robot.getDistanceSensorSettings(port);
    }
  }

  setDistanceSensorSettings(port, threshold) {
    if (this.robot) {
      this.robot.setDistanceSensorSettings(port, threshold);
    }
  }

  getBeamBreakSensorSettings(port) {
    if (this.robot) {
      this.robot.getBeamBreakSensorSettings(port);
    }
  }

  setBeamBreakSensorSettings(port, threshold) {
    if (this.robot) {
      this.robot.setBeamBreakSensorSettings(port, threshold);
    }
  }

  getMagneticSwitchSettings(port) {
    if (this.robot) {
      this.robot.getMagneticSwitchSettings(port);
    }
  }

  getBeeAdapterSettings(port) {
    if (this.robot) {
      this.robot.getBeeAdapterSettings(port);
    }
  }

  setBeeAdapterSettings(port, output) {
    if (this.robot) {
      this.robot.setBeeAdapterSettings(port, output);
    }
  }

  resetDriveUnitOrientation() {
    if (this.robot) {
      return this.robot.resetDriveUnitOrientation();
    }
    return false;
  }

  async getMotorPlusSettings(port) {
    if (!this.robot || this.robotType !== ROBOTS.BPRDU) {
      throw new Error('Motor Plus settings are only available for Blueprint Drive Units');
    }

    const response = await this.robot.getMotorPlusSettings(port);
    if (!response) {
      throw new Error('Get Motor Plus Settings is unavailable for the current robot.');
    }
    if (!response.success || !response.data) {
      throw new Error(_getErrorForResponse(response, 'Get Motor Plus Settings'));
    }

    const portFromRobot = response.data.port;
    return {
      ...response.data,
      port: portFromRobot == null ? port : portFromRobot + 1,
    };
  }

  async setMotorPlusSettings(port, settings) {
    if (!this.robot || this.robotType !== ROBOTS.BPRDU) {
      throw new Error('Motor Plus settings are only available for Blueprint Drive Units');
    }

    const response = await this.robot.setMotorPlusSettings(port, settings);
    if (!response) {
      throw new Error('Set Motor Plus Settings is unavailable for the current robot.');
    }
    if (response?.success === false) {
      throw new Error(this._formatCommandResponseError(response, 'Set Motor Plus Settings'));
    }

    return response;
  }

  _formatCommandResponseError(response, commandName) {
    const normalizedError = _getErrorForResponse(response, commandName);
    const message = normalizedError?.message ?? `Command failed! ${commandName}`;
    const detailKeys = ['status', 'mrsp', 'commandName', 'source'];
    const details = detailKeys.flatMap((key) => (response?.[key] == null ? [] : [`${key}=${response[key]}`]));

    return details.length > 0 ? `${message}; ${details.join(', ')}` : message;
  }

  async startMotorCheck({ port, electronicType, rpm, direction }) {
    if (!this.isRobotConnected()) {
      throw new Error('No robot connected');
    }
    if (this.robotType !== ROBOTS.BPRDU) {
      throw new Error('Motor checks are only supported on Blueprint Drive Units');
    }
    if (!this.is('ready') || !this.can('doStartProgram')) {
      throw new Error('A program is already executing');
    }

    const currentSettings = await this.getMotorPlusSettings(port);
    const restoreSettings = extractMotorPlusSettingsUpdate(currentSettings);
    this._motorCheckRestoreByPort.set(port, restoreSettings);

    const targetVelocityRpm = signedRpmForDirection(rpm, direction);
    const nextSettings = buildMotorCheckRunSettings(currentSettings, targetVelocityRpm);

    await this.setMotorPlusSettings(port, nextSettings);
  }

  async stopMotorCheck(port) {
    if (!this.robot || this.robotType !== ROBOTS.BPRDU) {
      return;
    }

    const restoreSettings = this._motorCheckRestoreByPort.get(port);
    const stopTemplate = restoreSettings ?? (await this.getMotorPlusSettings(port).catch(() => null));

    try {
      await this.setMotorPlusSettings(port, buildMotorCheckStopSettings(stopTemplate));
      if (restoreSettings) {
        await this.setMotorPlusSettings(port, buildMotorCheckRestoreSettings(restoreSettings));
      }
    } finally {
      this._motorCheckRestoreByPort.delete(port);
    }
  }

  monitoringBatteryLevel() {
    if (this.robot) {
      this.robot.getBatteryLevel();
    }
  }

  monitoringSystemState() {
    if (this.robot) {
      if (isMonitoringSystemState) {
        this._scheduleNextSystemStateMonitoringRequest();
      }
      if (this._isSystemStateResponseOwnershipAmbiguous(this.robot)) {
        return;
      }
      if (this._systemStateRequestInFlight) {
        return;
      }

      try {
        this._trackSystemStateRequest(this.robot);
      } catch (error) {
        throw error;
      }
    }
  }

  _scheduleNextSystemStateMonitoringRequest() {
    if (!isMonitoringSystemState) {
      return;
    }

    systemStateMonitorTimeout.start(() => {
      this._expireActiveSystemStateRequest(this.robot);
      this.monitoringSystemState();
    }, this._getSystemStateMonitoringIntervalMs());
  }

  stopMonitoringBatteryLevel() {
    isMonitoringBatteryLevel = false;
    batteryMonitorTimeout.stop();
  }

  stopMonitoringSystemState() {
    isMonitoringSystemState = false;
    systemStateMonitorTimeout.stop();
  }

  updatePowerState(batteryCharging, state, percentage) {
    // Use == null to catch both null and undefined (e.g. fields absent from a payload)
    if (batteryCharging == null || percentage == null) {
      return;
    }

    if (percentage <= 0 && state == null) {
      console.error('[RobotService] Ignoring invalid drive-base battery percentage', {
        batteryCharging,
        percentage,
        powerState: state ?? null,
        previousPowerState: this.powerState ?? null,
      });
      return;
    }

    let powerState = state;
    if (powerState === null) {
      powerState = this.powerState;
    }

    let powerLevel = null;
    if (percentage > BATTERY_TYPE[this.robot.robotType][3]) {
      powerLevel = BATTERY_STATES.LEVEL5;
    } else if (percentage > BATTERY_TYPE[this.robot.robotType][2]) {
      powerLevel = BATTERY_STATES.LEVEL4;
    } else if (percentage > BATTERY_TYPE[this.robot.robotType][1]) {
      powerLevel = BATTERY_STATES.LEVEL3;
    } else if (percentage > BATTERY_TYPE[this.robot.robotType][0]) {
      powerLevel = BATTERY_STATES.LEVEL2;
    } else {
      powerLevel = BATTERY_STATES.LEVEL1;
    }

    const powerDisplayChanged = this.batteryCharging !== batteryCharging || this.powerLevel !== powerLevel || this.powerState !== powerState;
    const batteryPercentageChanged = this.batteryPercentage !== percentage;

    if (powerDisplayChanged || batteryPercentageChanged) {
      this.batteryCharging = batteryCharging;
      this.batteryPercentage = percentage;
      this.powerLevel = powerLevel;
      this.powerState = powerState;
      this.dispatch(this.robotCommandActions.onUpdatePowerState({ batteryCharging, batteryPercentage: percentage, powerState, powerLevel }));
    }
  }

  /** *************************************************** */
  /** *************** TOY EVENT HANDLERS **************** */
  /** *************************************************** */

  _onSetHandshakeIdNotification(response) {
    try {
      // Store the handshake ID when received
      if (response.data && response.data.handshakeId !== undefined) {
        const handshakeId = response.data.handshakeId;
        const sourceId = response.source; // This is the target ID for our response
        const sequenceNumber = response.sequenceNumber;

        // Store the handshake ID
        storedHandshakeId = handshakeId;

        // Throttle sendSetHandshakeIdReply to at most once every 3 seconds for now.
        // We will need to remove this once firmware is in place and working correctly.
        const now = Date.now();
        const threeSeconds = 3000;
        if (this.lastSendApiResponseTime === null || now - this.lastSendApiResponseTime >= threeSeconds) {
          this.robot.sdkToy().sendSetHandshakeIdReply(sourceId, sequenceNumber, 0x00);
          this.lastSendApiResponseTime = now;
        }
      } else {
        console.warn('Set Handshake Id event received but no handshakeId in data');
      }
    } catch (error) {
      console.error('Error in onSetHandshakeIdNotification:', error);
    }
  }

  _onGetHandshakeIdResponseHandler(response) {
    try {
      if (response.success) {
        const sourceId = response.source; // This is the target ID for our response
        const sequenceNumber = response.sequenceNumber;
        this.robot.sdkToy().sendGetHandshakeIdReply(sourceId, sequenceNumber, 0x00, storedHandshakeId);
      }
    } catch (error) {
      console.error('Error in onGetHandshakeIdResponseHandler:', error);
    }
  }

  _onGetSkuResponseHandler(response) {
    try {
      console.log('onGetSkuResponseHandler', response);
      if (response.success) {
        if (response.data && response.data.sku !== undefined) {
          const { sku } = response.data;
          this.dispatch(this.robotDataActions.onUpdateRobotSku(sku));
        } else {
          const sourceId = response.source; // This is the target ID for our response
          const sequenceNumber = response.sequenceNumber;
          this.robot.sdkToy().sendGetSkuReply(sourceId, sequenceNumber, 0x00, BPS_SKU);
        }
      }
    } catch (error) {
      console.error('Error in onGetSkuResponseHandler:', error);
    }
  }

  _onGetBluetoothAdvertisingNameResponseHandler(response) {
    try {
      if (!response?.success) return;
      const name = response?.data?.name;
      if (typeof name === 'string' && name.length > 0) {
        this.dispatch(this.robotDataActions.onUpdateBluetoothAdvertisingName(name));
      }
    } catch (error) {
      console.error('Error in onGetBluetoothAdvertisingNameResponseHandler:', error);
    }
  }

  _onSetSkuNotificationHandler(response) {
    console.log('onSetSkuNotificationHandler', response);
    console.log('Set Sku response.data:', JSON.stringify(response.data, null, 2));
    if (response.success) {
      const { sku } = response.data;
      console.log('SKU:', sku);
    }
  }

  _onGetFuelGaugeStateResponseHandler(response) {
    if (response.success) {
      const { state, percent } = response.data;

      let batteryCharging = false;
      if (FUEL_GAUGE_STATES.charging.includes(state)) {
        batteryCharging = true;
      }

      let powerState = null;
      if (FUEL_GAUGE_STATES.low.includes(state)) {
        powerState = POWER_STATES.LOW;
      } else if (FUEL_GAUGE_STATES.critical.includes(state)) {
        powerState = POWER_STATES.CRITICAL;
      } else if (FUEL_GAUGE_STATES.ok.includes(state)) {
        powerState = POWER_STATES.OK;
      }

      this.updatePowerState(batteryCharging, powerState, percent);

      if (isMonitoringBatteryLevel) {
        batteryMonitorTimeout.stop();
        batteryMonitorTimeout.start(() => this.monitoringBatteryLevel(), this._getBatteryMonitoringIntervalMs());
      }
    } else {
      console.log('Fuel Gauge State response was not successful');

      if (isMonitoringBatteryLevel) {
        batteryMonitorTimeout.stop();
        batteryMonitorTimeout.start(() => this.monitoringBatteryLevel(), this._getBatteryMonitoringIntervalMs());
      }
    }
  }

  _onGetBoardRevisionResponseHandler(response) {
    console.log('onGetBoardRevisionResponseHandler', response);
    if (response.success && response.data) {
      const normalized = formatBoardRevision(response.data.revision);
      if (normalized !== null) {
        this.dispatch(this.robotDataActions.onUpdateRobotBoardRevision(normalized));
      }
    }
  }

  _onGetUidResponseHandler(response) {
    console.log('onGetUidResponseHandler', response);
    this.dispatch(this.robotDataActions.onUpdateRobotUid(response.data.uid));
    if (
      this._getEffectiveRobotType() === ROBOTS.BPRDU &&
      typeof this.queryRobotSlotStorage === 'function' &&
      !this._pendingConnectionMetadataBootstrap &&
      !this._deferConnectionMetadataBootstrapUntilObservedProgramEnds &&
      this._connectionMetadataBootstrapped &&
      !this._shouldDeferSlotStorageQueryForRunningProgram()
    ) {
      void this.queryRobotSlotStorage();
    }
  }

  _onGetCurrentApplicationIdResponseHandler(response) {
    console.log('onGetCurrentApplicationIdResponseHandler', response);
    if (response.success && response.data) {
      // Store the application ID - might be useful for firmware P/N or other purposes
      const appId = response.data.applicationId || response.data.id || JSON.stringify(response.data);
      this.dispatch(this.robotDataActions.onUpdateRobotCurrentApplicationId(appId));
    }
  }

  _onGetSystemStateResponseHandler(response) {
    const systemStateRequest = this._consumeSystemStateResponse(response);
    if (isMonitoringSystemState) {
      systemStateMonitorTimeout.stop();
      this._scheduleNextSystemStateMonitoringRequest();
    }
    if (!systemStateRequest || systemStateRequest.drivingDisabledWarningRunToken !== this._drivingDisabledWarningRunToken) {
      return;
    }

    const ownedWarningResponse = response;

    if (response.success && response.data) {
      const { state, drivebaseBattery, drivebaseCharging, controllerConnected, controllerConnection, controllerBattery, controllerCharging } =
        response.data;

      // Pass null for powerState: updatePowerState will fall back to this.powerState.
      // On the very first system-state response (before any fuel-gauge event), this.powerState
      // is also null and powerState stays null in Redux until a fuel-gauge event fires.
      this.updatePowerState(drivebaseCharging, null, drivebaseBattery);

      this.dispatch(this.mainAppActions.onSetControllerConnected(controllerConnected));
      // Only update when we have a value; periodic getSystemState (e.g. battery polling) may omit
      // controllerConnection and would otherwise overwrite the existing value with null.
      if (controllerConnected && controllerConnection != null) {
        const connectionType = controllerConnection === 'usb_connection' ? 1 : 2;
        this.dispatch(this.controllerDataActions.onUpdateControllerConnectionType(connectionType));

        if (controllerConnected !== lastControllerConnected || connectionType !== lastControllerConnectionType) {
          this.getNonPortRemoteDeviceSystemInfo();
        }
        lastControllerConnected = controllerConnected;
        lastControllerConnectionType = connectionType;
      } else {
        lastControllerConnected = false;
        lastControllerConnectionType = null;
      }

      let controllerBatteryLevel = null;
      if (isNotNil(controllerBattery)) {
        if (controllerBattery <= 20) {
          controllerBatteryLevel = BATTERY_STATES.LEVEL1;
        } else if (controllerBattery <= 40) {
          controllerBatteryLevel = BATTERY_STATES.LEVEL2;
        } else if (controllerBattery <= 60) {
          controllerBatteryLevel = BATTERY_STATES.LEVEL3;
        } else if (controllerBattery <= 80) {
          controllerBatteryLevel = BATTERY_STATES.LEVEL4;
        } else {
          controllerBatteryLevel = BATTERY_STATES.LEVEL5;
        }
      }

      this.dispatch(
        this.controllerDataActions.onUpdateControllerBatteryLevel({
          controllerCharging,
          controllerBatteryLevel,
          controllerBatteryPercentage: isNotNil(controllerBattery) ? controllerBattery : null,
        })
      );

      // Report overseer/firmware program state to Redux so the editor can show running program state
      const overseerState = normalizeDrivebaseSystemState(state);
      if (overseerState != null) {
        if (this._pendingConnectionMetadataBootstrap && this._getEffectiveRobotType() === ROBOTS.BPRDU) {
          if (isDrivebaseRuntimeBusyForSlotStorageDeferral(overseerState)) {
            this._pendingConnectionMetadataBootstrap = false;
            this._deferConnectionMetadataBootstrapUntilObservedProgramEnds = true;
          } else {
            this._runConnectionMetadataBootstrapIfReady();
          }
        }

        const previousOverseerProgramState = this._getRuntimeProgramState();
        this.dispatch(this.robotCommandActions.onUpdateRuntimeProgramState(overseerState));

        // Reconcile local program state with DU-reported runtime state.
        const localProgramState = this.store.getState().project.programState;
        if (isDrivebaseSystemStateIdle(overseerState) && this._locallyStartedProgramThisConnection && !this._awaitingLocalRunOverseerExecution) {
          this._localRunOverseerIdleSeen = true;
        }
        if (localProgramState === RobotActions.PROGRAM.STOPPED && this._locallyStartedProgramThisConnection) {
          if (this._localRunStoppedAt == null) {
            this._localRunStoppedAt = performance.now();
          }
        } else if (localProgramState !== RobotActions.PROGRAM.STOPPED) {
          this._localRunStoppedAt = null;
        }
        if (
          localProgramState === RobotActions.PROGRAM.STOPPED &&
          this._locallyStartedProgramThisConnection &&
          !this._isProgramStoppingState() &&
          (isDrivebaseSystemStateIdle(overseerState) ||
            (this.is('ready') &&
              this._localRunStoppedAt != null &&
              performance.now() - this._localRunStoppedAt >= LOCAL_PROGRAM_OVERSEER_STARTUP_GRACE_MS))
        ) {
          // Local run fully ended; allow a later DU-initiated program to sync as external.
          this._clearLocalRunTracking();
        }
        if (isDrivebaseSystemStateExecutingPython(overseerState) && this._locallyStartedProgramThisConnection) {
          this._awaitingLocalRunOverseerExecution = false;
          if (!this._localRunOverseerExecutionEstablished) {
            this._localRunOverseerExecutionEstablished = true;
          } else if (previousOverseerProgramState != null && !isDrivebaseSystemStateExecutingPython(previousOverseerProgramState)) {
            this._localRunOverseerExecutionReentrySeen = true;
            this._noteLocalRunExecutionBoundary();
          }
        } else if (
          this._awaitingLocalRunOverseerExecution &&
          this._localRunStartedAt != null &&
          performance.now() - this._localRunStartedAt > LOCAL_PROGRAM_OVERSEER_STARTUP_GRACE_MS
        ) {
          this._awaitingLocalRunOverseerExecution = false;
        }
        const isProgramFsmRunning = this._isProgramFsmRunning();
        if (
          isDrivebaseSystemStateExecutingPython(overseerState) &&
          this._locallyStartedProgramThisConnection &&
          !this._isProgramStoppingState() &&
          !isProgramFsmRunning &&
          (localProgramState === RobotActions.PROGRAM.STOPPED || localProgramState === RobotActions.PROGRAM.STOP)
        ) {
          // BPS finished stopping but the DU started (or resumed) Python without an overseer idle
          // poll in between — release local attachment so observed-external sync can run.
          this._clearLocalRunTracking();
        }
        if (this._shouldObserveExecutingPythonIdentityForLocalRun(overseerState, localProgramState, isProgramFsmRunning)) {
          void this._observeExecutingPythonIdentityForLocalRun();
        }
        if (this._shouldHandoffLocalRunToObservedExternal(overseerState, localProgramState, isProgramFsmRunning)) {
          // Overseer reported idle since the local run started, or the local FSM finished stopping
          // while overseer stayed on executing_python — hand off to an observed external run.
          void this._handoffLocalRunToObservedExternal({ systemStateResponse: ownedWarningResponse });
        }
        const isLocalRunning = this._isLocallyRunningProgram(localProgramState, isProgramFsmRunning);
        const canSyncObservedExternalRunningProgram = this._canSyncObservedExternalRunningProgram(
          overseerState,
          localProgramState,
          isProgramFsmRunning
        );

        if (canSyncObservedExternalRunningProgram) {
          this._syncObservedExternalRunningProgram({ systemStateResponse: ownedWarningResponse });
        } else if (isDrivebaseSystemStateExecutingPython(overseerState)) {
          this._awaitObservedRuntimeReconfirmation = false;
        } else if (isDrivebaseSystemStateIdle(overseerState)) {
          this._observedExternalRunningProgramSynced = false;
          this._runningProgramNotificationContextRequestToken += 1;
          const canReconcileLocalStopOnIdle =
            isLocalRunning &&
            this._locallyStartedProgramThisConnection &&
            (isDrivebasePythonProgramBusy(previousOverseerProgramState) || !this._awaitingLocalRunOverseerExecution);
          const canReconcileObservedStopOnIdle =
            (isDrivebasePythonProgramBusy(previousOverseerProgramState) || isDrivebaseDriving(previousOverseerProgramState)) &&
            localProgramState !== RobotActions.PROGRAM.STOP &&
            localProgramState !== RobotActions.PROGRAM.STOPPED &&
            (this._isObservedProgramExecutingState() || this._observedExternalRunningProgramSynced);
          // During startup, idle is expected until executePythonFile takes effect; defer STOP
          // until overseer confirms execution or the startup grace window expires.
          if (canReconcileLocalStopOnIdle || canReconcileObservedStopOnIdle) {
            if (canReconcileObservedStopOnIdle) {
              this._awaitObservedRuntimeReconfirmation = true;
            }
            this.dispatch(
              this.programActions.onUpdateProgramState({
                // Use STOP (not STOPPED) so watcher/state-machine runs full stop flow
                // and transitions out of programStarting/programExecuting safely.
                programState: RobotActions.PROGRAM.STOP,
              })
            );
          }
        }

        this._maybeShowDrivingDisabledWarningForActiveProgram(response, isProgramFsmRunning, systemStateRequest?.drivingDisabledWarningRunToken);

        const shouldResumeDeferredConnectionBootstrapOnIdle =
          this._getEffectiveRobotType() === ROBOTS.BPRDU && this._deferConnectionMetadataBootstrapUntilObservedProgramEnds;

        if (isDrivebaseSystemStateIdle(overseerState) && this._getEffectiveRobotType() === ROBOTS.BPRDU) {
          if (shouldResumeDeferredConnectionBootstrapOnIdle) {
            void this._resumeDeferredConnectionMetadataBootstrapAfterObservedProgramEnds();
          } else {
            const shouldRetryFailedConnectMetadataQuery = this._deferSlotStorageQueryUntilObservedProgramEnds !== true;
            // Only query after the program ends when connect-time metadata was deferred.
            const didKickDeferredSlotStorageQuery = this._fulfillDeferredSlotStorageQueryIfNeeded();
            if (
              !didKickDeferredSlotStorageQuery &&
              shouldRetryFailedConnectMetadataQuery &&
              !this._observedProgramEndedSlotStorageQueryAttempted &&
              !this._slotStorageMetadataLoadedForConnection &&
              this._slotStorageRecoveryNeeded() &&
              typeof this.queryRobotSlotStorage === 'function'
            ) {
              this._observedProgramEndedSlotStorageQueryAttempted = true;
              void this.queryRobotSlotStorage();
            }
          }

          this._startDeferredBackgroundFirmwareDownload();
          this._startDeferredCriticalFirmwareDownload();
          this._startDeferredFirmwareFinalization();
        }
      }
    }
  }

  _onSetPersistentOptionsResponseHandler(response) {
    if (this.is('querying')) {
      btConnectionTimeout.stop();
      if (!response.success) {
        this.disconnect(_getErrorForResponse(response, 'Set Persistent Options'));
        return;
      }
      btConnectionTimeout.start(() => this.onBtConnectionTimeout('Get Battery Info persistent options'));
    }
  }

  _getActiveControlSystemId(response) {
    const id = getControllID(response.data.controllerId);
  }

  _onConsoleStringNotificationHandler(response) {
    console.log('Console String Notification: ', response);
    if (response && response.success && response.data && response.data.consoleString) {
      this._syncObservedExternalRunningProgramFromActivity();
      this.dispatch(this.programActions.onAddConsoleDisplay(response.data.consoleString));
    }
  }

  _onPythonPrintNotificationHandler(response) {
    if (response && response.success && response.data && response.data.text) {
      this._syncObservedExternalRunningProgramFromActivity();
      const chunk = String(response.data.text);
      const combined = (this.pythonPrintBuffer || '') + chunk;
      const parts = combined.split(/\r?\n/);
      // Last segment is incomplete text or '' after a trailing newline — never emit it as a full line.
      const completeLines = parts.slice(0, -1);
      const tail = parts.length > 0 ? parts[parts.length - 1] : '';
      this._dispatchPythonPrintLines(completeLines);
      this.pythonPrintBuffer = tail;
    }
  }

  _onGetLibraryUpdaterStateDeprecatedResponseHandler(response) {
    console.log('onGetLibraryUpdaterStateDeprecatedResponseHandler', response);
  }

  _onGetLibraryUpdaterProgressResponseHandler(response) {
    console.log('onGetLibraryUpdaterProgressResponseHandler', response);
  }

  _onGetMotorPlusSettingsResponseHandler(response) {
    // Motor Plus responses are handled by per-call promises in `robot-interface`.
    void response;
  }

  _onSetMotorPlusSettingsResponseHandler(response) {
    // Motor Plus responses are handled by per-call promises in `robot-interface`.
    void response;
  }

  /**
   * Parses dataPayload into typed values using streamedItemDefinitions.
   * Wire sizes: bool/electronic_bool = 1 byte (0/1), electronic_uint8 = 1 byte (raw),
   * int = int32 BE (4 bytes), float/sensor_float/electronic_float = float32 BE (4 bytes).
   */
  _parseStreamVariablePayload(startingIndex, dataPayload) {
    if (!Array.isArray(dataPayload) || dataPayload.length < 1) return [];
    const toU8 = (b) => (b < 0 ? b + 256 : b) & 0xff;
    const uint8 = new Uint8Array(dataPayload.map(toU8));
    const view = new DataView(uint8.buffer, uint8.byteOffset, uint8.byteLength);
    const defs = this.streamedItemDefinitions ?? [];
    const parsed = [];
    // Defect B guard: the wire format has NO per-value delimiters; each value's width is inferred
    // purely from streamedItemDefinitions. If those defs are stale/missing/out-of-order vs the live
    // payload, the bytes misalign and every value past the discrepancy is garbage that still passes
    // Number.isFinite and gets plotted. Track the conditions that mean the defs don't match the
    // payload so we can reject the frame instead of emitting garbage.
    let hasMissingDef = false;
    let hasUnknownType = false;
    let byteOffset = 0;
    while (byteOffset < uint8.length) {
      const defIndex = (startingIndex & 0xff) + parsed.length;
      const def = defs[defIndex];
      const micropythonType = def?.micropythonType;
      if (def == null) hasMissingDef = true;
      else if (!STREAM_KNOWN_MICROPYTHON_TYPES.has(micropythonType)) hasUnknownType = true;
      if (isOneByteMicropythonStreamType(micropythonType)) {
        parsed.push(parseMicropythonStreamValue(view, uint8, byteOffset, micropythonType));
        byteOffset += 1;
        continue;
      }
      if (byteOffset + 4 > uint8.length) break;
      parsed.push(parseMicropythonStreamValue(view, uint8, byteOffset, micropythonType));
      byteOffset += 4;
    }

    // Reject the frame when cached defs don't line up with the payload, then re-request definitions
    // (debounced) so subsequent frames parse correctly. While definitions are still empty/pending,
    // keep the legacy float-width fallback so early stream frames aren't dropped before names load.
    //   - leftover bytes -> payload didn't align to def boundaries (byte misalignment)
    //   - missing def    -> defs/stream out of sync (undefined is a hard error, NOT a 4-byte float)
    //   - unknown type   -> defs incomplete (e.g. resync left a 'unknown' type fallback)
    const leftoverBytes = uint8.length - byteOffset;
    const hasCachedDefinitions = defs.length > 0;
    if (hasCachedDefinitions && (leftoverBytes !== 0 || hasMissingDef || hasUnknownType)) {
      this._requestPythonStreamDefinitionsResync();
      return [];
    }
    return parsed;
  }

  /**
   * Defect B recovery: re-request the python stream item definitions after the parser rejects a
   * frame whose bytes don't align to the cached definitions. Debounced so a burst of corrupt frames
   * triggers at most one resync per cooldown, and a fire-and-forget no-op when streaming isn't
   * active or definitions can't be queried (e.g. unit tests with no connected robot).
   */
  _requestPythonStreamDefinitionsResync() {
    try {
      if (!this.robot || typeof this._syncPythonStreamDefinitionsFromRobot !== 'function') return;
      if (this._hasTrustedCommittedPythonStreamDefinitionsForRun()) return;
      const now = typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : 0;
      if (this._pythonStreamDefsResyncInFlight) return;
      if (this._lastPythonStreamDefsResyncAt != null && now - this._lastPythonStreamDefsResyncAt < PYTHON_STREAM_DEFS_RESYNC_COOLDOWN_MS) {
        return;
      }
      this._pythonStreamDefsResyncInFlight = true;
      this._lastPythonStreamDefsResyncAt = now;
      Promise.resolve(this._syncPythonStreamDefinitionsFromRobot())
        .catch(() => undefined)
        .finally(() => {
          this._pythonStreamDefsResyncInFlight = false;
        });
    } catch {
      this._pythonStreamDefsResyncInFlight = false;
    }
  }

  /**
   * Gets a value from keyValues by key, case-insensitive (e.g. "Pitch" or "pitch" both match).
   */
  _getKeyValue(keyValues, key) {
    const normalize = (s) =>
      String(s ?? '')
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const target = normalize(key);
    const k = Object.keys(keyValues).find((x) => normalize(x) === target);
    return k != null ? keyValues[k] : undefined;
  }

  /**
   * Builds SensorData from parsed Python stream values for dashboard/persistence.
   * Uses streamedItemDefinitions for variable names; falls back to index-based keys if missing.
   */
  _buildSensorDataFromPythonStream(startingIndex, parsedValues) {
    const defs = this.streamedItemDefinitions ?? [];
    const keyValues = {};
    parsedValues.forEach((value, i) => {
      const def = defs[(startingIndex & 0xff) + i];
      const key = def?.name ?? `value_${i}`;
      const parsed = typeof value === 'number' ? value : parseFloat(value);
      if (typeof parsed === 'number' && Number.isFinite(parsed)) {
        keyValues[key] = parsed;
      } else {
        keyValues[key] = null;
      }
    });

    // Drive-unit position stream is meters; BPS field/charts/CSV assume cm. Scale once at ingest.
    for (const name of ['Position X', 'Position Y']) {
      if (Object.prototype.hasOwnProperty.call(keyValues, name)) {
        const v = keyValues[name];
        if (typeof v === 'number' && Number.isFinite(v)) {
          keyValues[name] = v * PYTHON_STREAM_POSITION_METERS_TO_CM;
        }
      }
    }

    if (!Number.isFinite(this.startTime)) {
      return null;
    }

    const elapsedTime = (Math.round(performance.now()) - this.startTime) * 10;
    const adjustedTime = elapsedTime / 10000;

    return {
      time: elapsedTime,
      pythonStream: { time: adjustedTime, ...keyValues },
    };
  }

  _onPythonStreamVariableNotificationHandler(response) {
    if (response && response.success && response.data) {
      this._syncObservedExternalRunningProgramFromActivity();
      if (!Number.isFinite(this.startTime)) {
        return;
      }

      const { startingIndex, dataPayload } = response.data;

      if (Array.isArray(dataPayload) && dataPayload.length >= 1) {
        const parsed = this._parseStreamVariablePayload(startingIndex, dataPayload);
        const sensorData = this._buildSensorDataFromPythonStream(startingIndex, parsed);
        if (!sensorData) return;
        const stream = sensorData.pythonStream;
        if (!this._recordLiveStreamSampleTime(stream)) return;
        if (!Object.keys(stream).some((k) => k !== 'time')) {
          return;
        }
        const runData = addDataToSensorRun(sensorData);
        this._dispatchLiveSensorData(runData || sensorData);
      }
    }
  }

  // Not actually used anywhere at the moment, but keeping it here in case we need it.
  _onGetNumberOfStreamedItemsResponseHandler(response) {
    if (response.success && response.data) {
      this.numberOfStreamedItems = response.data.numberOfStreamItems;
    }
  }

  // Not actually used anywhere at the moment, but keeping it here in case we need it.
  _onGetStreamedItemDataResponseHandler(response) {
    if (response.success && response.data) {
      this.streamedItemData = response.data.dataPayload;
      this.streamedItemName = response.data.name;
      this.streamedItemType = response.data.micropythonType;
      this.streamedItemStartingIndex = response.data.startingIndex;
      this.streamedItemDataPayload = response.data.dataPayload;
    }
    // turn on streaming (need to wait until all items are received)
    if (this.robot && this.robot.enablePythonStreaming && this.streamedItemDefinitions.length > 0) {
      this.robot.enablePythonStreaming(true);
    }
  }

  _onGetRemoteDeviceTypesResponseHandler(response) {
    if (!this.robot || this._robotDisconnectReleased || this._remoteDeviceTypesTimeoutBlockedRobot === this.robot) {
      return;
    }

    const sequenceNumber = response?.sequenceNumber;
    if (!isValidRemoteDeviceTypesSequenceNumber(sequenceNumber)) {
      return;
    }

    const request = this._remoteDeviceTypesActiveRequest;
    if (!this._isCurrentRemoteDeviceTypesRequest(request)) {
      return;
    }

    if (request.handlePending) {
      if (request.bufferedResponses.length >= REMOTE_DEVICE_TYPES_PRE_HANDLE_RESPONSE_BUFFER_LIMIT) {
        request.bufferedResponses.shift();
      }
      request.bufferedResponses.push(response);
      return;
    }

    if (request.expectedSequence !== sequenceNumber) {
      return;
    }

    this._processOwnedRemoteDeviceTypesResponse(request, response);
  }

  _processOwnedRemoteDeviceTypesResponse(request, response) {
    if (!this._isCurrentRemoteDeviceTypesRequest(request) || request.expectedSequence !== response.sequenceNumber) {
      return;
    }

    request.awaitingResponse = false;
    request.expectedSequence = null;
    if (!response.success || !response.data) {
      if (request.kind === 'one-shot') {
        this._completeRemoteDeviceTypesRequest({ error: new Error(REMOTE_DEVICE_TYPES_REQUEST_ERROR) });
        return;
      }
      if (this._remoteDeviceTypesOneShotQueue.length > 0) {
        this._completeRemoteDeviceTypesRequest({ resumeBackground: true });
        return;
      }
      this._clearRemoteDeviceTypesTimer();
      this._scheduleRemoteDeviceTypesTimer(REMOTE_DEVICE_TYPES_RETRY_DELAY_MS, request, 'retry');
      return;
    }
    this._clearRemoteDeviceTypesTimer();
    const data = response.data;
    const portTypes = [];
    for (let i = 1; i <= 8; i++) {
      const raw = data[`deviceTypePort${i}`];
      const type = raw == null || raw === '' || raw === 'none' ? null : String(raw);
      portTypes.push(type);
    }
    const prev = this._lastElectronicsPortTypes;
    const changed = prev == null || portTypes.length !== prev.length || portTypes.some((t, i) => t !== prev[i]);
    const connectedFlag = Number(data.connectedFlag);
    const connectedMask = Number.isFinite(connectedFlag) ? Math.trunc(connectedFlag) >>> 0 : null;
    const connectedPortTypes =
      connectedMask == null ? portTypes : portTypes.map((type, index) => (((connectedMask >>> index) & 1) === 1 ? type : null));
    let deferPortSystemInfoRequests = false;
    if (changed) {
      if (prev != null) {
        deferPortSystemInfoRequests =
          this._invalidatePortRemoteDeviceSystemInfoForChangedPorts(prev, portTypes, this._lastElectronicsPortConnectedMask, connectedMask) === true;
      }
      this._lastElectronicsPortTypes = portTypes.slice();
      this.getRobotFirmwareVersions(false, true);
    } else if (connectedMask != null && this._lastElectronicsPortConnectedMask != null && connectedMask !== this._lastElectronicsPortConnectedMask) {
      deferPortSystemInfoRequests =
        this._invalidatePortRemoteDeviceSystemInfoForChangedPorts(prev, portTypes, this._lastElectronicsPortConnectedMask, connectedMask) === true;
      this.getRobotFirmwareVersions(false, true);
    }
    if (connectedMask != null) {
      this._lastElectronicsPortConnectedMask = connectedMask;
    }
    this.remoteDeviceTypes = connectedPortTypes.map((electronicType) => ({ electronicType, info: {} }));
    this.dispatch(this.robotDataActions.onUpdateElectronicsPortTypes(connectedPortTypes));
    this._clearRemoteDeviceTypesBackgroundRecovery();
    if (!deferPortSystemInfoRequests) {
      this._requestPortRemoteDeviceSystemInfoForConnectedPorts(connectedPortTypes, { force: changed });
    }

    // Bit mask: bit i set => port i+1 is 1 = true, 0 = false
    const updateFlag = Number(data.updateFlag);
    if (Number.isFinite(updateFlag) && Number.isFinite(connectedFlag)) {
      // I suspect Math.trunc is unnecessary here, but it's here to be safe.
      const updateMask = Math.trunc(updateFlag) >>> 0;
      const connectedMask = Math.trunc(connectedFlag) >>> 0;
      for (let port = 1; port <= 8; port++) {
        const updateInProgress = (updateMask >>> (port - 1)) & 1;
        const isConnected = (connectedMask >>> (port - 1)) & 1;
        const deviceIndex = REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START + port - 1;
        const deviceType = data[`deviceTypePort${port}`];
        const progress = Number(data[`updateProgressPort${port}`]);
        this.dispatch(
          this.robotDataActions.onUpdateFirmwareInstallProgress(
            this._normalizeFirmwareInstallProgress(deviceIndex, deviceType, isConnected, updateInProgress, progress)
          )
        );
      }
    }
    this._completeRemoteDeviceTypesRequest({ connectedPortTypes });
  }

  _onGetRemoteDeviceSystemInfoByIndexResponseHandler(response) {
    const completedDeviceIndex = response?.data?.deviceIndex;
    const isPortIndexedDevice = this._isPortRemoteDeviceSystemInfoIndex(completedDeviceIndex);
    const isCurrentScanRobotResponse =
      this._remoteDeviceSystemInfoIndexScanActive && this.robot && this.robot === this._remoteDeviceSystemInfoIndexScanRobot;
    const isAwaitedActiveScanResponse = isCurrentScanRobotResponse && completedDeviceIndex === this._remoteDeviceSystemInfoIndexScanAwaitingIndex;

    if (isPortIndexedDevice) {
      const portResponseResolution = this._resolvePortRemoteDeviceSystemInfoResponse(completedDeviceIndex, response);
      if (!portResponseResolution.shouldApply) {
        return;
      }
      this._bufferPortRemoteDeviceSystemInfoResponse(completedDeviceIndex, response);
      return;
    }

    if (!isCurrentScanRobotResponse) {
      return;
    }
    if (!response.success || !response.data) {
      return;
    }

    const device = response.data;
    this._applyRemoteDeviceSystemInfo(device, { preserveExistingPortInfo: false });
    if (isAwaitedActiveScanResponse) {
      this._advanceRemoteDeviceSystemInfoIndexScanAfterResponse(completedDeviceIndex);
    }
  }

  _normalizeFirmwareInstallProgress(deviceIndex, deviceType, isConnected, updateInProgress, updateProgress) {
    if (deviceType == null) {
      return {
        deviceIndex,
        deviceType: null,
        progress: 0,
        updating: false,
      };
    }

    // Never show progress for bpr_bps_software, that's us.
    if (deviceType == 'bpr_bps_software') {
      return {
        deviceIndex,
        deviceType,
        progress: 0,
        updating: false,
      };
    }

    return {
      deviceIndex,
      deviceType: deviceType ?? null,
      progress: updateProgress,
      updating: updateInProgress,
    };
  }

  /**
   * Out-of-range contract for distance/beam-break sensors. The firmware reports +Infinity when
   * nothing is in range; a missing reading (null/undefined) is likewise "no object," NOT 0 m (which
   * falsely reads as "object touching the sensor"). Collapse those cases to the canonical
   * out-of-range sentinel so the UI does not report a fabricated measured distance.
   */
  _normalizeDistanceReading(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
  }

  _onGetDistanceSensorSettingsResponseHandler(response) {
    if (!response.success || !response.data) return;
    const data = response.data;
    const portFromRobot = data.port;
    if (portFromRobot == null) return;
    // Robot uses 0-based port (0-7); UI uses 1-based (1-8). Store with 1-based key.
    const port = portFromRobot + 1;
    const normalizedDistance = this._normalizeDistanceReading(data.distance);
    this.dispatch(
      this.robotDataActions.onUpdateDistanceSensorData({
        port,
        data: {
          port,
          distance: normalizedDistance,
          objectPresent: data.objectPresent ?? false,
          distanceThreshold: data.distanceThreshold ?? 0,
        },
      })
    );
  }

  _onGetBeamBreakSensorSettingsResponseHandler(response) {
    if (!response.success || !response.data) return;
    const data = response.data;
    const portFromRobot = data.port;
    if (portFromRobot == null) return;
    // Robot uses 0-based port (0-7); UI uses 1-based (1-8). Store with 1-based key.
    const port = portFromRobot + 1;
    const normalizedDistance = this._normalizeDistanceReading(data.distance);
    this.dispatch(
      this.robotDataActions.onUpdateBeamBreakSensorData({
        port,
        data: {
          port,
          distance: normalizedDistance,
          objectPresent: data.objectPresent ?? false,
          distanceThreshold: data.distanceThreshold,
        },
      })
    );
  }

  _onGetMagneticSwitchSettingsResponseHandler(response) {
    if (!response.success || !response.data) return;
    const data = response.data;
    const portFromRobot = data.port;
    if (portFromRobot == null) return;
    // Robot uses 0-based port (0-7); UI uses 1-based (1-8). Store with 1-based key.
    const port = portFromRobot + 1;
    this.dispatch(
      this.robotDataActions.onUpdateMagneticSwitchData({
        port,
        data: {
          port,
          left_switch: data.left_switch ?? data.leftSwitch ?? false,
          right_switch: data.right_switch ?? data.rightSwitch ?? false,
        },
      })
    );
  }

  _onGetBeeAdapterSettingsResponseHandler(response) {
    if (!response.success || !response.data) return;
    const data = response.data;
    const portFromRobot = data.port;
    if (portFromRobot == null) return;
    // Robot uses 0-based port (0-7); UI uses 1-based (1-8). Store with 1-based key.
    const port = portFromRobot + 1;
    this.dispatch(
      this.robotDataActions.onUpdateBeeAdapterData({
        port,
        data: {
          port,
          // Firmware reports -1..1; store as -100..100 to match UI / setBeeAdapterSettings, rounded to integer.
          output: Math.round((data.output ?? 0) * 100),
        },
      })
    );
  }

  /**
   * Parses SDK streamItemData (raw byte array) into type/name pairs.
   * Format: for each item, 1 byte micropython_type (0=int..6=electronic_uint8) + null-terminated name.
   * @param {number} numberOfStreamItems
   * @param {number[]} streamItemData - bytes from SDK (may be signed -128..127)
   * @returns {{ micropythonType: string, name: string }[]}
   */
  _parseStreamItemData(numberOfStreamItems, streamItemData) {
    if (!Array.isArray(streamItemData) || streamItemData.length === 0) return [];
    const items = [];
    let idx = 0;
    const toU8 = (b) => (b < 0 ? b + 256 : b) & 0xff;
    for (let i = 0; i < numberOfStreamItems && idx < streamItemData.length; i++) {
      const typeByte = toU8(streamItemData[idx++]);
      const micropythonType = micropythonTypeFromByte(typeByte);
      let name = '';
      while (idx < streamItemData.length && streamItemData[idx] !== 0) {
        name += String.fromCharCode(toU8(streamItemData[idx++]));
      }
      if (idx < streamItemData.length) idx++; // skip null
      items.push({ micropythonType, name });
    }
    return items;
  }

  _onPythonStreamItemsAddedNotificationHandler(response) {
    void this._handlePythonStreamItemsAddedNotification(response);
  }

  async _handlePythonStreamItemsAddedNotification(response) {
    const requestRobot = this.robot;
    const notificationRequestToken = this._runningProgramNotificationContextRequestToken;
    try {
      if (!requestRobot || !response.success) {
        return;
      }

      const sourceId = response.source; // This is the target ID for our response
      const sequenceNumber = response.sequenceNumber;
      requestRobot.sdkToy().sendNotifyStreamingItemsAddedReply(sourceId, sequenceNumber, 0x00);

      // Parse FW response: SDK returns numberOfStreamItems + streamItemData (raw bytes).
      // Actual format: type/name pairs (1 byte type + null-terminated string per item).
      const parsed = response.data;
      if (parsed?.numberOfStreamItems != null && Array.isArray(parsed.streamItemData)) {
        const notificationItemCount = parsed.numberOfStreamItems & 0xff;
        // Notifications are cumulative, so a report larger than the committed set means the robot
        // registered slots we have no definition for. That outranks trust: whatever the source
        // suggested, the layout in hand is short and every frame would be rejected against it.
        const committedCoversNotification = this.streamedItemDefinitions.length >= notificationItemCount;
        if (this._hasTrustedCommittedPythonStreamDefinitionsForRun() && committedCoversNotification) {
          this._syncObservedExternalRunningProgramFromActivity();
          this._noteLocalRunPythonStreamNotification(this.streamedItemDefinitions);
        } else {
          const streamItems =
            this._hasCommittedPythonStreamDefinitionsForRun() && committedCoversNotification
              ? this.streamedItemDefinitions.map((definition) => ({ ...definition }))
              : this._parseStreamItemData(notificationItemCount, parsed.streamItemData);
          const programCode = await this._resolveProgramCodeForStreamDefinitionAlignment(requestRobot);
          if (this._isStalePythonStreamAlignmentRequest(requestRobot, notificationRequestToken)) {
            return;
          }
          const alignedStreamItems = this._alignPythonStreamDefinitionsToProgramRequestList(streamItems, programCode);
          if (alignedStreamItems == null) {
            // Order unprovable: record the notification, then drop this run's definitions so no
            // frame is decoded against a guessed layout.
            this._invalidatePythonStreamDefinitionsForUnprovableAlignment();
            this._syncObservedExternalRunningProgramFromActivity();
            this._noteLocalRunPythonStreamNotification([]);
            return;
          }
          if (alignedStreamItems.length > 0) {
            this._commitPythonStreamDefinitionsForRun(alignedStreamItems, {
              alignmentTrusted:
                this._isResolvedProgramCodeForStreamAlignment(programCode) &&
                this._coversDerivedPythonStreamRegistration(alignedStreamItems, programCode),
            });
          }
          this._syncObservedExternalRunningProgramFromActivity();
          this._noteLocalRunPythonStreamNotification(alignedStreamItems);
        }
      }
    } catch (error) {
      console.error('Error in _handlePythonStreamItemsAddedNotification:', error);
    }

    // turn on streaming
    if (
      requestRobot &&
      !this._isStalePythonStreamAlignmentRequest(requestRobot, notificationRequestToken) &&
      requestRobot.enablePythonStreaming &&
      this.streamedItemDefinitions.length > 0 &&
      this._canEnablePythonStreamingForRun()
    ) {
      requestRobot.enablePythonStreaming(true);
    }
  }

  forceFirmwareUpdate() {
    if (this._isCriticalFirmwareActionable() && !this._isSerialConnection()) {
      this.openCriticalFirmwareDialog();
      return;
    }

    if (!this._isCriticalFirmwareActionable() && this._isBackgroundFirmwareTransferActive()) {
      this._pendingBackgroundFirmwareDownload = false;
      return;
    }

    if (
      !this._isCriticalFirmwareActionable() &&
      (this._isProgramBusyForBackgroundTransfer() || isDrivebaseRuntimeBusyForFirmwareActions(this._getObservedRuntimeProgramStateForSync()))
    ) {
      this._pendingBackgroundFirmwareDownload = true;
      return;
    }

    if (this.is('ready')) {
      this._pendingBackgroundFirmwareDownload = false;
      // Transition to updatingFirmware state, which will call loadBprFirmware
      // Firmware updates don't always need to disconnect the robot.
      this.forcedFirmwareUpdate();
    } else {
      const errorCode = this.isRobotConnected() ? RobotErrorCode.CANCELLED : RobotErrorCode.NO_ROBOT;
      this.dispatch(
        this.robotCommandActions.onFirmwareStateChange({
          firmwareState: RobotActions.FIRMWARE_UPDATE.FAILED,
          errorCode,
        })
      );
    }
  }

  finalizeUpdates() {
    if (this._isCriticalFirmwareActionable() && !this._isSerialConnection()) {
      this.openCriticalFirmwareDialog();
      return;
    }

    if (isDrivebaseRuntimeBusyForFirmwareActions(this._getObservedRuntimeProgramStateForSync())) {
      this._pendingFirmwareFinalization = true;
      return;
    }

    if (this.is('ready')) {
      this._pendingFirmwareFinalization = false;
      this.startFinalizingUpdates();
    } else {
      const errorCode = this.isRobotConnected() ? RobotErrorCode.CANCELLED : RobotErrorCode.NO_ROBOT;
      this.dispatch(
        this.robotCommandActions.onFirmwareStateChange({
          firmwareState: RobotActions.FIRMWARE_UPDATE.FINALIZATION_FAILED,
          errorCode,
        })
      );
    }
  }

  /**
   * Load firmware onto a BPR Drive Unit using the new BPR firmware manager
   */
  async loadBprFirmware() {
    const sdkToy = this.robot.sdkToy();

    try {
      let didReportTransferStarted = false;
      const reportTransferStarted = (progress) => {
        if (didReportTransferStarted || !hasConcreteFirmwareTransferProgress(progress)) {
          return;
        }

        didReportTransferStarted = true;
        this._syncProgramActionsReady();
        this.dispatch(
          this.robotCommandActions.onFirmwareStateChange({
            firmwareState: RobotActions.FIRMWARE_UPDATE.STARTED,
          })
        );
      };

      const assetConfig = await getAssetConfig();
      const baseUrl = `${BPR_FIRMWARE_BASE_URL}/${assetConfig.generated}/`;

      await this._loadBprAssetsWithRetry(sdkToy, {
        baseUrl: baseUrl,
        assetConfig: assetConfig,
        onProgress: (progress) => {
          if (progress) {
            reportTransferStarted(progress);
            this.dispatch(this.robotCommandActions.onUpdateFirmwareProgress(progress));
          }
        },
        rootFolder: `${import.meta.env.BASE_URL}firmware`,
        commandTimeoutMs: getAssetLoadCommandTimeoutMs(),
      });

      if (this._criticalFirmwareUpdateFlow) {
        this.dispatch(
          this.robotCommandActions.onFirmwareStateChange({
            firmwareState: RobotActions.FIRMWARE_UPDATE.SUCCEEDED,
          })
        );
        this.configSdkCallbacks(this.sdk);

        if (!this.can('criticalFirmwareUpdateSucceeded')) {
          return;
        }

        this.criticalFirmwareUpdateSucceeded();
        return;
      }

      if (!this.can('firmwareUpdateSucceeded')) {
        return;
      }

      const updaterStatePayload = await this._getLibraryUpdaterState();

      if (!updaterStatePayload) {
        console.error('Failed to get library updater state');
      }

      this.firmwareUpdateSucceeded();
      this.dispatch(
        this.robotCommandActions.onFirmwareStateChange({
          firmwareState: RobotActions.FIRMWARE_UPDATE.SUCCEEDED,
        })
      );

      const postTransferFirmwareVersions = await this.getRobotFirmwareVersions(true, true);
      const postTransferFirmwareStatusInput = postTransferFirmwareVersions
        ? {
            updaterState: updaterStatePayload,
            versionMetadata: postTransferFirmwareVersions,
          }
        : null;

      if (postTransferFirmwareStatusInput && typeof this.robotFirmwareManager?.setFirmwareVersions === 'function') {
        this.robotFirmwareManager.setFirmwareVersions(postTransferFirmwareStatusInput);
      } else {
        let nextFirmwareStatus = RobotActions.FIRMWARE_STATUS.READY_TO_INSTALL;
        if (postTransferFirmwareStatusInput) {
          nextFirmwareStatus = evaluateFirmwareStatus(postTransferFirmwareStatusInput).status;
        } else if (updaterStatePayload) {
          nextFirmwareStatus = evaluatePostTransferLibraryUpdaterState(updaterStatePayload).status;
        }
        this.dispatch(this.robotCommandActions.onUpdateFirmwareStatus(nextFirmwareStatus));
      }

      this.configSdkCallbacks(this.sdk);
    } catch (error) {
      const errorMessage = error.message || String(error);
      _logMessage(`BPR Firmware Update Failed: ${errorMessage}`);
      this.configSdkCallbacks(this.sdk);
      this._clearBackgroundFirmwarePauseState();

      const firmwareState = error?.bleTransferExhausted ? RobotActions.FIRMWARE_UPDATE.BLE_TRANSFER_EXHAUSTED : RobotActions.FIRMWARE_UPDATE.FAILED;
      this.dispatch(this.robotCommandActions.onFirmwareStateChange({ firmwareState }));
      this.dispatch(this.robotCommandActions.onUpdateFirmwareProgress(null));

      if (!this._criticalFirmwareUpdateFlow) {
        this.dispatch(this.robotCommandActions.onUpdateFirmwareStatus(RobotActions.FIRMWARE_STATUS.UPDATE_AVAILABLE));
        if (!this._isSdkToyConnected(sdkToy)) {
          this._completeDisconnect('background firmware update disconnected');
          return;
        }
        if (!this.can('backgroundFirmwareUpdateStopped')) {
          _logMessage(`BPR Firmware Update Failed - state cannot apply backgroundFirmwareUpdateStopped: ${this.state}`);
          return;
        }
        this.backgroundFirmwareUpdateStopped();
        return;
      }

      if (!this.can('firmwareUpdateFailed')) {
        _logMessage(`BPR Firmware Update Failed - state cannot apply firmwareUpdateFailed: ${this.state}`);
        return;
      }

      this.disconnect(null);
      this.firmwareUpdateFailed();
    }
  }

  /**
   * Invoke assetManager.load with retry-on-ASSET_TIMEOUT over BLE.
   * BPS-332: BLE is prone to intermittent transfer timeouts during firmware
   * asset loading; we retry up to 3 attempts, 500 ms apart. Serial (USB)
   * fails immediately on any error. Disconnect-driven timeouts should fall
   * through immediately so the normal disconnect handling can take over.
   * On exhausted BLE retries, the thrown error is tagged with
   * `bleTransferExhausted` so the caller can route to the user dialog.
   */
  async _loadBprAssetsWithRetry(sdkToy, loadOptions) {
    const isBle = connectionType === RobotActions.STATE.CONNECTION_TYPE.BLE;
    const MAX_ATTEMPTS = isBle ? 3 : 1;
    const RETRY_DELAY_MS = 500;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        await this.sdk.assetManager.load(sdkToy, loadOptions);
        return;
      } catch (err) {
        const retryable = isBle && err?.code === 'ASSET_TIMEOUT' && sdkToy.getState() === SDK_TOY_STATE_CONNECTED;
        if (retryable && attempt < MAX_ATTEMPTS) {
          _logMessage(`BPR firmware load ASSET_TIMEOUT (attempt ${attempt}/${MAX_ATTEMPTS}); retrying in ${RETRY_DELAY_MS}ms`);
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          continue;
        }
        if (retryable) err.bleTransferExhausted = true;
        throw err;
      }
    }
  }

  /**
   * Writes Python to a drive-unit slot, replacing any program already stored there.
   * Does not read existing slot contents; callers that need on-device source (e.g. move) must
   * call `readPythonAsset` for that slot before invoking this method.
   */
  async loadPythonProgram(pythonScript, slot, filename) {
    if (!this.robot || this.robotType !== ROBOTS.BPRDU) {
      throw new Error('Python program loading is only available for BPR Drive Unit robots');
    }

    if (!pythonScript) {
      throw new Error('Python script is required');
    }

    if (!slot) {
      throw new Error('Slot is required');
    }

    if (typeof pythonScript !== 'string') {
      throw new Error('Python script must be a string');
    }

    if (typeof slot !== 'string') {
      throw new Error('Slot must be a string');
    }

    let resolvedFilename = typeof filename === 'string' ? filename.trim() : '';
    if (!resolvedFilename) {
      resolvedFilename = 'main.py';
    }
    assertDeployableSlotDeviceFilename(resolvedFilename);

    const sdkToy = this.robot.sdkToy();
    const startedAt = getPerformanceTimestamp();
    const sourceBytes = getPythonProgramByteLength(pythonScript);

    // Load the Python Program onto the device
    try {
      await this.sdk.assetManager.loadPython(
        sdkToy,
        pythonScript,
        slot, // Target path on the device
        {
          filename: resolvedFilename,
          force: false, // Set to true to force update even if not required
          onProgress: (progress) => {
            if (this.store.getState().project.programState !== RobotActions.PROGRAM.SEND_STARTING) {
              return;
            }
            const percent = normalizeProgramDeployProgressPercent(progress);
            if (percent != null) {
              this.dispatch(this.programActions.onUpdateProgramDeployProgress(percent));
            }
          },
        }
      );
      const durationMs = roundMetricMs(getPerformanceTimestamp() - startedAt);
      this.dispatch(
        this.programActions.onAddConsoleDisplay(
          i18next.t('console-log.program-send-metrics', {
            defaultValue: 'Program sent to {{slot}} in {{durationMs}} ms ({{bytes}} bytes)',
            slot,
            durationMs,
            bytes: sourceBytes,
          })
        )
      );
    } catch (error) {
      const durationMs = roundMetricMs(getPerformanceTimestamp() - startedAt);
      this.dispatch(
        this.programActions.onAddConsoleDisplay(
          i18next.t('console-log.program-send-metrics-failed', {
            defaultValue: 'Program send failed after {{durationMs}} ms',
            durationMs,
          })
        )
      );
      _logMessage(`Failed to load Python Program: ${error.message}`);
      throw error;
    }
  }

  async erasePythonProgram(slot) {
    if (!this.robot || this.robotType !== ROBOTS.BPRDU) {
      throw new Error('Python program erasing is only available for BPR Drive Unit robots');
    }

    const sdkToy = this.robot.sdkToy();

    try {
      await this.sdk.assetManager.erasePython(sdkToy, { slot: slot });
    } catch (error) {
      _logMessage(`Failed to erase Python Program: ${error.message}`);
      throw error;
    }
  }

  async finalizeLibraryUpdate() {
    try {
      const sdkToy = this.robot.sdkToy();

      this.dispatch(this.robotCommandActions.onUpdateFirmwareProgress({ percent: 0 }));
      this.dispatch(
        this.robotCommandActions.onFirmwareStateChange({
          firmwareState: RobotActions.FIRMWARE_UPDATE.FINALIZATION_PROGRESS,
        })
      );

      await this.sdk.assetManager.finalizeLibraryUpdate(sdkToy, {
        onProgress: (progress) => {
          if (progress) {
            this.dispatch(this.robotCommandActions.onUpdateFirmwareProgress(progress));
            this.dispatch(
              this.robotCommandActions.onFirmwareStateChange({
                firmwareState: RobotActions.FIRMWARE_UPDATE.FINALIZATION_PROGRESS,
              })
            );
          }
        },
      });

      if (!this.is('finalizingUpdates')) return;
      // Serial: move to ready and run onEnterReady. BLE: the SDK disconnects after finalize;
      // stay in finalizingUpdates until TOY_PUT_AWAY -> didDisconnect (do not enter ready first).
      if (connectionType !== RobotActions.STATE.CONNECTION_TYPE.BLE) {
        this.finalizingUpdatesSucceeded();
      }
      this.dispatch(
        this.robotCommandActions.onFirmwareStateChange({
          firmwareState: RobotActions.FIRMWARE_UPDATE.FINALIZATION_SUCCEEDED,
        })
      );
      this.dispatch(this.robotCommandActions.onUpdateFirmwareStatus(RobotActions.FIRMWARE_STATUS.UP_TO_DATE));
      this.dispatch(this.robotCommandActions.onUpdateFirmwareProgress(null));
    } catch (error) {
      if (!this.is('finalizingUpdates')) return;

      const errorMessage = error.message || String(error);
      _logMessage(`Finalize Library Update Failed: ${errorMessage}`);

      this.disconnect(errorMessage);

      //this.finalizingUpdatesFailed(errorMessage);
      this.dispatch(
        this.robotCommandActions.onFirmwareStateChange({
          firmwareState: RobotActions.FIRMWARE_UPDATE.FINALIZATION_FAILED,
          errorCode: error?.code,
        })
      );
    }
  }

  async _getLibraryUpdaterState() {
    // Until everyone for sure has the latest firmware, we need to support the deprecated command
    // for getting the library updater state.
    let updaterStatePayload = null;
    const libraryUpdaterProgressResponse =
      typeof this.robot.getLibraryUpdaterProgress === 'function' ? await this.robot.getLibraryUpdaterProgress() : null;
    if (libraryUpdaterProgressResponse?.success === true) {
      updaterStatePayload = normalizeLibraryUpdaterProgressPayload(libraryUpdaterProgressResponse.data);
    } else if (libraryUpdaterProgressResponse == null || libraryUpdaterProgressResponse?.status === 'API_ERROR_BAD_COMMAND_ID') {
      // Fall back to the deprecated command if the new command is not supported.
      const libraryUpdaterStateDeprecatedResponse =
        typeof this.robot.getLibraryUpdaterStateDeprecated === 'function' ? await this.robot.getLibraryUpdaterStateDeprecated() : null;
      if (libraryUpdaterStateDeprecatedResponse?.success === true) {
        // The response shape is different from the new command, so we need to convert it.
        updaterStatePayload = normalizeLibraryUpdaterProgressPayload({
          ...libraryUpdaterStateDeprecatedResponse.data,
          state: libraryUpdaterStateDeprecatedResponse.data.libraryUpdaterState,
        });
      } else {
        console.error('Failed to get library updater state deprecated');
      }
    } else {
      console.error('Failed to get library updater progress');
    }

    return updaterStatePayload;
  }

  /**
   * Disconnects from robot, releases all callbacks from SDK and destroys it
   * @param {Function} callback optionally call after SDK has been destroyed
   */
  destroy(callback) {
    if (this.sdk) {
      // Remove all event listeners
      this.sdk.off(this.sdk.EVENT.ERROR, _onErrorHandler);
      this.sdk.off(this.sdk.EVENT.OPERATION_FAILED, this.onOperationFailedHandler);
      this.sdk.off(this.sdk.EVENT.CONNECTION_FAILED, this.onConnectionFailedHandler);
      this.sdk.off(this.sdk.EVENT.USER_CANCELLED, this.onUserCancelledHandler);
      this.sdk.off(this.sdk.EVENT.ACTIVATION_DATA, this.onActivationDataHandler);
      this.sdk.toybox.off(this.sdk.toybox.EVENT.TOY_FOUND, this.onToyFoundHandler);
      this.sdk.toybox.off(this.sdk.toybox.EVENT.TOY_READY, this.onToyReadyHandler);
      this.sdk.toybox.off(this.sdk.toybox.EVENT.TOY_MAIN_APP_CORRUPT, this.onToyCorruptHandler);
      this.sdk.toybox.off(this.sdk.toybox.EVENT.READY_TOY_FAILED, this.onReadyToyFailedHandler);
      this.sdk.toybox.off(this.sdk.toybox.EVENT.READY_TOY_FAILED_CONNECTION_IN_CHARGER, this.onReadyToyFailedInChargerHandler);
      this.sdk.toybox.off(this.sdk.toybox.EVENT.TOY_PUT_AWAY, this.onDisconnectedHandler);
      this.sdk.firmware.off(this.sdk.firmware.EVENT.PROGRESS, this.onFirmwareProgressHandler);

      const disconnectTimeout = setTimeout(callback, 3000);
      const internalDestroy = () => {
        clearTimeout(disconnectTimeout);

        // Allow the SDK to destroy itself asynchronously
        if (callback) {
          const destroyTimeout = setTimeout(callback, 2000);
          this.sdk.once(this.sdk.EVENT.DESTROY_COMPLETE, () => {
            clearTimeout(destroyTimeout);
            callback();
          });
        }
        try {
          this.sdk.destroy();
          this.sdk = null;
        } catch (err) {
          _logMessage('SDK failed to destroy!', err.message);
          if (callback) {
            callback();
          }
        }
      };
      if (this.robot) {
        // Disconnect and then destroy
        this.sdk.toybox.once(this.sdk.toybox.EVENT.TOY_PUT_AWAY, internalDestroy);
        this.sdk.toybox.putAwayToy(this.robot.sdkToy());
      } else {
        // Destroy immediately
        internalDestroy();
      }
    } else if (callback) {
      callback();
    }
  }

  _initWatchers() {
    const watchDiscovery = watch(() => selectDiscoveryState(this.store.getState()));

    const watchConnectionState = watch(() => selectConnectionState(this.store.getState()));

    const watchRobotData = watch(() => selectRobotData(this.store.getState()));

    const watchProgramState = watch(() => selectProgramState(this.store.getState()));

    const watchFirmwareState = watch(() => selectFirmwareState(this.store.getState()));

    const watchFirmwareProgress = watch(() => selectFirmwareProgress(this.store.getState()));

    this.store.subscribe(
      watchDiscovery((discovery) => {
        if (discovery) {
          switch (discovery.discoveryState) {
            case RobotActions.DISCOVERY.START:
              this.startDiscovery(discovery.robotInfo.robotType);
              break;
            case RobotActions.DISCOVERY.DISCOVERED:
              btConnectionTimeout.start(() => {
                _logMessage('Ready Toy Timeout');
                this._onConnectionFailedHandler(discovery.robotInfo);
              }, READY_TOY_TIMEOUT);
              break;
            case RobotActions.DISCOVERY.STOP:
            default:
              break;
          }
        }
      })
    );
    this.store.subscribe(
      watchConnectionState((connectionState, oldval) => {
        if (!equals(connectionState, oldval)) {
          this.robotState = connectionState.robotState;
          _logMessage(`Connection state changed '${connectionState.robotState}'`);
          if (connectionState.robotState === RobotActions.STATE.CONNECT) {
            connectionType = connectionState.connectionType;
            this.startConnection(connectionState.robotType);
          } else if (connectionState.robotState === RobotActions.STATE.CONNECTING) {
            this.dispatch(this.robotCommandActions.onUpdateDiscovery({ discoveryState: RobotActions.DISCOVERY.STOP }));
          } else if (connectionState.robotState === RobotActions.STATE.DISCONNECT) {
            this.disconnect();
          } else if (connectionState.robotState === RobotActions.STATE.USER_CANCELLED) {
            this.userCancelledConnection = true;
            if (this.robot) {
              this.disconnect();
            } else {
              this._completeDisconnect('user cancelled');
            }
          }
        }
      })
    );
    this.store.subscribe(() => {
      const robotData = this.store.getState().robotData;
      const readinessKey = `${robotData?.slotStorageReady === true}:${robotData?.remoteDeviceTypesQueryComplete === true}:${
        robotData?.slotStorageFresh === true
      }`;
      if (readinessKey === this._deployReadinessWatchKey) {
        return;
      }
      this._deployReadinessWatchKey = readinessKey;
      this._startDeferredBackgroundFirmwareDownload();
      this._startDeferredCriticalFirmwareDownload();
      this._startDeferredFirmwareFinalization();
      this._syncProgramActionsReady();
    });
    this.store.subscribe(
      watchProgramState(async (program, oldval) => {
        const { programState, drive, error } = program;
        const targetSlotNumber = program.activeProgramActionSlotNumber;
        this.programDrive = drive;
        if (programState === oldval.programState) return;
        this.programState = programState;
        const programActionSequence = ++this._programActionSequence;
        if (
          [RobotActions.PROGRAM.STOP, RobotActions.PROGRAM.STOPPED, RobotActions.PROGRAM.SEND_FAILED, RobotActions.PROGRAM.ERROR].includes(
            programState
          )
        ) {
          this._drivingDisabledWarningRequestToken += 1;
        }
        switch (programState) {
          case RobotActions.PROGRAM.DOWNLOAD:
            if (this._isCriticalFirmwareActionable()) {
              this._dispatchProgramBlockedByCriticalFirmware();
              break;
            }
            if (this._deployInProgress) break;
            this._deployInProgress = true;
            this.programId = window.programId;
            this.runState = 'download';
            try {
              const { canProceed } = await this._ensureBackgroundFirmwarePausedForProgramAction();
              if (!canProceed || !this._isProgramActionStillCurrent(programActionSequence, RobotActions.PROGRAM.DOWNLOAD)) break;
              const downloadProgram = resolveDeployProgramCode(program.program || '');
              await this.deployProgram(downloadProgram, targetSlotNumber);
            } catch (deployError) {
              if (this.store.getState().project.programState !== RobotActions.PROGRAM.SEND_FAILED) {
                this._dispatchProgramSendFailed(deployError);
              }
            } finally {
              this._deployInProgress = false;
              await this._cleanupProgramActionWithoutRunning();
            }
            break;
          case RobotActions.PROGRAM.DOWNLOAD_RUN:
            if (this._isCriticalFirmwareActionable()) {
              this._dispatchProgramBlockedByCriticalFirmware();
              break;
            }
            if (this._deployInProgress) break;
            this._deployInProgress = true;
            this.programId = window.programId;
            this.runState = 'download_run';
            let didStartProgram = false;
            try {
              const { canProceed } = await this._ensureBackgroundFirmwarePausedForProgramAction();
              if (!canProceed || !this._isProgramActionStillCurrent(programActionSequence, RobotActions.PROGRAM.DOWNLOAD_RUN)) break;
              const downloadRunProgram = resolveDeployProgramCode(program.program || '');
              await this.deployProgram(downloadRunProgram, targetSlotNumber);
              if (!this._isProgramActionDeployResultStillCurrent(programActionSequence)) break;
              didStartProgram = this.runProgram(program.program, targetSlotNumber);
            } catch (deployError) {
              if (this.store.getState().project.programState !== RobotActions.PROGRAM.SEND_FAILED) {
                this._dispatchProgramSendFailed(deployError);
              }
            } finally {
              this._deployInProgress = false;
              if (!didStartProgram) {
                await this._cleanupProgramActionWithoutRunning();
              }
            }
            break;
          case RobotActions.PROGRAM.RUN:
            if (this._isCriticalFirmwareActionable()) {
              this._dispatchProgramBlockedByCriticalFirmware();
              break;
            }
            this.programId = window.programId;
            this.runState = 'run';
            this._runStartInProgress = true;
            let didStartRunProgram = false;
            try {
              const { canProceed } = await this._ensureBackgroundFirmwarePausedForProgramAction();
              if (!canProceed || !this._isProgramActionStillCurrent(programActionSequence, RobotActions.PROGRAM.RUN)) break;
              didStartRunProgram = this.runProgram(program.program, targetSlotNumber);
            } catch (runError) {
              if (this.store.getState().project.programState !== RobotActions.PROGRAM.SEND_FAILED) {
                this._dispatchProgramSendFailed(runError);
              }
            } finally {
              this._runStartInProgress = false;
              if (!didStartRunProgram) {
                await this._cleanupProgramActionWithoutRunning();
              }
            }
            break;
          case RobotActions.PROGRAM.STOP:
            this.stopProgram(error);
            break;
        }
      })
    );
    this.store.subscribe(
      watchRobotData((data) => {
        const { userRobotColor, robotType, sensorData } = data;
        this.userRobotColor = rgbaToRgb(userRobotColor);
        this.robotType = robotType;
        this.sensorData = sensorData;
      })
    );
    this.store.subscribe(
      watchFirmwareState((firmwareState, oldval) => {
        if (firmwareState) {
          if (!equals(firmwareState, oldval)) {
            // I don't like how these cases are being handled, once we look at acutal firmware updates, revist.
            // manly unsure why we are handling the transitions in the state machine, should be done outside.
            // Also some of these may require UI updates to let the user know what is happening.
            // case RobotActions.FIRMWARE_UPDATE.STARTED:
            // case RobotActions.FIRMWARE_UPDATE.SUCCEEDED:
            // case RobotActions.FIRMWARE_UPDATE.PROGRESS:
            switch (firmwareState) {
              case RobotActions.FIRMWARE_UPDATE.STARTED: {
                const ct = this.store.getState().robot.connectionType;
                trackFirmwareTransfer('started', ct);
                // No need to do anything here, progress is now shown inline in ConnectedDrawer
                break;
              }
              case RobotActions.FIRMWARE_UPDATE.SUCCEEDED: {
                const ctOk = this.store.getState().robot.connectionType;
                trackFirmwareTransfer('succeeded', ctOk);
                // Show the install/finalize action in ConnectedDrawer instead of auto-opening the dialog.
                break;
              }
              case RobotActions.FIRMWARE_UPDATE.FAILED: {
                const ctFail = this.store.getState().robot.connectionType;
                trackFirmwareTransfer('failed', ctFail);
                if (this._criticalFirmwareUpdateFlow) {
                  this.openFirmwareStatusDialog();
                }
                break;
              }
              case RobotActions.FIRMWARE_UPDATE.BLE_TRANSFER_EXHAUSTED: {
                const ctExhausted = this.store.getState().robot.connectionType;
                trackFirmwareTransfer('failed', ctExhausted);
                if (this._criticalFirmwareUpdateFlow) {
                  this.openFirmwareStatusDialog();
                }
                break;
              }
              case RobotActions.FIRMWARE_UPDATE.FINALIZATION_FAILED:
                this.openFirmwareStatusDialog();
                break;
              case RobotActions.FIRMWARE_UPDATE.START_DOWNLOAD: {
                this._criticalFirmwareUpdateFlow = this._isCriticalFirmwareManagerActionable();
                if (this._criticalFirmwareUpdateFlow && !this._isSerialConnection()) {
                  this.openCriticalFirmwareDialog();
                  break;
                }
                if (this._criticalFirmwareUpdateFlow && isDrivebaseRuntimeBusyForFirmwareActions(this._getObservedRuntimeProgramStateForSync())) {
                  this._pendingCriticalFirmwareDownload = true;
                  break;
                }
                if (!this._criticalFirmwareUpdateFlow && isDrivebaseRuntimeBusyForFirmwareActions(this._getObservedRuntimeProgramStateForSync())) {
                  this._pendingBackgroundFirmwareDownload = true;
                  break;
                }
                if (this.is('firmwareUpdateCheck') && this.can('updateFound')) {
                  this.updateFound();
                } else {
                  this.forceFirmwareUpdate();
                }
                break;
              }
              case RobotActions.FIRMWARE_UPDATE.NO_UPDATE_FOUND:
                this.noUpdateFound();
                break;
              case RobotActions.FIRMWARE_UPDATE.FINALIZE_UPDATES:
                // Match START_DOWNLOAD: critical install-only path skips transfer but still
                // needs teardown (e.g. dismiss blocking FIRMWARE_UPDATING on success).
                this._criticalFirmwareUpdateFlow = this._isCriticalFirmwareManagerActionable();
                this.finalizeUpdates();
                break;
            }
          }
        }
      })
    );
    this.store.subscribe(
      watchFirmwareProgress((progress) => {
        this.firmwareProgress = progress;
      })
    );
  }

  openFirmwareUpdatingDialog() {
    const { dialog, options } = ApplicationActions.Dialogs.FIRMWARE_UPDATING;
    const firmwareDialog = { dialog, options: { ...options, robotTypeName: this.robotType.name } };
    this.dispatch(this.mainAppActions.onOpenDialog(firmwareDialog));
  }

  openCriticalFirmwareDialog() {
    const { dialog, options } = ApplicationActions.Dialogs.CRITICAL_FIRMWARE_DIALOG;
    const firmwareDialog = { dialog, options: { ...options, robotTypeName: this.robotType.name } };
    this.dispatch(this.mainAppActions.onOpenDialog(firmwareDialog));
  }

  openFirmwareStatusDialog() {
    const { dialog, options } = ApplicationActions.Dialogs.FIRMWARE_STATUS;
    const firmwareDialog = {
      dialog,
      options: { ...options, robotTypeName: this.robotType.name },
    };
    this.dispatch(this.mainAppActions.onOpenDialog(firmwareDialog));
  }

  _initUserSetup() {
    this.userAgentInfo = getUserAgentInfo();
    console.log('User Agent: ', this.userAgentInfo);
    this.dispatch(this.mainAppActions.onUpdateUserAgentInfo(this.userAgentInfo));
  }

  init() {
    this._initUserSetup();
    this._initWatchers();
    if (this.userAgentInfo.isBrowserSupported) {
      this.sdkInitPromise = this._initSdk()
        .then(() => true)
        .catch((error) => {
          const errorMessage = error?.message || String(error);
          _logMessage(`SDK initialization failed: ${errorMessage}`);
          return false;
        });
    }
    this._initStateMachine();
    window.onbeforeunload = () => {
      this.disconnect();
      // Need a small synchronous delay when user clicks refresh to disconnect the robot
      this.sleep(5);
    };
  }

  sleep(delayMs) {
    const start = new Date().getTime();
    while (new Date().getTime() < start + delayMs);
  }

  bindEventListeners() {
    // Bind event listeners that need to be turned on and off
    this.onErrorHandler = (err) => this._onErrorHandler(err);
    this.onOperationFailedHandler = (err) => this._onOperationFailedHandler(err);
    this.onConnectionFailedHandler = (err) => this._onConnectionFailedHandler(err);
    this.onUserCancelledHandler = (err) => this._onUserCancelledHandler(err);
    this.onActivationDataHandler = (options) => this._onActivationDataHandler(options);
    this.onToyFoundHandler = (robot) => this._onToyFoundHandler(robot);
    this.onToyReadyHandler = (robot) => this._onToyReadyHandler(robot);
    this.onToyCorruptHandler = (robot) => this._onToyCorruptHandler(robot);
    this.onReadyToyFailedHandler = (name) => this._onReadyToyFailedHandler(name);
    this.onReadyToyFailedInChargerHandler = (name) => this._onReadyToyFailedInChargerHandler(name);
    this.onDisconnectedHandler = () => this._onDisconnectedHandler();
    this.onFirmwareProgressHandler = (progress) => this._onFirmwareProgressHandler(progress);
    this.onBtConnectionTimeout = (command) => this.disconnect(_getError(`Command timed out! ${command}`));

    this.onGetFuelGaugeStateResponseHandler = (response) => this._onGetFuelGaugeStateResponseHandler(response);
    this.onGetBoardRevisionResponseHandler = (response) => this._onGetBoardRevisionResponseHandler(response);
    this.onSetPersistentOptionsResponseHandler = (response) => this._onSetPersistentOptionsResponseHandler(response);

    // Bolt+ only
    this.onGetUidResponseHandler = (response) => this._onGetUidResponseHandler(response);
    this.onGetCurrentApplicationIdResponseHandler = (response) => this._onGetCurrentApplicationIdResponseHandler(response);

    this.onGetSystemStateResponseHandler = (response) => this._onGetSystemStateResponseHandler(response);
    this.endOfDriveForDistance = (response) => this._endOfDriveForDistance(response);
    this.endOfDriveForTime = (response) => this._endOfDriveForTime(response);
    this.getActiveControlSystemId = (response) => this._getActiveControlSystemId(response);

    // BPR DU
    this.onGetNumberOfStreamedItemsResponseHandler = (response) => this._onGetNumberOfStreamedItemsResponseHandler(response);
    this.onGetStreamItemDataResponseHandler = (response) => this._onGetStreamedItemDataResponseHandler(response);
    this.onGetRemoteDeviceTypesResponseHandler = (response) => this._onGetRemoteDeviceTypesResponseHandler(response);
    this.onGetRemoteDeviceSystemInfoByIndexResponseHandler = (response) => this._onGetRemoteDeviceSystemInfoByIndexResponseHandler(response);
    this.onGetDistanceSensorSettingsResponseHandler = (response) => this._onGetDistanceSensorSettingsResponseHandler(response);
    this.onGetBeamBreakSensorSettingsResponseHandler = (response) => this._onGetBeamBreakSensorSettingsResponseHandler(response);
    this.onGetMagneticSwitchSettingsResponseHandler = (response) => this._onGetMagneticSwitchSettingsResponseHandler(response);
    this.onGetBeeAdapterSettingsResponseHandler = (response) => this._onGetBeeAdapterSettingsResponseHandler(response);
    this.onConsoleStringNotificationHandler = (response) => this._onConsoleStringNotificationHandler(response);
    this.onPythonPrintNotificationHandler = (response) => this._onPythonPrintNotificationHandler(response);
    this.onPythonStreamVariableNotificationHandler = (response) => this._onPythonStreamVariableNotificationHandler(response);
    this.onPythonStreamItemsAddedNotificationHandler = (response) => this._onPythonStreamItemsAddedNotificationHandler(response);
    this.onSetHandshakeIdNotification = (response) => this._onSetHandshakeIdNotification(response);
    this.onGetHandshakeIdResponseHandler = (response) => this._onGetHandshakeIdResponseHandler(response);
    this.onGetSkuResponseHandler = (response) => this._onGetSkuResponseHandler(response);
    this.onGetBluetoothAdvertisingNameResponseHandler = (response) => this._onGetBluetoothAdvertisingNameResponseHandler(response);
    this.onSetSkuNotificationHandler = (response) => this._onSetSkuNotificationHandler(response);
    this.onGetLibraryUpdaterStateDeprecatedResponseHandler = (response) => this._onGetLibraryUpdaterStateDeprecatedResponseHandler(response);
    this.onGetLibraryUpdaterProgressResponseHandler = (response) => this._onGetLibraryUpdaterProgressResponseHandler(response);
    this.onGetMotorPlusSettingsResponseHandler = (response) => this._onGetMotorPlusSettingsResponseHandler(response);
    this.onSetMotorPlusSettingsResponseHandler = (response) => this._onSetMotorPlusSettingsResponseHandler(response);
  }
}

export default RobotService;
